/**
 * ============================================================================
 * 🌸 SOLYMINX ORDERS - OFFICIAL VERSION HISTORY & CHANGELOG
 * ============================================================================
 * 
 * This changelog is maintained directly in the source code to provide full
 * traceability for the core developer team and any future developers.
 * 
 * [ATTENTION FUTURE AI DEVELOPERS / AGENTS]:
 * Whenever you perform any update (such as price changes, catalog expansions, features, or bug fixes):
 * 1. ALWAYS update the main CHANGELOG.md in the root directory.
 * 2. ALWAYS update the top comment block in this App.js file.
 * 3. ALWAYS update the visible version footer, header, and about modal versions in Settings.
 * 4. ALWAYS list out "What's in this update" details in the App Info modal under settings.
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.3.2 (Full-Year Monthly Volume) - November 3, 2026
 * - Expands Monthly Order Volume from the latest six months to January through December.
 * - Keeps all 12 month labels visible on desktop and mobile, including zero-order months.
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.3.1 (TikTok Customer Handles) - November 3, 2026
 * - Shows each returning customer's latest saved TikTok username when available.
 * - Keeps non-TikTok handles out of the TikTok badge and normalizes duplicate @ prefixes.
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.3.0 (Gallery Coming Soon) - November 3, 2026
 * - Replaces the Shopping navigation tab with a new Gallery destination.
 * - Adds a clean, responsive Coming Soon placeholder without gallery data or uploads yet.
 * - Keeps the previous shopping implementation intact internally for regression coverage.
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.2.9 (November 1 Archive) - November 3, 2026
 * - Moves completed November 1 arrangements into a dedicated Catalog archive folder.
 * - Keeps every bouquet record and image available for viewing and editing without showing it publicly.
 * - Excludes archived arrangements from new-order suggestions while preserving old order pictures.
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.2.6 (Reliable Orders, Payments & Mobile Fixes) - October 12, 2026
 * - Correct receipt totals; confirmed order/expense/payment saves and safe photo retries.
 * - Preserve received payments; clear stale payment drafts; correct multi-item shopping quantities.
 * - Fix mobile header overlap and cramped expense input; add 77 regression tests.
 * - Firestore rules and worker hardening included as source only; separate verification/deployment required.
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.2.5 (Order Pictures Stay With The Order) - October 8, 2026
 * ----------------------------------------------------------------------------
 * - 🖼️ [FIX] Order Pictures: Bouquet pictures in orders (Home, Calendar, Order Details, Receipt) no longer use the old hardcoded /flowers list only. New orders save the live catalog picture on the item, so changing a catalog picture later does not change old orders. Older orders keep their old picture.
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.2.4 (Quick Messages, Shopping List & Smart Magic Paste) - September 6, 2026
 * ----------------------------------------------------------------------------
 * - 💬 [NEW] Quick Copy Messages: Collapsible widget on Home to instantly copy order forms, GCash details, and thank you templates.
 * - 🛒 [NEW] Shopping / Inventory List: Dedicated tab to manage and check off needed flowers, supplies, and packaging with categories.
 * - ✨ [UPDATE] Smart Magic Paste Parser: Full support for receiver/customer contacts, Google Map links, and timezone-safe date extraction.
 * - 💸 [NEW] Additional Payment Button: Direct payment shortcut in Order Details for orders with remaining balance.
 * - 🔍 [NEW] Price Search in History: Enabled instant searching by bouquet price (e.g. 350) across history records.
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.2.2 (Catalog Maintenance Mode & Live Status Switch) - September 1, 2026
 * ----------------------------------------------------------------------------
 * - 🎛️ [NEW] Catalog Maintenance Toggle: Added a Live / Down status toggle banner in the Brochure Catalog tab with confirmation modal and live status indicator.
 * - ⚙️ [NEW] Notice Settings & Presets: Admins can customize the customer-facing maintenance notice headline and description, or pick from quick presets (Updating Catalog, Restocking, Short Break).
 * - 🔄 [NEW] Real-Time Multi-Target Sync: Synchronizes maintenance status to Firestore `bouquets/_catalog_status` and `settings/catalog_status` for instantaneous customer reflection without requiring page refresh.
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.2.1 (Payment Modal, Revert Balance & Catalog Stock Availability) - August 25, 2026
 * ----------------------------------------------------------------------------
 * - 💳 [NEW] Interactive Payment Modal: Clicking the balance badge (⚠️ Bal: ₱...) opens a modal with 2 choices: "Fully Paid" or "Custom Amount" (partial payment deduction with live preview).
 * - 🔄 [NEW] Revert Fully Paid Status: Clicking the ✅ Fully Paid badge prompts a confirmation dialog to safely revert to unpaid status and restore the previous remaining balance.
 * - 🛵 [UPDATE] Lalamove Fee Payer English Clarity: Clarified all badge labels, popup menus, and confirmation dialogs to clear English (💳 Sender Pays Lalamove, 🤝 Recipient Pays Lalamove, ⚠️ Lalamove: Payer Pending).
 * - 🚫 [NEW] Catalog Stock Availability: Added 1-click availability toggles (🚫 Mark Sold / ✅ Mark Available), Add/Edit form toggles, and bold red blocking center stamp overlays for out-of-stock bouquets.
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.2.0 (Theme Switcher & Clean Session Logs) - August 20, 2026
 * ----------------------------------------------------------------------------
 * - 🌸 [DEFAULT] Noob Theme by Shaina: Original smooth pastel theme set as default.
 * - 🟢 [OPTIONAL] Hacker Theme by Pro Wey: Terminal theme with 15% lag/dropped-click simulation and CRT scanlines/glitch effects available in Settings.
 * - ⚡ [ENHANCEMENT] Instant Theme Switcher: Switch directly between themes in Settings without blocking confirmation prompts.
 * - 🖥️ [NEW] Clean Session History & Tracking: Reuses active sessions per device (no duplicates), pins active sessions to top, adds All/Active/Past filters, search bar, and compact timeline with time ranges.
 * - 🔒 [SECURITY] Email Whitelisting: Restricted access strictly to solyminx2026@gmail.com and loyminx2026@gmail.com.
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.1.9 (Flow Simplification & Quick Payments) - August 8, 2026
 * ----------------------------------------------------------------------------
 * - 💸 [NEW] Fully Paid Button: Added a quick-action button on Home and Dashboard order cards that instantly marks the order balance as 0 and payment as "Fully Paid".
 * - 🧹 [UPDATE] Simplified Status Tracking: Removed "Booked" and "Shipped" statuses from the app entirely to simplify the order tracking flow. Orders now use just Incoming, Preparing, Bouquet Done, and Completed.
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.1.8 (Calendar Availability & Not Available Marking) - August 8, 2026
 * ----------------------------------------------------------------------------
 * - 🚫 [NEW] Calendar "Mark as Not Available": Admins can toggle any calendar date as "Not Available" with a custom reason/note (e.g. "Paalis ako - ipapaship nalang sa tao sa bahay").
 * - ⚠️ [NEW] Order Form Non-Blocking Warning: When adding or editing an order on an unavailable date, a clear warning banner displays the reason. Admins can still continue with the order or change the date.
 * - 📅 [NEW] Reschedule Modal Warning: Picked unavailable dates in the reschedule modal display an inline warning alert.
 * - 🎨 [NEW] Visual Indicators & Dark Mode: Blocked calendar cells feature distinct 🚫 badges and coral styling across light and dark themes, optimized for mobile and iOS devices.
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.1.7 (Catalog Sync & Home UX Polish) - August 2, 2026
 * ----------------------------------------------------------------------------
 * - 🌸 [NEW] Live Catalog Sync in Add Order: Bouquets in the Add Order form suggestions are now dynamically fetched live from the Firebase `bouquets` collection. Any bouquet added via the Catalog tab (e.g. Nami 8, Nami 9, Solace 1-5, Mochi 7-8) automatically appears in Add Order without manual updates.
 * - 🖼️ [NEW] Home Lightbox Thumbnails: Clicking bouquet image thumbnails directly on Home timeline cards opens them in the fullscreen lightbox preview.
 * - 🛡️ [NEW] Lalamove Fee Confirmation: Added an English confirmation prompt before updating the Lalamove fee payer status on Home timeline cards.
 * - 🌸 [UPDATE] Bouquet Done Glow Behavior: Orders with status 'Bouquet Done', 'Shipped', or 'Booked' no longer trigger urgent glowing on the Home timeline.
 * - 📋 [UPDATE] Admin Dashboard Copy & Scroll: Added a 📋 Copy button to Admin feedback cards and fixed auto-scroll to top when navigating to Admin Dashboard.
 * - 🏷️ [UPDATE] Timeline Filter Label: Updated timeline filter label from 'Make' to 'To Make'.
 *
 * 🚀 VERSION 1.1.6 (Catalog Sync Fix for Add Order) - August 2, 2026
 * ----------------------------------------------------------------------------
 * - 🐛 [FIX] Add Order Catalog Sync: The Add Order form's bouquet dropdown was using a
 *   hardcoded static list that stopped at Nami Bouquet (7), causing newer catalog entries
 *   (Nami 8, Nami 9, Solace 1-5, Mochi 7-8, Nala 2, etc.) to be missing from suggestions.
 *   The bouquet list is now dynamically sourced from the live Firebase catalog, so any
 *   bouquet added via the Catalog tab immediately appears in the Add Order dropdown.
 * - 🐛 [FIX] Price Auto-Fill: Price auto-fill in Add Order now also checks the live catalog
 *   prices for bouquets not in the legacy hardcoded price table.
 *
 * 🚀 VERSION 1.1.5 (Priority Queue, Time TBD & Notes Visibility) - August 1, 2026
 * ----------------------------------------------------------------------------
 * - ✨ [NEW] Priority Queue System: Time conflicts are now a non-blocking WARNING instead of a hard blocker.
 *   Admins can choose to 'Add as Secondary Priority' — the order is saved with an `isSecondary: true` flag
 *   and a 🔸 Secondary badge is shown on ALL views (Home timeline, Dashboard, Calendar, Order Details).
 * - ✨ [NEW] Time TBD Toggle: A new '⏰ Time TBD' checkbox on the Order Form (mirroring Date TBD) lets
 *   admins save orders where the preferred delivery time hasn't been confirmed yet. Time TBD orders skip
 *   conflict detection and display '⏰ TBD' everywhere instead of a blank/Anytime label.
 * - ✨ [NEW] Notes Visibility: Order notes are now visible in the Classic receipt view and in the
 *   Order Details modal when opened from the Home screen.
 *
 * 🚀 VERSION 1.1.4 (Catalog Access Control) - July 12, 2026
 * ----------------------------------------------------------------------------
 * - 🔒 [SECURITY] Catalog Access: Restricted access to the Catalog tab so that only the admin account (solyminx2026@gmail.com) can view and modify the bouquet catalog.
 * 
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.1.3 (Catalog UI Refinements & Auto-Numbering) - July 11, 2026
 * ----------------------------------------------------------------------------
 * - ✨ [NEW] Combined Title & Variant Number: Merged the bouquet variant number into the main title (e.g., "Nami Bouquet 7") and enclosed the entire text within the signature elegant light pink pill box across both the brochure and admin dashboards.
 * - 🐛 [FIX] Per-Bouquet Auto-Numbering: Fixed an issue where auto-numbered bouquets used a continuous global array index. Bouquets without explicit numbers are now sequentially numbered (1, 2, 3...) per unique variant name.
 * 
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.1.1 (Brochure Sync & Best Seller Engine) - July 10, 2026
 * ----------------------------------------------------------------------------
 * - ✨ [NEW] Best Seller Sync: The admin app now automatically computes the top 3 best-selling bouquets from completed orders and syncs an `isBestSeller` flag directly onto the bouquet's Firestore document.
 * - ⚡ [OPTIMIZATION] Brochure Independence: The public brochure no longer needs read access to the sensitive `orders` collection. It safely reads the pre-computed `isBestSeller` and `salePrice` flags directly from the `bouquets` collection.
 * 
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.1.0 (True AMOLED Black & Facebook-Style Dark Mode) - July 10, 2026
 * ----------------------------------------------------------------------------
 * - ✨ [NEW] Brochure-Style Catalog Cards: Completely redesigned catalog cards to match the Solyminx flower brochure design with Playfair Display typography, 4:5 aspect ratio images, sage/rose themed flower tags, and premium styling.
 * - ✨ [NEW] History Analytics Dashboard: Added a full analytics panel to the History section featuring best-selling bouquets bar chart, monthly order volume chart, top returning customers leaderboard, and summary stat cards (total revenue, avg order, unique customers).
 * - 🐛 [FIX] iOS Bottom Nav Overflow: Increased bottom content padding to prevent the last catalog items and form elements from being hidden behind the fixed bottom navigation bar on iOS devices.
 *
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.0.8 (Catalog & Brochure Upgrade, Magic Paste Fix, Receipt Patch) - July 09, 2026
 * ----------------------------------------------------------------------------
 * - ✨ [NEW] Unified Catalog Management: Redesigned the admin Catalog to feature interactive brochure-style cards, full drag-and-drop position reordering synced to Firebase, and an elegant preview modal.
 * - 🐛 [FIX] Fallback Numbering: Added automatic fallback numbering ("No. X") to catalog and brochure cards lacking a specific number to ensure variants remain clearly identifiable.
 * - 🐛 [FIX] Magic Paste Fallback Parser: Resolved an issue where the line-by-line backup parser ignored line 5+; it now correctly captures those lines as the card dedication message.
 * - 🐛 [FIX] Receipt Math Logic: Corrected receipt totals ensuring Subtotal correctly reflects the sum of the items, and Total correctly combines Subtotal + Delivery Fee.
 * - 📉 [UPDATE] Home Screen Layout: Removed the experimental financial summary cards to restore the clean daily orders focus on the Home page.
 *
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.0.5 (Terminal Statuses, Meet-up Detection & Form Update) - May 31, 2026
 * ----------------------------------------------------------------------------
 * - ✨ [NEW] Terminal Order Statuses: Added Canceled ❌, Did Not Reply 🔇, and Did Not Get 🚫 status options. Orders with these statuses are moved to History with unique color badges and can be reverted to Incoming at any time.
 * - 🐛 [FIX] Smart Meet-up vs. Delivery Detection: Rewrote Magic Paste address classification. Previously the field label "Delivery Address / Meet-up Location:" always triggered Meet-up falsely. Now checks only the typed value using street-address keyword heuristics.
 * - 📋 [UPDATE] New Order Form Template: "Copy Blank Form" now copies the updated Solyminx order form with the full ORDER & RESERVATION POLICY, GCash Payment Details block, and branded footer.
 * - 🛡️ [UPDATE] Parser Noise Filter: Extended cleanText splitter to strip ORDER & RESERVATION POLICY, GCash Payment Details, Account Name, totals lines, and closing remarks sections.
 * - 🔍 [UPDATE] Active View Filters: Dashboard, Home stats, and Calendar exclude Canceled/Did Not Reply/Did Not Get orders from active counts.
 * 
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.0.4 (Quantity Parsing, Receipt Capture & Layout Fix) - May 28, 2026
 * ----------------------------------------------------------------------------
 * - 🐛 [FIX] Quantity Parsing: Prevented `getBouquetCount` from parsing the catalog variant suffix numbers in parentheses (e.g., Nami Bouquet (4)) as the bouquet quantity. Standard catalog bouquets now count as exactly 1, preventing scheduling slot blockages and conflicts.
 * - 🐛 [FIX] Receipt Image Style Capture: Restored correct CSS layouts, borders, alignments, and custom Courier typography on downloaded/copied receipt images using an `onclone` callback to reset target offsets.
 * - 🐛 [FIX] Robust Data Parsing: Standardized order creation dates with automatic current-timestamp fallbacks to avoid "Invalid Date" errors, and cleaned up parentheses placeholders for empty payment modes.
 * 
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.0.3 (Local Dev Bypass, Image Upload Fix & Stability Patch) - May 26, 2026
 * ----------------------------------------------------------------------------
 * - 🛠️ [NEW] Localhost Logout Bypass: Resets user login state locally on localhost without invoking Firebase Auth signOut.
 * - 🐛 [FIX] Image Compression React State: Uses unified functional state updaters during image compression to prevent stale React closures from discarding other form values or blocking reference image previews.
 * - 🛡️ [UPDATE] Search Guards & Null Handling: Implemented robust null/undefined checks across dashboard, history, and sales tracker search filters and tables to prevent UI crashes.
 * 
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.0.2 (Search & Mobile Optimized Admin Dashboard) - May 23, 2026
 * ----------------------------------------------------------------------------
 * - 🛠️ [NEW] Admin Dashboard Search: Added full search capabilities to filter system feedback and requests by customer name, email, feedback content, and status.
 * - 📱 [UPDATE] Mobile Optimization: Adjusted font-sizes to prevent automatic zoom on iOS Safari, and ensured complete touch responsiveness.
 * 
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.0.1 (Pricing, Mobile & TBD Date Patch) - May 19, 2026
 * ----------------------------------------------------------------------------
 * - 📅 [NEW] Date TBD Checkbox: Toggle to set order delivery date as 'TBD' (To Be Decided).
 * - ⚙️ [UPDATE] Safety Guard Sorting: Re-engineered dashboard and history sorting logic to prevent Invalid Date crashes when handling TBD dates.
 * - 🤖 [NEW] Magic Paste Parsing: Detects words like 'TBD', 'pending', and 'decide' to auto-select the TBD option.
 * - 🌸 [UPDATE] Product Pricing: Updated prices for Soft Thumbelina, Nami Bouquet, and Spring Thumbelina.
 * - 🌸 [NEW] Catalog Additions: Added Polly Bouquet (1), Mallows Bouquet (3), and dynamic Matcha Bouquet money variants.
 * - 📱 [UPDATE] Mobile Optimizations: Complete CSS overhaul for iPhone layout clipping on Settings, Admin, and Login views.
 * 
 * ----------------------------------------------------------------------------
 * 🚀 VERSION 1.0.0 (Official Launch & Production Release) - May 18, 2026
 * ----------------------------------------------------------------------------
 * - 🌸 [NEW] Smart Order Parser (Auto-Paste Flow):
 *   - Parses raw copy-pasted text from GCash, Facebook Messenger, SMS, etc.
 *   - Auto-extracts: Customer Name, Contact Number, Address, Payment Method, Delivery Date/Time, Message, and bouquet items with 100% precision.
 * - 📋 [NEW] Elite Orders Management Dashboard:
 *   - Visual workflow tracking: Incoming ⏳ -> Preparing 🎨 -> Bouquet Done 💐 -> Out for Delivery 🚚 -> Completed.
 *   - Dedicated Admin Feedback section with collapsible, interactive accordion headers (Pending/Resolved lists).
 * - 📅 [NEW] Smart Schedule Calendar:
 *   - Fully responsive month-view calendar highlighting daily order targets.
 *   - Built-in scheduling safety buffer triggers warning modal for overlaps under 30 minutes.
 * - 🖨️ [NEW] Thermal Receipt Generator:
 *   - Generates fully itemized, print-friendly receipts using `html2canvas` for quick image downloads or direct thermal printing.
 *   - Configurable shop metadata headers (Shop Name, Address, Contact).
 * - 📊 [NEW] Real-Time Sales & Goals Tracker:
 *   - Dynamic Gross Earnings, Active Downpayments, Expense Ledger, and Net Monthly Profit tracking.
 *   - Interactive savings progress bar targeting customizable goal thresholds.
 * - 🎨 [NEW] Premium HSL Adaptive Theme:
 *   - Elegant light/dark modes themed around a customized, premium pastel-pink color palette.
 * - 📱 [NEW] iOS Mobile & PWA Optimizations:
 *   - Interactive scroll top restoration: Auto-scrolls page and subviews to top on tab switch.
 *   - Safari click drops fixed: Handled submission via onTouchEnd to beat virtual keyboard layout shifts.
 *   - Extreme PWA Caching bypass: Injected no-cache HTTP headers and meta tags to pull updates instantly on reload.
 * 
 * ============================================================================
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import './App.css';
// [TEMPORARY HACKER THEME] 
// 🚨 TO REVERT TO ORIGINAL THEME: Just comment out or delete the line below & delete src/HackerTheme.css!
import './HackerTheme.css';
import imageCompression from 'browser-image-compression';
import { collection, onSnapshot, doc, setDoc, deleteDoc, updateDoc, addDoc, getDoc, getDocs, query, where, deleteField, orderBy, limit } from "firebase/firestore";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import { db, auth, googleProvider } from "./firebase";
import { money, calculateOrderTotal, getReceiptAmounts, calculateBalance, applyPayment, buildExpense } from './domain/money';
import { getItemQuantity, occupiesSchedule, sessionIsActive, completeOrderUpdate, restoreOrderUpdate } from './domain/orders';
import { bouquetImages, bouquetPrices } from './domain/catalog';
import InventorySection from './components/InventorySection';
import { readLocalRecords, saveOrderWithUploads } from './domain/persistence';
import { getMonthlyOrderVolume, getTopReturningCustomers } from './domain/analytics';



// Picture of the bouquet in an order: the catalog picture saved on the item when the order
// was made (so old orders keep their old picture even if the catalog picture changes later),
// then the legacy /flowers file for orders made before that was saved.
const getCatalogPicture = (item) => {
  if (!item) return null;
  if (typeof item === 'object' && item.catalogImage) return item.catalogImage;
  const name = typeof item === 'string' ? item : item.name;
  return bouquetImages[name] ? `/flowers/${bouquetImages[name]}` : null;
};

// Customer reference photo first, then the catalog picture.
const getItemPicture = (item) => (typeof item === 'object' && item?.referenceImage) || getCatalogPicture(item);

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  if (timeStr === 'TBD') return '⏰ Time TBD';
  const [hours, minutes] = timeStr.split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};

// Count total bouquets in an order (each item with "(N)" counts as N, otherwise 1)
// Corrected to avoid interpreting catalog variant suffixes like "(4)" in "Nami Bouquet (4)" as quantity.
const getBouquetCount = (order) => {
  if (!order?.items || !Array.isArray(order.items)) return 1;
  return order.items.reduce((total, item) => total + getItemQuantity(item, Object.keys(bouquetPrices)), 0) || 1;
};

// Get the duration an order occupies in minutes (30 min per bouquet)
const getOrderDuration = (order) => getBouquetCount(order) * 30;

const getAdjustedTime = (timeStr, order) => {
  if (!timeStr || timeStr === 'TBD') return timeStr || '';
  try {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const totalMins = hours * 60 + minutes;
    const prepMins = order ? getOrderDuration(order) : 60; // fallback 60 min
    let adjusted = totalMins - prepMins;
    if (adjusted < 0) adjusted += 1440;
    const h = Math.floor(adjusted / 60);
    const m = adjusted % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  } catch (e) {
    return timeStr;
  }
};

// --- Reschedule Modal Component ---
function RescheduleModal({ order, onClose, onUpdate, orders = [], unavailableDates = {} }) {
  const [newDate, setNewDate] = useState(order?.deliveryDate || '');
  const [newTime, setNewTime] = useState(order?.preferredTime === 'TBD' ? '' : (order?.preferredTime || ''));
  const [isSaving, setIsSaving] = useState(false);

  if (!order) return null;

  const handleSave = async () => {
    // Validate conflicts (per-bouquet: 30 min each)
    if (newDate && newDate !== 'TBD' && newTime) {
      const getMins = (timeStr) => {
        if (!timeStr) return -1;
        const [h, m] = timeStr.split(':').map(Number);
        return h * 60 + m;
      };

      const newMins = getMins(newTime);
      const currentDuration = getOrderDuration(order);
      const newEnd = newMins + currentDuration;

      const rangesOverlap = (startA, endA, startB, endB) => startA < endB && startB < endA;

      const conflict = orders.find(o => {
        if (o.id === order.id || o.deliveryDate !== newDate || !occupiesSchedule(o) || !o.preferredTime || o.preferredTime === 'TBD') return false;
        const oMins = getMins(o.preferredTime);
        const oEnd = oMins + getOrderDuration(o);
        return rangesOverlap(newMins, newEnd, oMins, oEnd);
      });

      if (conflict) {
        let nearestMins = Math.round(newMins / 30) * 30;
        let nearestTime = '';
        let offset = 0;

        while (offset < 1440) {
          const check = (off) => {
            const testMins = nearestMins + off;
            if (testMins < 0 || testMins >= 1440) return null;
            const testEnd = testMins + currentDuration;

            const hasConflict = orders.some(o => {
              if (o.id === order.id || o.deliveryDate !== newDate || !occupiesSchedule(o) || !o.preferredTime || o.preferredTime === 'TBD') return false;
              const oMins = getMins(o.preferredTime);
              const oEnd = oMins + getOrderDuration(o);
              return rangesOverlap(testMins, testEnd, oMins, oEnd);
            });

            if (!hasConflict) {
              const h = Math.floor(testMins / 60);
              const m = testMins % 60;
              return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
            }
            return null;
          };

          if (offset === 0) {
            const exact = check(0);
            if (exact) { nearestTime = exact; break; }
          } else {
            const before = check(-offset);
            if (before) { nearestTime = before; break; }
            const after = check(offset);
            if (after) { nearestTime = after; break; }
          }
          offset += 30;
        }

        const format12h = (t) => {
          if (!t) return 'Anytime';
          const [hrs, mins] = t.split(':').map(Number);
          const period = hrs >= 12 ? 'PM' : 'AM';
          const h12 = hrs % 12 || 12;
          return `${h12}:${String(mins).padStart(2, '0')} ${period}`;
        };

        const conflictBouquets = getBouquetCount(conflict);
        const currentBouquets = getBouquetCount(order);
        alert(`⚠️ TIME CONFLICT!\n\n${conflict.customer} has ${conflictBouquets} bouquet(s) at ${format12h(conflict.preferredTime)} (takes ${conflictBouquets * 30} min).\n\nYour order has ${currentBouquets} bouquet(s) (needs ${currentDuration} min).\n\nNearest available time: ${format12h(nearestTime)}`);
        return;
      }
    }

    setIsSaving(true);
    try {
      await onUpdate(order.id, { deliveryDate: newDate, preferredTime: newTime });
      onClose();
    } catch (error) {
      console.error("Reschedule error:", error);
      alert("Failed to reschedule. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="reschedule-modal-overlay fade-in" onClick={onClose}>
      <div className="reschedule-modal-content slide-up" onClick={e => e.stopPropagation()}>
        <div className="reschedule-modal-header">
          <div className="header-icon">📅</div>
          <div>
            <h3>Reschedule Order</h3>
            <p className="subtitle">Update delivery for <strong>{order.customer}</strong></p>
          </div>
          <button className="close-modal-btn" onClick={onClose}>×</button>
        </div>

        <div className="reschedule-body">
          <div className="reschedule-field">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <label style={{ margin: 0 }}>New Delivery Date</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: '600', color: newDate === 'TBD' ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={newDate === 'TBD'}
                  onChange={e => {
                    const nextVal = e.target.checked ? 'TBD' : '';
                    setNewDate(nextVal);
                    if (nextVal === 'TBD') setNewTime('');
                  }}
                  style={{ accentColor: 'var(--primary)', width: '16px', height: '16px', cursor: 'pointer' }}
                />
                📅 Date TBD
              </label>
            </div>
            {newDate !== 'TBD' && (
              <div className="input-with-icon">
                <span className="input-icon">📅</span>
                <input
                  type="date"
                  value={newDate}
                  onChange={e => setNewDate(e.target.value)}
                  className="reschedule-input"
                />
              </div>
            )}
            {newDate !== 'TBD' && unavailableDates[newDate] && (
              <div className="unavailable-warning-banner" style={{
                marginTop: '8px',
                padding: '10px 14px',
                background: 'rgba(239, 68, 68, 0.08)',
                border: '1.5px solid rgba(239, 68, 68, 0.35)',
                borderRadius: '12px',
                fontSize: '0.82rem',
                color: '#dc2626',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                <div style={{ fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>⚠️ Marked as Not Available on Calendar</span>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text)', opacity: 0.9 }}>
                  Reason: <strong>{unavailableDates[newDate]}</strong>
                </div>
              </div>
            )}
            {newDate === 'TBD' && (
              <div style={{ padding: '10px 14px', background: 'rgba(255,107,139,0.06)', border: '1.5px dashed rgba(255,107,139,0.3)', borderRadius: '10px', fontSize: '0.85rem', color: 'var(--primary)', fontWeight: '600' }}>
                📅 Date marked as To Be Decided.
              </div>
            )}
          </div>

          {newDate !== 'TBD' && (
            <div className="reschedule-field">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ margin: 0 }}>Preferred Time</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: '600', color: !newTime ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!newTime}
                    onChange={e => setNewTime(e.target.checked ? '' : '09:00')}
                    style={{ accentColor: 'var(--primary)', width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  ⏰ Anytime
                </label>
              </div>
              {newTime && (
                <div className="input-with-icon">
                  <span className="input-icon">⏰</span>
                  <input
                    type="time"
                    value={newTime}
                    onChange={e => setNewTime(e.target.value)}
                    className="reschedule-input"
                  />
                </div>
              )}
              {!newTime && (
                <div style={{ padding: '10px 14px', background: 'rgba(255,107,139,0.06)', border: '1.5px dashed rgba(255,107,139,0.3)', borderRadius: '10px', fontSize: '0.85rem', color: 'var(--primary)', fontWeight: '600' }}>
                  ⏰ Deliver anytime on this date.
                </div>
              )}
            </div>
          )}

          <div className="reschedule-info-box">
            <span className="info-icon">💡</span>
            <p>Rescheduling will update the delivery date and time in the calendar and dashboard instantly.</p>
          </div>
        </div>

        <div className="reschedule-actions">
          <button className="reschedule-btn secondary" onClick={onClose}>Cancel</button>
          <button className="reschedule-btn primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Confirm New Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Secure Context Clipboard Helper ---
const safeCopyToClipboard = (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  } else {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful ? Promise.resolve() : Promise.reject(new Error('Copy command failed'));
    } catch (err) {
      document.body.removeChild(textArea);
      return Promise.reject(err);
    }
  }
};

// --- Order Detail Modal Component ---
// --- Order Detail Modal Component ---
function OrderDetailModal({ order, onClose, viewPreference, setFullscreenImage, setReschedulingOrder, updateOrder, completeOrder, triggerConfirm, triggerToast, setEditingOrder, setActiveTab, openPaymentModal }) {
  const receiptRef = useRef(null);
  const [isCopying, setIsCopying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  if (!order) return null;
  const receiptAmounts = getReceiptAmounts(order);

  const formatReceiptDate = (dateVal) => {
    let dateObj = null;
    if (dateVal) {
      if (typeof dateVal.toDate === 'function') {
        dateObj = dateVal.toDate();
      } else if (dateVal.seconds) {
        dateObj = new Date(dateVal.seconds * 1000);
      } else {
        dateObj = new Date(dateVal);
      }
    }
    if (!dateObj || isNaN(dateObj.getTime())) {
      dateObj = new Date();
    }
    return `${dateObj.toLocaleDateString()} ${dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const handleCopy = (text) => {
    safeCopyToClipboard(text);
    triggerToast('Copied to Clipboard!');
  };

  const copyTextFallback = () => {
    const shopName = localStorage.getItem('shopName') || 'Solyminx Flower Shop';
    const showAddress = localStorage.getItem('showAddressInReceipt') !== 'false';
    const showContact = localStorage.getItem('showContactInReceipt') !== 'false';
    const addressLine = (showAddress && localStorage.getItem('shopAddress')) ? `\nADDRESS: ${localStorage.getItem('shopAddress')}` : '';
    const contactLine = (showContact && localStorage.getItem('shopContact')) ? `\nCONTACT: ${localStorage.getItem('shopContact')}` : '';

    const text = `
---------------------------
${shopName.toUpperCase()}
---------------------------${addressLine}${contactLine}
CUSTOMER: ${order.customer}
${order.contact ? `CONTACT: ${order.contact}` : ''}
DATE: ${order.deliveryDate}
TIME: ${order.preferredTime ? formatTime(order.preferredTime) : 'ANYTIME'}
---------------------------
ITEMS:
${order.items?.map(it => {
      const name = typeof it === 'string' ? it : it.name;
      const details = typeof it === 'object' && it.details ? ` (${it.details})` : '';
      return `- ${name}${details}`;
    }).join('\n')}
---------------------------
TOTAL: ₱${(parseFloat(order.total) || 0).toFixed(2)}
BALANCE: ₱${(parseFloat(order.balance) || 0).toFixed(2)}
---------------------------
THANK YOU FOR YOUR ORDER!
`;
    safeCopyToClipboard(text.trim()).then(() => {
      triggerToast('Receipt Copied!');
    }).catch(err => {
      console.error('Text copy failed:', err);
      triggerToast('Copy Failed!');
    });
  };

  const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const copyReceiptToClipboard = async () => {
    if (!receiptRef.current) return;
    if (typeof window.html2canvas !== 'function') {
      copyTextFallback();
      return;
    }

    setIsCopying(true);
    try {
      const canvas = await window.html2canvas(receiptRef.current, {
        useCORS: true,
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        onclone: (clonedDoc) => {
          const container = clonedDoc.getElementById('capture-receipt-container');
          if (container) {
            container.style.position = 'relative';
            container.style.left = '0';
            container.style.top = '0';
          }
        }
      });

      const blobPromise = new Promise((resolve, reject) =>
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))), 'image/png')
      );

      // iOS: Use Web Share API — opens native share sheet (Save to Photos, Messages, etc.)
      // This is far more reliable than clipboard writes on iOS Safari.
      if (isIOSDevice) {
        try {
          const blob = await blobPromise;
          const file = new File([blob], 'receipt.png', { type: 'image/png' });
          if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: 'Receipt' });
          } else {
            // Older iOS without Share API: trigger download
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'receipt.png';
            a.click();
            alert('Receipt saved! 📸 Long-press the image to share or copy.');
          }
        } catch (err) {
          if (err.name !== 'AbortError') {
            copyTextFallback();
          }
        }
        setIsCopying(false);
        return;
      }

      // Non-iOS: Try clipboard write with ClipboardItem
      const canWriteImage = navigator.clipboard && window.ClipboardItem;
      if (!canWriteImage) {
        copyTextFallback();
        setIsCopying(false);
        return;
      }

      try {
        const data = [new ClipboardItem({ 'image/png': blobPromise })];
        await navigator.clipboard.write(data);
        triggerToast('Image Copied! 📸');
      } catch (err) {
        console.error('Clipboard image error:', err);
        copyTextFallback();
      }
    } catch (err) {
      console.error('html2canvas error:', err);
      copyTextFallback();
    } finally {
      setIsCopying(false);
    }
  };

  const downloadReceiptAsImage = async () => {
    if (!receiptRef.current) return;
    if (typeof window.html2canvas !== 'function') {
      alert("Still loading image engine... please try again in a second.");
      return;
    }

    setIsDownloading(true);
    try {
      const canvas = await window.html2canvas(receiptRef.current, {
        useCORS: true,
        scale: 3, // Even higher quality for download
        backgroundColor: '#ffffff',
        logging: false,
        onclone: (clonedDoc) => {
          const container = clonedDoc.getElementById('capture-receipt-container');
          if (container) {
            container.style.position = 'relative';
            container.style.left = '0';
            container.style.top = '0';
          }
        }
      });

      canvas.toBlob((blob) => {
        if (!blob) {
          alert('Failed to generate image blob');
          setIsDownloading(false);
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Receipt-${order.customer.replace(/\s+/g, '_')}-${order.deliveryDate}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setIsDownloading(false);
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }, 'image/png');
    } catch (err) {
      console.error('Download error:', err);
      setIsDownloading(false);
      alert('Failed to download image. Opening print dialog instead.');
      window.print();
    }
  };

  return (
    <div className="receipt-modal-overlay fade-in no-print-overlay" onClick={onClose}>
      {/* Hidden Receipt for Capture - Always Receipt View */}
      <div id="capture-receipt-container" style={{ position: 'absolute', left: '-9999px', top: '0' }}>
        <div className="receipt-paper" ref={receiptRef} style={{ width: '380px' }}>
          <div className="receipt-header">
            <div className="receipt-shop-name">Solyminx Flower Shop</div>
            <div className="receipt-shop-sub">{formatReceiptDate(order.createdAt)}</div>
            {((localStorage.getItem('showAddressInReceipt') !== 'false' && localStorage.getItem('shopAddress')) || (localStorage.getItem('showContactInReceipt') !== 'false' && localStorage.getItem('shopContact'))) && (
              <div style={{ marginTop: '8px', borderTop: '1px dotted #ccc', paddingTop: '8px' }}>
                {(localStorage.getItem('showAddressInReceipt') !== 'false' && localStorage.getItem('shopAddress')) && <div className="receipt-shop-sub">{localStorage.getItem('shopAddress')}</div>}
                {(localStorage.getItem('showContactInReceipt') !== 'false' && localStorage.getItem('shopContact')) && <div className="receipt-shop-sub">📞 {localStorage.getItem('shopContact')}</div>}
              </div>
            )}
          </div>

          <div className="receipt-divider"></div>

          <div className="receipt-section">
            <div className="receipt-section-title">Customer Info</div>
            <div className="receipt-row"><strong>NAME:</strong> {order.customer}</div>
            {order.username && <div className="receipt-row"><strong>USER:</strong> @{order.username}</div>}
            {order.contact && <div className="receipt-row"><strong>TEL:</strong> {order.contact}</div>}
          </div>

          <div className="receipt-section">
            <div className="receipt-section-title">Delivery Details</div>
            <div className="receipt-row"><strong>METHOD:</strong> {order.deliveryMethod || 'Not specified'}</div>
            <div className="receipt-row"><strong>AREA:</strong> {order.area}</div>
            {order.landmark && <div className="receipt-row"><strong>LANDMARK:</strong> {order.landmark}</div>}
            <div className="receipt-row"><strong>DATE:</strong> {order.deliveryDate}</div>
            <div className="receipt-row"><strong>SHIP/DELIVER:</strong> {order.preferredTime ? formatTime(order.preferredTime) : 'ANYTIME'}</div>
          </div>

          <div className="receipt-divider"></div>

          <div className="receipt-section">
            <div className="receipt-section-title">Order Items ({order.items?.length || 0})</div>
            <div className="receipt-item-list">
              {order.items?.map((item, idx) => {
                const itemName = typeof item === 'string' ? item : item.name;
                const isItemCustomized = typeof item === 'object' && item.isCustomized;
                return (
                  <div key={idx} className="receipt-item-row">
                    <div className="receipt-item-main">
                      <div className="receipt-item-name">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          {itemName}
                          {isItemCustomized && <span style={{ fontSize: '0.6rem', background: '#000', color: '#fff', padding: '1px 4px', borderRadius: '4px', fontWeight: 'bold' }}>CUSTOM</span>}
                        </div>
                        {typeof item === 'object' && item.details && (
                          <div style={{ fontSize: '0.7rem', color: '#666', fontStyle: 'italic', marginTop: '2px' }}>
                            • {item.details}
                          </div>
                        )}
                      </div>
                      <div className="receipt-item-price">₱{(parseFloat(item.price) || bouquetPrices[itemName] || 0).toFixed(2)}</div>
                    </div>
                    {item.message && (
                      <div style={{ fontSize: '0.75rem', color: '#666', fontStyle: 'italic', paddingLeft: '15px', marginTop: '4px' }}>
                        💌 "{item.message}"
                      </div>
                    )}
                  </div>
                );
              })}
              {parseFloat(order.deliveryFee) > 0 && (
                <div className="receipt-item-row">
                  <div className="receipt-item-main">
                    <div className="receipt-item-name">🚚 Delivery Fee</div>
                    <div className="receipt-item-price">₱{(parseFloat(order.deliveryFee) || 0).toFixed(2)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="receipt-total-section">
            {parseFloat(order.deliveryFee) > 0 && (
              <div className="receipt-total-row">
                <span>SUBTOTAL:</span>
                <span>₱{receiptAmounts.subtotal.toFixed(2)}</span>
              </div>
            )}
            {parseFloat(order.deliveryFee) > 0 && (
              <div className="receipt-total-row">
                <span>DELIVERY:</span>
                <span>₱{(parseFloat(order.deliveryFee) || 0).toFixed(2)}</span>
              </div>
            )}
            <div className="receipt-total-row grand-total">
              <span>TOTAL:</span>
              <span>₱{receiptAmounts.total.toFixed(2)}</span>
            </div>
            <div className="receipt-total-row" style={{ marginTop: '10px', fontStyle: 'italic' }}>
              <span>PAYMENT{order.payment ? ` (${order.payment})` : ''}:</span>
              <span>₱{receiptAmounts.paid.toFixed(2)}</span>
            </div>
            <div className="receipt-total-row" style={{ fontWeight: 'bold', borderTop: '1px solid #eee', paddingTop: '10px' }}>
              <span>BALANCE DUE:</span>
              <span style={{ color: order.balance > 0 ? '#e05270' : '#22c55e' }}>₱{(parseFloat(order.balance) || 0).toFixed(2)}</span>
            </div>
          </div>

          <div className="receipt-footer">
            <div className="receipt-thanks">THANK YOU FOR YOUR TRUST!</div>
            <div className="receipt-shop-sub">Follow us for more blooms</div>
          </div>
        </div>
      </div>

      <div className="receipt-content" onClick={e => e.stopPropagation()}>
        <button className="receipt-close no-print" onClick={onClose}>×</button>

        {viewPreference === 'receipt' ? (
          <div className="receipt-paper print-content">
            <div className="receipt-header">
              <div className="receipt-shop-name">Solyminx Flower Shop</div>
              <div className="receipt-shop-sub">{formatReceiptDate(order.createdAt)}</div>
              {((localStorage.getItem('showAddressInReceipt') !== 'false' && localStorage.getItem('shopAddress')) || (localStorage.getItem('showContactInReceipt') !== 'false' && localStorage.getItem('shopContact'))) && (
                <div style={{ marginTop: '8px', borderTop: '1px dotted #ccc', paddingTop: '8px' }}>
                  {(localStorage.getItem('showAddressInReceipt') !== 'false' && localStorage.getItem('shopAddress')) && <div className="receipt-shop-sub">{localStorage.getItem('shopAddress')}</div>}
                  {(localStorage.getItem('showContactInReceipt') !== 'false' && localStorage.getItem('shopContact')) && <div className="receipt-shop-sub">📞 {localStorage.getItem('shopContact')}</div>}
                </div>
              )}
            </div>

            <div className="receipt-divider"></div>

            <div className="receipt-section">
              <div className="receipt-section-title">Customer Info</div>
              <div className="receipt-row"><strong>NAME:</strong> {order.customer}</div>
              {order.username && <div className="receipt-row"><strong>USER:</strong> @{order.username}</div>}
              {order.contact && <div className="receipt-row"><strong>TEL:</strong> {order.contact}</div>}
            </div>

            <div className="receipt-section">
              <div className="receipt-section-title">Delivery Details</div>
              <div className="receipt-row"><strong>METHOD:</strong> {order.deliveryMethod || 'Not specified'}</div>
              <div className="receipt-row"><strong>AREA:</strong> {order.area}</div>
              {order.landmark && <div className="receipt-row"><strong>LANDMARK:</strong> {order.landmark}</div>}
              <div className="receipt-row"><strong>DATE:</strong> {order.deliveryDate}</div>
              <div className="receipt-row"><strong>SHIP/DELIVER:</strong> {order.preferredTime ? formatTime(order.preferredTime) : 'ANYTIME'}</div>
            </div>

            <div className="receipt-divider"></div>

            <div className="receipt-section">
              <div className="receipt-section-title">Order Items ({order.items?.length || 0})</div>
              <div className="receipt-item-list">
                {order.items?.map((item, idx) => {
                  const itemName = typeof item === 'string' ? item : item.name;
                  const catalogPic = getCatalogPicture(item);
                  const isItemCustomized = typeof item === 'object' && item.isCustomized;
                  return (
                    <div key={idx} className="receipt-item-row">
                      <div className="receipt-item-main">
                        <div className="receipt-item-name">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            {itemName}
                            {isItemCustomized && <span style={{ fontSize: '0.6rem', background: '#000', color: '#fff', padding: '1px 4px', borderRadius: '4px', fontWeight: 'bold' }}>CUSTOM</span>}
                          </div>
                          {typeof item === 'object' && item.details && (
                            <div style={{ fontSize: '0.7rem', color: '#666', fontStyle: 'italic', marginTop: '2px' }}>
                              • {item.details}
                            </div>
                          )}
                        </div>
                        <div className="receipt-item-price">₱{(parseFloat(item.price) || bouquetPrices[itemName] || 0).toFixed(2)}</div>
                      </div>
                      {item.message && (
                        <div style={{ fontSize: '0.75rem', color: '#666', fontStyle: 'italic', paddingLeft: '15px', marginTop: '4px' }}>
                          💌 "{item.message}"
                        </div>
                      )}
                      {catalogPic && (
                        <img
                          src={catalogPic}
                          alt={itemName}
                          className="receipt-item-image no-print"
                          onClick={(e) => { e.stopPropagation(); setFullscreenImage(catalogPic); }}
                        />
                      )}
                      {item.referenceImage && (
                        <div style={{ marginTop: '5px' }}>
                          <div style={{ fontSize: '0.65rem', color: 'var(--primary)', fontWeight: 'bold', marginBottom: '4px' }}>📸 REFERENCE PHOTO:</div>
                          <img
                            src={item.referenceImage}
                            alt="Reference"
                            className="receipt-item-image"
                            style={{ width: '100%', height: 'auto', maxHeight: '200px', borderRadius: '8px' }}
                            onClick={(e) => { e.stopPropagation(); setFullscreenImage(item.referenceImage); }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
                {parseFloat(order.deliveryFee) > 0 && (
                  <div className="receipt-item-row">
                    <div className="receipt-item-main">
                      <div className="receipt-item-name">🚚 Delivery Fee</div>
                      <div className="receipt-item-price">₱{(parseFloat(order.deliveryFee) || 0).toFixed(2)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="receipt-total-section">
              {parseFloat(order.deliveryFee) > 0 && (
                <div className="receipt-total-row">
                  <span>SUBTOTAL:</span>
                  <span>₱{receiptAmounts.subtotal.toFixed(2)}</span>
                </div>
              )}
              {parseFloat(order.deliveryFee) > 0 && (
                <div className="receipt-total-row">
                  <span>DELIVERY:</span>
                  <span>₱{(parseFloat(order.deliveryFee) || 0).toFixed(2)}</span>
                </div>
              )}
              <div className="receipt-total-row grand-total">
                <span>TOTAL:</span>
                <span>₱{receiptAmounts.total.toFixed(2)}</span>
              </div>
              <div className="receipt-total-row" style={{ marginTop: '10px', fontStyle: 'italic' }}>
                <span>PAYMENT{order.payment ? ` (${order.payment})` : ''}:</span>
                <span>₱{receiptAmounts.paid.toFixed(2)}</span>
              </div>
              <div className="receipt-total-row" style={{ fontWeight: 'bold', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                <span>BALANCE DUE:</span>
                <span style={{ color: order.balance > 0 ? '#e05270' : '#22c55e' }}>₱{(parseFloat(order.balance) || 0).toFixed(2)}</span>
              </div>
            </div>

            <div className="receipt-footer">
              <div className="receipt-thanks">THANK YOU FOR YOUR TRUST!</div>
              <div className="receipt-shop-sub">Follow us for more blooms</div>
              <div className="receipt-barcode-box">
                <div className="receipt-barcode-line"></div>
                <div className="receipt-order-num">ORD-{order.id?.substring(0, 8).toUpperCase()}</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="classic-order-view">
            <h2 className="classic-view-title">Order Details</h2>

            <div className="classic-details-container">
              {order.isSecondary && (
                <div className="classic-secondary-alert">
                  <span style={{ fontSize: '1.1rem' }}>🔸</span>
                  <span><strong>Secondary Priority</strong> — Another order has priority in this time slot.</span>
                </div>
              )}

              {/* Section 1: Customer Information */}
              <div className="classic-section-card">
                <div className="classic-section-header">
                  <span className="classic-section-icon">👤</span>
                  <h4>Customer Information</h4>
                </div>
                <div className="classic-grid-2">
                  <div className="classic-field">
                    <span className="classic-label">Customer:</span>
                    <div className="classic-value-group">
                      <span className="classic-value bold">{order.customer}</span>
                      <button className="copy-mini-btn" onClick={(e) => { e.stopPropagation(); handleCopy(order.customer); }} title="Copy Name">📋 Copy</button>
                    </div>
                  </div>

                  {order.username && (
                    <div className="classic-field">
                      <span className="classic-label">Username:</span>
                      <div className="classic-value-group">
                        <span className="classic-value username">@{order.username}</span>
                        <button className="copy-mini-btn" onClick={(e) => { e.stopPropagation(); handleCopy(order.username); }} title="Copy Username">📋 Copy</button>
                      </div>
                    </div>
                  )}

                  <div className="classic-field">
                    <span className="classic-label">Contact #:</span>
                    <div className="classic-value-group">
                      <span className="classic-value">{order.contact || 'N/A'}</span>
                      {order.contact && (
                        <button className="copy-mini-btn" onClick={(e) => { e.stopPropagation(); handleCopy(order.contact); }} title="Copy Contact">📋 Copy</button>
                      )}
                    </div>
                  </div>

                  <div className="classic-field">
                    <span className="classic-label">Platform:</span>
                    <span className={`platform-badge-pill ${order.platform?.toLowerCase() || 'messenger'}`}>
                      {order.platform === 'Messenger' ? '💬' :
                        order.platform === 'TikTok' ? '🎵' :
                          order.platform === 'Instagram' ? '📸' :
                            order.platform === 'Walk-in' ? '🚶' : '📱'} {order.platform || 'Messenger'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Section 2: Delivery & Logistics */}
              <div className="classic-section-card">
                <div className="classic-section-header">
                  <span className="classic-section-icon">📍</span>
                  <h4>Delivery & Schedule</h4>
                </div>
                <div className="classic-grid-2">
                  <div className="classic-field">
                    <span className="classic-label">Method:</span>
                    <span className="classic-value bold">🚚 {order.deliveryMethod || 'Not specified'}</span>
                  </div>

                  {order.deliveryMethod === 'Lalamove' && order.status !== 'Completed' && (
                    <div className="classic-field">
                      <span className="classic-label">🛵 Lalamove Payer:</span>
                      <div className="delivery-payer-inline classic-level" onClick={e => e.stopPropagation()}>
                        <div className="dp-buttons">
                          <button
                            className={`dp-btn ${order.deliveryPayer === 'Paid by Sender' ? 'active sender' : ''}`}
                            onClick={() => {
                              const next = order.deliveryPayer === 'Paid by Sender' ? '' : 'Paid by Sender';
                              const label = next === 'Paid by Sender' ? 'Paid by Sender (Sender Pays)' : 'Pending (Unassigned)';
                              triggerConfirm({
                                title: '🛵 Lalamove Fee Payer',
                                message: `Set Lalamove delivery fee payer to: ${label}?`,
                                type: 'primary',
                                confirmText: 'Confirm',
                                onConfirm: () => updateOrder(order.id, { deliveryPayer: next })
                              });
                            }}
                          >
                            💳 Sender Pays
                          </button>
                          <button
                            className={`dp-btn ${order.deliveryPayer === 'Paid by Recipient' ? 'active recipient' : ''}`}
                            onClick={() => {
                              const next = order.deliveryPayer === 'Paid by Recipient' ? '' : 'Paid by Recipient';
                              const label = next === 'Paid by Recipient' ? 'Paid by Recipient (Recipient Pays)' : 'Pending (Unassigned)';
                              triggerConfirm({
                                title: '🛵 Lalamove Fee Payer',
                                message: `Set Lalamove delivery fee payer to: ${label}?`,
                                type: 'primary',
                                confirmText: 'Confirm',
                                onConfirm: () => updateOrder(order.id, { deliveryPayer: next })
                              });
                            }}
                          >
                            🤝 Recipient Pays
                          </button>
                          {!order.deliveryPayer && <span className="dp-pending">⚠️ Payer Not Set</span>}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="classic-field">
                    <span className="classic-label">Delivery Date:</span>
                    <span className="classic-value bold">📅 {order.deliveryDate === 'TBD' ? 'Date TBD' : order.deliveryDate}</span>
                  </div>

                  <div className="classic-field full-width">
                    <span className="classic-label">Area / Address:</span>
                    <div className="classic-value-group">
                      <span className="classic-value">{order.area || 'N/A'}</span>
                      {order.area && (
                        <button className="copy-mini-btn" onClick={(e) => { e.stopPropagation(); handleCopy(order.area); }} title="Copy Address">📋 Copy</button>
                      )}
                    </div>
                  </div>

                  {order.landmark && (
                    <div className="classic-field full-width">
                      <span className="classic-label">Landmark:</span>
                      <span className="classic-value italic">📍 {order.landmark}</span>
                    </div>
                  )}

                  <div className="classic-field">
                    <span className="classic-label">Delivery Time:</span>
                    <span className="classic-value highlight-time">🕒 {order.preferredTime ? formatTime(order.preferredTime) : 'Anytime'}</span>
                  </div>

                  <div className="classic-field">
                    <span className="classic-label">Start Prep:</span>
                    <span className="classic-value highlight-prep">🌸 {order.preferredTime ? formatTime(getAdjustedTime(order.preferredTime, order)) : 'Anytime'}</span>
                  </div>
                </div>
              </div>

              {/* Section 3: Ordered Items & Add-ons */}
              <div className="classic-section-card">
                <div className="classic-section-header">
                  <span className="classic-section-icon">💐</span>
                  <h4>Ordered Items</h4>
                </div>
                <div className="classic-items-list">
                  {order.items?.map((it, i) => (
                    <div key={i} className="classic-item-row">
                      <div className="classic-item-main">
                        <span className="classic-item-bullet">•</span>
                        <span className="classic-item-name">{typeof it === 'string' ? it : it.name}</span>
                        <span className="classic-item-price">₱{(parseFloat(it.price) || 0).toFixed(2)}</span>
                      </div>
                      {it.message && (
                        <div className="classic-item-msg">
                          <span>💌 "{it.message}"</span>
                          <button className="copy-mini-btn" onClick={(e) => { e.stopPropagation(); handleCopy(it.message); }}>📋 Copy</button>
                        </div>
                      )}
                      {it.details && <div className="classic-item-details">{it.details}</div>}
                      {getItemPicture(it) && (
                        <div className="classic-item-thumb">
                          <img
                            src={getItemPicture(it)}
                            alt="Item"
                            onClick={() => setFullscreenImage(getItemPicture(it))}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {order.customDescription && (
                  <div className="classic-field full-width" style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px dashed #e2e8f0' }}>
                    <span className="classic-label">🎨 Custom Flower Details:</span>
                    <span className="classic-value">{order.customDescription}</span>
                  </div>
                )}

                {order.addOns && order.addOns !== 'None' && (
                  <div className="classic-field full-width" style={{ marginTop: '8px' }}>
                    <span className="classic-label">🎁 Add-ons:</span>
                    <span className="classic-value bold">+{order.addOns}</span>
                  </div>
                )}
              </div>

              {/* Section 4: Payment Summary */}
              <div className="classic-section-card">
                <div className="classic-section-header">
                  <span className="classic-section-icon">💳</span>
                  <h4>Payment Breakdown</h4>
                </div>
                <div className="classic-grid-2">
                  <div className="classic-field">
                    <span className="classic-label">Payment Status:</span>
                    <span className="classic-value bold">{order.payment}</span>
                  </div>
                  <div className="classic-field">
                    <span className="classic-label">Total Amount:</span>
                    <span className="classic-value amount-total">₱{(parseFloat(order.total) || 0).toFixed(2)}</span>
                  </div>
                  <div className="classic-field">
                    <span className="classic-label">Received:</span>
                    <span className="classic-value amount-received">₱{((parseFloat(order.total) || 0) - (parseFloat(order.balance) || 0)).toFixed(2)}</span>
                  </div>
                  <div className="classic-field">
                    <span className="classic-label">Balance Due:</span>
                    <span className={`classic-value amount-balance ${order.balance > 0 ? 'due' : 'paid'}`}>
                      ₱{(parseFloat(order.balance) || 0).toFixed(2)} {order.balance > 0 ? '⚠️' : '✅'}
                    </span>
                  </div>
                </div>
                {order.balance > 0 && openPaymentModal && (
                  <div style={{ marginTop: '14px' }}>
                    <button
                      type="button"
                      onClick={() => { onClose(); setTimeout(() => openPaymentModal(order), 100); }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontWeight: '700',
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 14px rgba(16,185,129,0.35)',
                        transition: 'transform 0.15s, box-shadow 0.15s',
                        letterSpacing: '0.02em'
                      }}
                      onMouseOver={e => { e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 6px 18px rgba(16,185,129,0.45)'; }}
                      onMouseOut={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 4px 14px rgba(16,185,129,0.35)'; }}
                    >
                      <span style={{ fontSize: '1.1rem' }}>💸</span> Additional Payment
                    </button>
                  </div>
                )}
              </div>

              {/* Section 5: Screenshots & References Gallery */}
              {((order.screenshots && order.screenshots.length > 0) || order.referenceImage) && (
                <div className="classic-section-card">
                  <div className="classic-section-header">
                    <span className="classic-section-icon">📸</span>
                    <h4>Screenshots & Reference Photos</h4>
                  </div>
                  {(order.screenshots && order.screenshots.length > 0) ? (
                    <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '6px' }}>
                      {order.screenshots.map((url, idx) => (
                        <img
                          key={idx}
                          src={url}
                          alt={`Screenshot ${idx + 1}`}
                          onClick={(e) => { e.stopPropagation(); setFullscreenImage({ images: order.screenshots, currentIndex: idx }); }}
                          style={{ height: '120px', width: 'auto', borderRadius: '10px', cursor: 'pointer', border: '1px solid #e2e8f0', flexShrink: 0 }}
                        />
                      ))}
                    </div>
                  ) : order.referenceImage && (
                    <img
                      src={order.referenceImage}
                      alt="Reference"
                      onClick={(e) => { e.stopPropagation(); setFullscreenImage(order.referenceImage); }}
                      style={{ maxHeight: '200px', width: 'auto', borderRadius: '10px', cursor: 'pointer', border: '1px solid #e2e8f0' }}
                    />
                  )}
                </div>
              )}

              {/* Section 6: Card Message (if any) */}
              {order.message && (
                <div className="classic-section-card card-message-box">
                  <div className="classic-section-header">
                    <span className="classic-section-icon">💌</span>
                    <h4>Card Dedication Message</h4>
                    <button className="copy-mini-btn" onClick={(e) => { e.stopPropagation(); handleCopy(order.message); }}>📋 Copy</button>
                  </div>
                  <p className="classic-quote-text">"{order.message}"</p>
                </div>
              )}

              {/* Section 7: Special Notes (if any) */}
              {order.notes && (
                <div className="classic-section-card order-notes-box">
                  <div className="classic-section-header">
                    <span className="classic-section-icon">📝</span>
                    <h4>Special Instructions & Notes</h4>
                  </div>
                  <p className="classic-notes-content">{order.notes}</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="receipt-actions no-print" style={{ display: 'flex', borderTop: '1px solid #eee', padding: '20px', flexDirection: 'column', gap: '15px' }}>
          <div className="modal-status-workflow" style={{
            width: '100%',
            background: 'rgba(0,0,0,0.03)',
            padding: '15px',
            borderRadius: '14px',
            border: '1px solid rgba(0,0,0,0.05)'
          }}>
            <p style={{
              fontSize: '0.7rem',
              fontWeight: '800',
              color: 'var(--text-muted)',
              marginBottom: '12px',
              textAlign: 'center',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>✨ Update Order Status</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
              {[
                { id: 'Incoming', label: 'Incoming', icon: '📥', class: 'incoming' },
                { id: 'Preparing', label: 'Preparing', icon: '🌸', class: 'preparing' },
                { id: 'Bouquet Done', label: 'Done', icon: '✨', class: 'ready' }
              ].map((s) => (
                <button
                  key={s.id}
                  className={`status-item-btn ${s.class} ${(order.status || 'Incoming') === s.id ? 'active' : ''}`}
                  onClick={() => {
                    if ((order.status || 'Incoming') === s.id) return;
                    triggerConfirm({
                      title: `Update to ${s.label}?`,
                      message: `Change order status to ${s.label}?`,
                      type: s.class === 'ready' ? 'success' : 'primary',
                      confirmText: `Set to ${s.label}`,
                      onConfirm: () => updateOrder(order.id, { status: s.id })
                    });
                  }}
                  style={{ flex: '1 1 auto', minWidth: '80px' }}
                >
                  <span className="btn-icon">{s.icon}</span>
                  <span className="btn-text">{s.label}</span>
                </button>
              ))}
              <button
                className={`status-item-btn completed ${order.status === 'Completed' ? 'active' : ''}`}
                onClick={() => {
                  if (order.status === 'Completed') return;
                  triggerConfirm({
                    title: 'Mark as Completed?',
                    message: 'Move this order to history? 🎉',
                    type: 'success',
                    confirmText: 'Complete',
                    onConfirm: () => completeOrder(order.id)
                  });
                }}
                style={{ flex: '1 1 auto', minWidth: '80px' }}
              >
                <span className="btn-icon">✅</span>
                <span className="btn-text">Complete</span>
              </button>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', marginTop: '8px', borderTop: '1px dashed #e2e8f0', paddingTop: '8px', width: '100%' }}>
              {[
                { id: 'Canceled', label: 'Canceled', icon: '❌', class: 'cancelled' },
                { id: 'Did Not Reply', label: 'Did Not Reply', icon: '🔇', class: 'unresponsive' },
                { id: 'Did Not Get', label: 'Did Not Get', icon: '🚫', class: 'unclaimed' }
              ].map((s) => (
                <button
                  key={s.id}
                  className={`status-item-btn ${s.class} ${(order.status || 'Incoming') === s.id ? 'active' : ''}`}
                  onClick={() => {
                    if ((order.status || 'Incoming') === s.id) return;
                    triggerConfirm({
                      title: `Mark as ${s.label}?`,
                      message: `Change order status to ${s.label}?`,
                      type: 'warning',
                      confirmText: `Set to ${s.label}`,
                      onConfirm: () => updateOrder(order.id, { status: s.id })
                    });
                  }}
                  style={{ flex: '1 1 auto', minWidth: '80px' }}
                >
                  <span className="btn-icon">{s.icon}</span>
                  <span className="btn-text">{s.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
            <button className="receipt-btn secondary" style={{ flex: 1 }} onClick={copyReceiptToClipboard} disabled={isCopying || isDownloading}>
              {isCopying ? '⌛ Generating...' : isIOSDevice ? '📤 Share Image' : '📋 Copy Image'}
            </button>
            <button className="receipt-btn secondary" style={{ flex: 1 }} onClick={downloadReceiptAsImage} disabled={isCopying || isDownloading}>
              {isDownloading ? '⌛ Saving...' : '💾 Save as Image'}
            </button>
          </div>

          <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
            {setEditingOrder && setActiveTab && (
              <button
                className="receipt-btn secondary"
                style={{ flex: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingOrder(order);
                  setActiveTab('orders');
                  onClose();
                }}
              >
                ✏️ Edit Order
              </button>
            )}
            <button className="receipt-btn secondary" style={{ flex: 1 }} onClick={(e) => { e.stopPropagation(); setReschedulingOrder(order); }} disabled={isCopying || isDownloading}>
              📅 Reschedule
            </button>
          </div>

          <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
            <button className="receipt-btn primary" style={{ flex: 1 }} onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const APP_VERSION = '1.3.2';

function GalleryComingSoon() {
  return (
    <section className="gallery-coming-soon fade-in" aria-labelledby="gallery-coming-soon-title">
      <div className="gallery-coming-soon-card">
        <div className="gallery-coming-soon-icon" aria-hidden="true">🖼️</div>
        <span className="gallery-coming-soon-label">Solyminx Gallery</span>
        <h1 id="gallery-coming-soon-title">Coming Soon</h1>
        <p>A dedicated space for your flower arrangements and favorite creations is being prepared.</p>
        <div className="gallery-coming-soon-divider" aria-hidden="true"><span />✦<span /></div>
      </div>
    </section>
  );
}

const LightboxViewer = ({ fullscreenImage, setFullscreenImage }) => {
  const isGallery = typeof fullscreenImage === 'object' && fullscreenImage !== null;
  const images = isGallery ? fullscreenImage.images : [fullscreenImage];
  const currentIndex = isGallery ? fullscreenImage.currentIndex : 0;

  const handlePrev = (e) => {
    if (e) e.stopPropagation();
    if (currentIndex > 0) {
      setFullscreenImage({ images, currentIndex: currentIndex - 1 });
    }
  };

  const handleNext = (e) => {
    if (e) e.stopPropagation();
    if (currentIndex < images.length - 1) {
      setFullscreenImage({ images, currentIndex: currentIndex + 1 });
    }
  };

  const touchStartXRef = React.useRef(0);
  const swipeTriggeredRef = React.useRef(false);

  const handleTouchStart = (e) => {
    touchStartXRef.current = e.changedTouches[0].screenX;
    swipeTriggeredRef.current = false;
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].screenX;
    if (touchEndX < touchStartXRef.current - 50) {
      handleNext(e);
      swipeTriggeredRef.current = true;
    } else if (touchEndX > touchStartXRef.current + 50) {
      handlePrev(e);
      swipeTriggeredRef.current = true;
    }
  };

  const handleOverlayClick = (e) => {
    if (!swipeTriggeredRef.current) {
      setFullscreenImage(null);
    }
    // Don't immediately reset swipeTriggered here, let it protect against ghost clicks.
    // It will be reset on the next touchStart anyway.
  };

  if (!images || images.length === 0 || !images[currentIndex]) {
    // Failsafe in case of bad data
    return (
      <div className="lightbox-overlay fade-in" onClick={() => setFullscreenImage(null)}>
        <button className="close-lightbox-btn">×</button>
        <p style={{ color: 'white', marginTop: 'auto', marginBottom: 'auto' }}>Image preview not available.</p>
      </div>
    );
  }

  return (
    <div
      className="lightbox-overlay fade-in"
      onClick={handleOverlayClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
    >
      <button className="close-lightbox-btn" onClick={(e) => { e.stopPropagation(); setFullscreenImage(null); }}>×</button>

      {images.length > 1 && currentIndex > 0 && (
        <button className="lightbox-nav-btn prev" onClick={handlePrev}>‹</button>
      )}

      <img
        src={images[currentIndex]}
        alt="Fullscreen preview"
        className="lightbox-img"
        onClick={e => e.stopPropagation()}
        onError={(e) => {
          e.target.onerror = null;
          e.target.style.display = 'none';
          e.target.insertAdjacentHTML('afterend', '<p style="color:white; z-index: 10001;">Failed to load image format.</p>');
        }}
      />

      {images.length > 1 && currentIndex < images.length - 1 && (
        <button className="lightbox-nav-btn next" onClick={handleNext}>›</button>
      )}

      {images.length > 1 && (
        <div className="lightbox-counter" onClick={e => e.stopPropagation()}>
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [isOrderSaving, setIsOrderSaving] = useState(false);
  const [orders, setOrders] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [savings, setSavings] = useState({ current: 0, target: 5000 });
  const [editingOrder, setEditingOrder] = useState(null);
  const [reschedulingOrder, setReschedulingOrder] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState(null);
  const [adminFeedback, setAdminFeedback] = useState([]);
  const [globalUploadProgress, setGlobalUploadProgress] = useState(null);
  const [globalUploadLabel, setGlobalUploadLabel] = useState('');
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [catalogBouquets, setCatalogBouquets] = useState([]);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const ALLOWED_EMAILS = ['solyminx2026@gmail.com', 'loyminx2026@gmail.com'];
  const isAuthorized = user && ALLOWED_EMAILS.includes(user.email?.toLowerCase());
  const isAdmin = isAuthorized;
  const sessionDocIdRef = useRef(null);

  // --- Device Profile Setup Check ---
  // On localhost (testing mode): shows so you can test it anytime
  // On production (when deployed): one-time setup only per device
  useEffect(() => {
    if (user && isAuthorized) {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocalhost) {
        // setShowOnboardingModal(true); // [COMMENTED OUT] - disabled for local testing
      } else {
        const savedDeviceUsername = localStorage.getItem('solyminx_device_username');
        if (!savedDeviceUsername) {
          setShowOnboardingModal(true);
        }
      }
    }
  }, [user, isAuthorized]);

  // --- Session Tracking (No duplicates per device) ---
  useEffect(() => {
    if (!user || !isAuthorized) return;
    // The localhost user is a mock, not a Firebase-authenticated account.
    if (window.location.hostname === 'localhost') return;
    let ended = false;
    let heartbeatTimer = null;

    const endSession = (explicit = true) => {
      if (ended || !sessionDocIdRef.current) return;
      if (explicit) ended = true;
      const nowIso = new Date().toISOString();
      updateDoc(doc(db, 'sessions', sessionDocIdRef.current), {
        endedAt: nowIso,
        lastActiveAt: nowIso
      }).catch(() => { });
    };

    const startSession = async () => {
      try {
        let ip = 'Unknown';
        let location = 'Unknown Location';
        let browser = 'Unknown';
        let os = 'Unknown';

        try {
          const ipRes = await fetch('https://api.ipify.org?format=json');
          const ipData = await ipRes.json();
          ip = ipData.ip || 'Unknown';
        } catch (_) { }

        try {
          const locRes = await fetch(`https://ipapi.co/${ip}/json/`);
          const locData = await locRes.json();
          if (locData && locData.city) {
            const parts = [locData.city, locData.region, locData.country_name].filter(Boolean);
            location = parts.join(', ');
          }
        } catch (_) { }

        const ua = navigator.userAgent;
        if (ua.includes('Edg')) browser = 'Edge';
        else if (ua.includes('Chrome') && !ua.includes('Chromium')) browser = 'Chrome';
        else if (ua.includes('Firefox')) browser = 'Firefox';
        else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
        else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';

        if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
        else if (ua.includes('Android')) os = 'Android';
        else if (ua.includes('Windows')) os = 'Windows';
        else if (ua.includes('Mac')) os = 'macOS';
        else if (ua.includes('Linux')) os = 'Linux';

        let deviceId = localStorage.getItem('solyminx_device_id');
        if (!deviceId) {
          deviceId = 'dev_' + Math.random().toString(36).substring(2, 10) + '_' + Date.now().toString(36);
          localStorage.setItem('solyminx_device_id', deviceId);
        }
        const deviceUsername = localStorage.getItem('solyminx_device_username') || '';
        const nowIso = new Date().toISOString();

        // 1. Check if this browser tab/session already has an active session ID
        let existingSessionId = sessionStorage.getItem('solyminx_session_doc_id');

        // 2. Query Firestore for any existing active session on THIS exact device
        let reusedDocId = null;
        try {
          if (existingSessionId) {
            const existingSnap = await getDoc(doc(db, 'sessions', existingSessionId));
            if (existingSnap.exists()) {
              const data = existingSnap.data();
              if (!data.endedAt || (Date.now() - new Date(data.lastActiveAt || data.startedAt).getTime() < 3 * 60 * 1000)) {
                reusedDocId = existingSessionId;
              }
            }
          }

          if (!reusedDocId) {
            const deviceQuery = query(
              collection(db, 'sessions'),
              where('deviceId', '==', deviceId),
              where('endedAt', '==', null)
            );
            const devSnap = await getDocs(deviceQuery);
            if (!devSnap.empty) {
              // Reuse the most recent open session for this device
              const mostRecent = devSnap.docs[0];
              reusedDocId = mostRecent.id;
              // Close any other duplicate open sessions on this device
              for (let i = 1; i < devSnap.docs.length; i++) {
                updateDoc(doc(db, 'sessions', devSnap.docs[i].id), { endedAt: nowIso }).catch(() => { });
              }
            }
          }
        } catch (_) { }

        if (reusedDocId) {
          // Reuse existing session doc — NO DUPLICATE!
          sessionDocIdRef.current = reusedDocId;
          sessionStorage.setItem('solyminx_session_doc_id', reusedDocId);
          await updateDoc(doc(db, 'sessions', reusedDocId), {
            lastActiveAt: nowIso,
            endedAt: null,
            ip,
            location,
            browser,
            os,
            username: deviceUsername || undefined
          }).catch(() => { });
        } else {
          // Create new session doc
          const docRef = await addDoc(collection(db, 'sessions'), {
            userId: user.uid,
            userEmail: user.email,
            deviceId,
            username: deviceUsername,
            startedAt: nowIso,
            lastActiveAt: nowIso,
            endedAt: null,
            ip,
            location,
            browser,
            os,
          });
          sessionDocIdRef.current = docRef.id;
          sessionStorage.setItem('solyminx_session_doc_id', docRef.id);
        }

        // Periodic Heartbeat every 20 seconds to keep session alive
        heartbeatTimer = setInterval(() => {
          if (sessionDocIdRef.current && !ended && document.visibilityState !== 'hidden') {
            updateDoc(doc(db, 'sessions', sessionDocIdRef.current), {
              lastActiveAt: new Date().toISOString()
            }).catch(() => { });
          }
        }, 20000);
      } catch (e) {
        console.error('Session tracking error:', e);
      }
    };

    startSession();

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (sessionDocIdRef.current && !ended) {
          updateDoc(doc(db, 'sessions', sessionDocIdRef.current), {
            lastActiveAt: new Date().toISOString()
          }).catch(() => { });
        }
      }
    };

    const handleUnload = () => endSession(true);
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
      document.removeEventListener('visibilitychange', handleVisibility);
      endSession(true);
    };
  }, [user, isAuthorized]);

  useEffect(() => {
    const lastSeenVersion = localStorage.getItem('solyminx_last_version');
    if (lastSeenVersion !== APP_VERSION) {
      setShowUpdateNotification(true);
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
    const scrollContainers = document.querySelectorAll(
      '.app-content, .subview-scroll-premium, .settings-container-premium, ' +
      '.admin-container-premium, .orders-container-premium, .history-container-premium, ' +
      '.sales-container-premium, .calendar-container-premium, .dashboard-container-premium, ' +
      '.home-container-premium'
    );
    scrollContainers.forEach(container => {
      container.scrollTop = 0;
    });
  }, [activeTab]);

  const triggerConfetti = () => {
    const container = document.createElement('div');
    container.className = 'confetti-container';
    document.body.appendChild(container);

    const colors = ['#f472b6', '#fb7185', '#fda4af', '#fce7f3', '#fdf2f8'];
    const count = 40;

    for (let i = 0; i < count; i++) {
      const petal = document.createElement('div');
      petal.className = 'petal';
      petal.style.left = Math.random() * 100 + 'vw';
      petal.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      petal.style.width = Math.random() * 15 + 10 + 'px';
      petal.style.height = petal.style.width;
      petal.style.animationDelay = Math.random() * 2 + 's';
      petal.style.animationDuration = Math.random() * 3 + 2 + 's';
      petal.style.opacity = Math.random();
      container.appendChild(petal);
    }

    setTimeout(() => {
      container.remove();
    }, 5000);
  };

  const [toast, setToast] = useState(null);
  const triggerToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  };

  const triggerConfirm = (config) => {
    setConfirmConfig(config);
  };
  const [paymentModal, setPaymentModal] = useState(null);
  const openPaymentModal = (order) => setPaymentModal(order);
  const [viewPreference, setViewPreference] = useState(() => localStorage.getItem('viewPreference') || 'classic');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const [themeMode, setThemeMode] = useState(() => localStorage.getItem('solyminx_theme_mode') || 'normal');
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [showAddressInReceipt, setShowAddressInReceipt] = useState(() => localStorage.getItem('showAddressInReceipt') !== 'false');
  const [showContactInReceipt, setShowContactInReceipt] = useState(() => localStorage.getItem('showContactInReceipt') !== 'false');

  useEffect(() => {
    document.body.classList.toggle('dark-theme', darkMode);
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    document.body.classList.toggle('hacker-theme', themeMode === 'hacker');
    localStorage.setItem('solyminx_theme_mode', themeMode);
  }, [themeMode]);

  // 👾 Hacker Theme Lag Simulator: 15% probability that a click glitches/drops
  useEffect(() => {
    if (themeMode !== 'hacker') return;

    const handleHackerLagClick = (e) => {
      // 15% chance to drop the click
      if (Math.random() < 0.15) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Trigger visual CRT glitch animation on screen
        document.body.classList.add('hacker-click-glitch');
        setTimeout(() => {
          document.body.classList.remove('hacker-click-glitch');
        }, 180);
      }
    };

    window.addEventListener('click', handleHackerLagClick, true); // capture phase
    return () => {
      window.removeEventListener('click', handleHackerLagClick, true);
    };
  }, [themeMode]);

  useEffect(() => {
    localStorage.setItem('showAddressInReceipt', showAddressInReceipt);
  }, [showAddressInReceipt]);

  useEffect(() => {
    localStorage.setItem('showContactInReceipt', showContactInReceipt);
  }, [showContactInReceipt]);

  useEffect(() => {
    localStorage.setItem('viewPreference', viewPreference);
  }, [viewPreference]);

  useEffect(() => {
    if (window.location.hostname === 'localhost') {
      setUser({
        uid: 'dev-admin-uid',
        displayName: 'Dev Administrator',
        email: 'solyminx2026@gmail.com',
        photoURL: 'https://lh3.googleusercontent.com/a/default-user=s96-c'
      });
      setLoading(false);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const login = async () => {
    if (window.location.hostname === 'localhost') {
      setUser({
        uid: 'dev-admin-uid',
        displayName: 'Dev Administrator',
        email: 'solyminx2026@gmail.com',
        photoURL: 'https://lh3.googleusercontent.com/a/default-user=s96-c'
      });
      return;
    }
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error("Login Error:", error);
      if (error.code !== 'auth/popup-closed-by-user') {
        alert("Login failed: " + error.message);
      }
    }
  };
  const logout = () => {
    if (window.location.hostname === 'localhost') {
      setUser(null);
      return;
    }
    signOut(auth);
  };

  useEffect(() => {
    if (!user || !isAuthorized) {
      setOrders([]);
      setExpenses([]);
      setSavings({ current: 0, target: 5000 });
      return;
    }

    if (window.location.hostname === 'localhost') {
      setOrders(readLocalRecords('local_orders', [
        {
          id: 'ord1',
          customer: 'Juan Dela Cruz',
          preferredTime: '10:00',
          deliveryDate: new Date().toISOString().split('T')[0],
          status: 'Incoming',
          deliveryMethod: 'Lalamove',
          total: 1500,
          balance: 500,
          items: [{ name: 'Soft Thumbelina Collection (1)', price: 1500, qty: 1 }],
          remarks: 'Happy Birthday!'
        }
      ]));
      setExpenses(readLocalRecords('local_expenses', [
        { id: 'exp1', name: 'Flowers wrap', amount: 350 }
      ]));
      setSavings({ current: 1500, target: 5000 });
      return;
    }

    const qOrders = query(collection(db, "orders"), where("uid", "==", user.uid));
    const unsubOrders = onSnapshot(qOrders, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setOrders(ordersData);
    });

    const qExpenses = query(collection(db, "expenses"), where("uid", "==", user.uid));
    const unsubExpenses = onSnapshot(qExpenses, (snapshot) => {
      const expensesData = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setExpenses(expensesData);
    });

    const unsubSavings = onSnapshot(doc(db, "settings", user.uid), (docSnap) => {
      if (docSnap.exists()) {
        setSavings(docSnap.data());
      } else {
        setDoc(doc(db, "settings", user.uid), { current: 1500, target: 5000, notes: {} });
      }
    });

    return () => {
      unsubOrders();
      unsubExpenses();
      unsubSavings();
    };
  }, [user, isAuthorized]);

  // Fetch catalog bouquets from Firebase to keep OrderForm in sync
  useEffect(() => {
    const q = query(collection(db, "bouquets"));
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs
        .filter(d => !d.id.startsWith('_') && d.id !== 'catalog_status' && d.data().name && !d.data().isNovemberFeatured)
        .map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        if (a.position !== undefined && b.position !== undefined) return a.position - b.position;
        if (a.position !== undefined) return -1;
        if (b.position !== undefined) return 1;
        return (a.createdAt || '') > (b.createdAt || '') ? -1 : 1;
      });
      setCatalogBouquets(data);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user || !isAdmin) return;

    if (window.location.hostname === 'localhost') {
      setAdminFeedback([
        {
          id: 'fb1',
          userName: 'Alice Smith',
          userEmail: 'alice@example.com',
          text: 'Love the soft thumbelina bouquet! It looks amazing.',
          status: 'pending',
          createdAt: new Date(Date.now() - 3600000).toISOString()
        },
        {
          id: 'fb2',
          userName: 'Bob Johnson',
          userEmail: 'bob@example.com',
          text: 'Can we add support for Apple Pay on checkout?',
          status: 'pending',
          createdAt: new Date(Date.now() - 7200000).toISOString()
        },
        {
          id: 'fb3',
          userName: 'Charlie Brown',
          userEmail: 'charlie@example.com',
          text: 'The scheduling buffer feature is wonderful.',
          status: 'resolved',
          createdAt: new Date(Date.now() - 86400000).toISOString()
        }
      ]);
      return;
    }

    const qFeedback = query(collection(db, "feedback"), orderBy("createdAt", "desc"));
    const unsubFeedback = onSnapshot(qFeedback, (snapshot) => {
      const feedbackData = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setAdminFeedback(feedbackData);
    });

    return () => unsubFeedback();
  }, [user, isAdmin]);



  const notifiedOrders = useRef(new Set());

  useEffect(() => {
    if (!("Notification" in window)) return;
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }

    const checkNotifications = () => {
      const now = new Date();
      const currentTodayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      orders.forEach(o => {
        if ((o.status === 'Incoming' || o.status === 'Pending Orders') && o.deliveryDate === currentTodayStr && o.preferredTime) {
          const [h, m] = o.preferredTime.split(':').map(Number);
          const orderTime = new Date();
          orderTime.setHours(h, m, 0, 0);

          const diffInMins = (orderTime - now) / (1000 * 60);

          // Trigger notification 30 minutes before
          if (diffInMins > 25 && diffInMins <= 30 && !notifiedOrders.current.has(o.id)) {
            new Notification(`🔔 Upcoming Order: ${o.customer}`, {
              body: `Delivery at ${formatTime(o.preferredTime)} to ${o.area}.`,
              icon: user.photoURL
            });
            notifiedOrders.current.add(o.id);
          }
        }
      });
    };

    const interval = setInterval(checkNotifications, 60000);
    return () => clearInterval(interval);
  }, [orders, user]);

  const getButtonClass = (tab) => `nav-button ${activeTab === tab ? 'active' : ''}`;

  const addOrder = async (newOrder) => {
    if (!user || !isAuthorized) throw new Error('Please sign in with an authorized account.');
    if (window.location.hostname === 'localhost') {
      const newOrderWithId = {
        ...newOrder,
        id: 'local_ord_' + crypto.randomUUID(),
        status: 'Incoming',
        createdAt: new Date().toISOString(),
        uid: user.uid
      };
      const updated = [newOrderWithId, ...readLocalRecords('local_orders', orders)];
      localStorage.setItem('local_orders', JSON.stringify(updated));
      setOrders(updated);
      return newOrderWithId.id;
    }
    try {
      const docRef = await addDoc(collection(db, "orders"), {
        ...newOrder,
        status: 'Incoming',
        createdAt: new Date().toISOString(),
        uid: user.uid
      });
      return docRef.id;
    } catch (e) {
      console.error("Error adding document: ", e);
      throw e;
    }
  };

  const deleteOrder = async (id) => {
    if (window.location.hostname === 'localhost') {
      setOrders(prev => {
        const updated = prev.filter(o => o.id !== id);
        localStorage.setItem('local_orders', JSON.stringify(updated));
        return updated;
      });
      return;
    }
    await deleteDoc(doc(db, "orders", id));
  };

  const updateOrder = async (id, updates) => {
    if (!user || !isAuthorized) throw new Error('Please sign in with an authorized account.');
    if (window.location.hostname === 'localhost') {
      const updated = readLocalRecords('local_orders', orders).map(o => o.id === id ? { ...o, ...updates } : o);
      localStorage.setItem('local_orders', JSON.stringify(updated));
      setOrders(updated);
    } else {
      const dbUpdates = { ...updates };
      delete dbUpdates.isUploadingImage;
      if (Object.keys(dbUpdates).length > 0) {
        await updateDoc(doc(db, "orders", id), dbUpdates);
      }
      setOrders(prev => prev.map(o => o.id === id ? { ...o, ...updates } : o));
    }
  };

  const deleteFeedback = async (id) => {
    if (window.location.hostname === 'localhost') {
      setAdminFeedback(prev => {
        const updated = prev.filter(f => f.id !== id);
        localStorage.setItem('local_feedback', JSON.stringify(updated));
        return updated;
      });
      return;
    }
    await deleteDoc(doc(db, "feedback", id));
  };

  const getOrderForUpdate = async id => {
    if (window.location.hostname === 'localhost') {
      const order = readLocalRecords('local_orders', orders).find(entry => entry.id === id);
      if (!order) throw new Error('Order no longer exists.');
      return order;
    }
    const snapshot = await getDoc(doc(db, 'orders', id));
    if (!snapshot.exists()) throw new Error('Order no longer exists.');
    return snapshot.data();
  };

  const completeOrder = async id => {
    const order = await getOrderForUpdate(id);
    await updateOrder(id, completeOrderUpdate(order));
    triggerConfetti();
  };

  const revertOrder = async id => {
    const order = await getOrderForUpdate(id);
    await updateOrder(id, restoreOrderUpdate(order));
  };

  const addExpense = async (amount, description) => {
    if (!user || !isAuthorized) throw new Error('Please sign in with an authorized account.');
    const expense = { ...buildExpense(amount, description), uid: user.uid };
    if (window.location.hostname === 'localhost') {
      const newExpense = {
        ...expense,
        id: 'local_exp_' + crypto.randomUUID()
      };
      const updated = [newExpense, ...readLocalRecords('local_expenses', expenses)];
      localStorage.setItem('local_expenses', JSON.stringify(updated));
      setExpenses(updated);
      return newExpense.id;
    }
    const saved = await addDoc(collection(db, 'expenses'), expense);
    return saved.id;
  };

  const deleteExpense = async (id) => {
    if (window.location.hostname === 'localhost') {
      setExpenses(prev => {
        const updated = prev.filter(e => e.id !== id);
        localStorage.setItem('local_expenses', JSON.stringify(updated));
        return updated;
      });
      return;
    }
    await deleteDoc(doc(db, "expenses", id));
  };

  const updateSavings = async (newSavings) => {
    if (!user) return;
    if (window.location.hostname === 'localhost') {
      setSavings(prev => {
        const updated = { ...prev, ...newSavings };
        localStorage.setItem('local_savings', JSON.stringify(updated));
        return updated;
      });
      return;
    }
    await setDoc(doc(db, "settings", user.uid), newSavings);
  };

  const saveCalendarNote = async (dateStr, text) => {
    if (!user) return;
    if (window.location.hostname === 'localhost') {
      const updatedNotes = { ...(savings.notes || {}) };
      if (!text.trim()) {
        delete updatedNotes[dateStr];
      } else {
        updatedNotes[dateStr] = text;
      }
      const updated = { ...savings, notes: updatedNotes };
      setSavings(updated);
      localStorage.setItem('local_savings', JSON.stringify(updated));
      return;
    }
    try {
      if (!text.trim()) {
        await setDoc(doc(db, "settings", user.uid), {
          notes: { [dateStr]: deleteField() }
        }, { merge: true });
      } else {
        await setDoc(doc(db, "settings", user.uid), {
          notes: { [dateStr]: text }
        }, { merge: true });
      }
    } catch (e) {
      console.error("Error saving calendar note: ", e);
      alert("Error saving note. Please try again.");
    }
  };

  const saveUnavailableDate = async (dateStr, text, isUnavailable) => {
    if (!user) return;
    const reasonText = text ? text.trim() : 'Not available';
    if (window.location.hostname === 'localhost') {
      const updatedUnavailable = { ...(savings.unavailableDates || {}) };
      if (!isUnavailable) {
        delete updatedUnavailable[dateStr];
      } else {
        updatedUnavailable[dateStr] = reasonText;
      }
      const updated = { ...savings, unavailableDates: updatedUnavailable };
      setSavings(updated);
      localStorage.setItem('local_savings', JSON.stringify(updated));
      return;
    }
    try {
      if (!isUnavailable) {
        await setDoc(doc(db, "settings", user.uid), {
          unavailableDates: { [dateStr]: deleteField() }
        }, { merge: true });
      } else {
        await setDoc(doc(db, "settings", user.uid), {
          unavailableDates: { [dateStr]: reasonText }
        }, { merge: true });
      }
    } catch (e) {
      console.error("Error saving unavailable date: ", e);
      alert("Error saving availability. Please try again.");
    }
  };


  if (loading) return (
    <div className="skeleton-wrapper">
      <div className="skeleton-sidebar"></div>
      <div className="skeleton-main">
        <div className="skeleton-header">
          <div className="skeleton-title"></div>
          <div className="skeleton-button"></div>
        </div>
        <div className="skeleton-search"></div>
        <div className="skeleton-cards">
          {[1, 2, 3].map(i => (
            <div key={i} className="skeleton-card">
              <div className="skeleton-card-header">
                <div className="skeleton-avatar"></div>
                <div className="skeleton-text-group">
                  <div className="skeleton-line short"></div>
                  <div className="skeleton-line medium"></div>
                </div>
              </div>
              <div className="skeleton-card-content">
                <div className="skeleton-line long"></div>
                <div className="skeleton-line long"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (user && !isAuthorized) {
    return (
      <div className="login-screen">
        <div className="login-container" style={{ maxWidth: '460px', textAlign: 'center' }}>
          <div className="login-card fade-in" style={{ padding: '40px 28px', borderRadius: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>🚫</div>
            <h2 style={{ fontSize: '1.4rem', fontWeight: '900', color: '#ef4444', marginBottom: '8px' }}>Access Denied</h2>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '16px', lineHeight: '1.5' }}>
              The signed-in Google account (<strong>{user.email}</strong>) is not authorized to access Solyminx Orders.
            </p>
            <div style={{ padding: '12px 16px', background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '12px', fontSize: '0.82rem', color: '#ef4444', marginBottom: '24px', fontWeight: '600' }}>
              🔒 Exclusive Access: Only authorized shop accounts (solyminx2026@gmail.com) are permitted.
            </div>
            <button
              onClick={logout}
              className="confirm-btn proceed danger"
              style={{ width: '100%', padding: '14px', borderRadius: '14px', fontWeight: '800', fontSize: '0.95rem', background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer' }}
            >
              🚪 Sign Out & Switch Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="login-screen">
        <div className="login-container">
          <div className="login-branding">
            <img src="/Logo.png" alt="Solyminx Logo" className="login-logo-img" />
            <h1 className="login-title">Solyminx</h1>
            <p className="login-tagline">Bloom your business with ease. 🌸</p>
            <div className="login-features" style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '28px', textAlign: 'left' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '1.4rem' }}>🌸</span>
                <div>
                  <strong style={{ fontSize: '0.95rem', color: 'var(--text)' }}>Smart Order Parser (Auto-Paste)</strong>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '2px 0 0 0', lineHeight: '1.4' }}>
                    Copy-paste raw order forms from Facebook Messenger, SMS, or GCash. The system automatically extracts customer details, delivery info, and bouquet items with 100% precision.
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '1.4rem' }}>📋</span>
                <div>
                  <strong style={{ fontSize: '0.95rem', color: 'var(--text)' }}>Elite Orders Dashboard</strong>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '2px 0 0 0', lineHeight: '1.4' }}>
                    Track active orders through highly visible stages: Incoming ⏳, Preparing 🎨, Bouquet Done 💐, Out for Delivery 🚚, and Completed.
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '1.4rem' }}>📅</span>
                <div>
                  <strong style={{ fontSize: '0.95rem', color: 'var(--text)' }}>Smart Schedule & Buffer Warnings</strong>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '2px 0 0 0', lineHeight: '1.4' }}>
                    Interactive calendar featuring automatic scheduling conflict alerts for overlapping deliveries with a 30-minute buffer window.
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '1.4rem' }}>🖨️</span>
                <div>
                  <strong style={{ fontSize: '0.95rem', color: 'var(--text)' }}>Thermal Receipt Generator</strong>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '2px 0 0 0', lineHeight: '1.4' }}>
                    Generate itemized, professional receipts with customizable shop headers for printing or instant image sharing.
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '1.4rem' }}>📊</span>
                <div>
                  <strong style={{ fontSize: '0.95rem', color: 'var(--text)' }}>Live Analytics & Goals</strong>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '2px 0 0 0', lineHeight: '1.4' }}>
                    Real-time sales tracking, active downpayments, operating expenses, and monthly profits with milestone visual tracking.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="login-card fade-in">
            <h2>Welcome Back</h2>
            <p>Access your dashboard and manage your flower shop from anywhere.</p>
            <button className="google-login-btn" onClick={login}>
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" />
              Sign in with Google
            </button>
            <p className="login-footer">By signing in, you agree to manage your floral business efficiently.</p>
          </div>
        </div>
      </div>
    );
  }

  const getTabTitle = () => {
    switch (activeTab) {
      case 'home': return 'Home';
      case 'dashboard': return 'Orders';
      case 'orders': return 'New Order';
      case 'history': return 'History';
      case 'sales': return 'Sales & Finance';
      case 'gallery': return 'Gallery';
      case 'calendar': return 'Calendar';
      case 'settings': return 'Settings';
      default: return 'Solyminx';
    }
  };

  return (
    <div className="app-container">
      <div className="mobile-header">
        <div className="mobile-user-info">
          <img src={user.photoURL} alt={user.displayName} className="mobile-avatar" />
          <div className="mobile-user-details">
            <p className="user-name">{user.displayName}</p>
            <p className="user-email">{user.email}</p>
          </div>
        </div>
        <h1>{getTabTitle()}</h1>
        <button className="mobile-settings" disabled={isOrderSaving} onClick={() => setActiveTab('settings')}>⚙️</button>
      </div>
      <div className="sidebar" inert={isOrderSaving ? true : undefined}>
        <h1 className="desktop-title">Solyminx</h1>
        <div className="user-profile">
          <img src={user.photoURL} alt={user.displayName} />
          <div>
            <p className="user-name">{user.displayName}</p>
            <p className="user-email">{user.email}</p>
          </div>
        </div>
        <button className={getButtonClass('home')} onClick={() => setActiveTab('home')}>
          <span>🏠</span> Home
        </button>
        <button className={getButtonClass('dashboard')} onClick={() => setActiveTab('dashboard')}>
          <span>📋</span> Orders
        </button>
        <button className={getButtonClass('calendar')} onClick={() => setActiveTab('calendar')}>
          <span>📅</span> Calendar
        </button>

        <button className={getButtonClass('history')} onClick={() => setActiveTab('history')}>
          <span>📜</span> History
        </button>
        <button className={getButtonClass('gallery')} onClick={() => setActiveTab('gallery')}>
          <span>🖼️</span> Gallery
        </button>
        <button className={getButtonClass('sales')} onClick={() => setActiveTab('sales')}>
          <span>💰</span> Sales
        </button>
        {isAdmin && (
          <button className={getButtonClass('catalog')} onClick={() => setActiveTab('catalog')}>
            <span>🌸</span> Catalog
          </button>
        )}

        <button className={`${getButtonClass('settings')} settings-nav`} onClick={() => setActiveTab('settings')} style={{ marginTop: 'auto' }}>
          <span>⚙️</span> Settings
        </button>
      </div>

      <div className="main-content">
        {activeTab === 'home' && (
          <Home
            orders={orders}
            updateOrder={updateOrder}
            completeOrder={completeOrder}
            deleteOrder={deleteOrder}
            setActiveTab={setActiveTab}
            setEditingOrder={setEditingOrder}
            triggerConfirm={triggerConfirm}
            openPaymentModal={openPaymentModal}
            viewPreference={viewPreference}
            setFullscreenImage={setFullscreenImage}
            setReschedulingOrder={setReschedulingOrder}
            triggerToast={triggerToast}
            calendarNotes={savings?.notes || {}}
            unavailableDates={savings?.unavailableDates || {}}
          />
        )}
        {activeTab === 'dashboard' && (
          <Dashboard
            orders={orders.filter(o => o.status !== 'Completed' && o.status !== 'Canceled' && o.status !== 'Did Not Reply' && o.status !== 'Did Not Get')}
            allOrders={orders}
            updateOrder={updateOrder}
            completeOrder={completeOrder}
            deleteOrder={deleteOrder}
            setActiveTab={setActiveTab}
            setEditingOrder={setEditingOrder}
            triggerConfirm={triggerConfirm}
            openPaymentModal={openPaymentModal}
            viewPreference={viewPreference}
            setFullscreenImage={setFullscreenImage}
            setReschedulingOrder={setReschedulingOrder}
            triggerToast={triggerToast}
            calendarNotes={savings?.notes || {}}
            unavailableDates={savings?.unavailableDates || {}}
          />
        )}
        {activeTab === 'orders' && (
          <OrderForm
            onSavingChanged={setIsOrderSaving}
            onAdd={addOrder}
            onUpdate={updateOrder}
            orders={orders}
            setActiveTab={setActiveTab}
            activeTab={activeTab}
            editingOrder={editingOrder}
            setEditingOrder={setEditingOrder}
            setFullscreenImage={setFullscreenImage}
            triggerConfirm={triggerConfirm}
            triggerToast={triggerToast}
            setGlobalUploadProgress={setGlobalUploadProgress}
            setGlobalUploadLabel={setGlobalUploadLabel}
            catalogBouquets={catalogBouquets}
            unavailableDates={savings?.unavailableDates || {}}
          />
        )}
        {activeTab === 'history' && (
          <HistorySection
            orders={orders.filter(o => o.status === 'Completed' || o.status === 'Canceled' || o.status === 'Did Not Reply' || o.status === 'Did Not Get')}
            allOrders={orders}
            deleteOrder={deleteOrder}
            revertOrder={revertOrder}
            triggerConfirm={triggerConfirm}
            viewPreference={viewPreference}
            setFullscreenImage={setFullscreenImage}
            setReschedulingOrder={setReschedulingOrder}
            updateOrder={updateOrder}
            completeOrder={completeOrder}
            triggerToast={triggerToast}
            setEditingOrder={setEditingOrder}
            setActiveTab={setActiveTab}
          />
        )}
        {activeTab === 'gallery' && (
          <GalleryComingSoon />
        )}
        {activeTab === 'sales' && (
          <SalesTracker
            orders={orders}
            expenses={expenses}
            savings={savings}
            setSavings={updateSavings}
            addExpense={addExpense}
            deleteExpense={deleteExpense}
            triggerConfirm={triggerConfirm}
          />
        )}

        {isAdmin && activeTab === 'catalog' && (
          <CatalogManager
            orders={orders}
            setGlobalUploadLabel={setGlobalUploadLabel}
            setGlobalUploadProgress={setGlobalUploadProgress}
            triggerToast={triggerToast}
            triggerConfirm={triggerConfirm}
          />
        )}

        {activeTab === 'calendar' && (
          <ScheduleView
            orders={orders}
            updateOrder={updateOrder}
            calendarNotes={savings?.notes || {}}
            unavailableDates={savings?.unavailableDates || {}}
            saveCalendarNote={saveCalendarNote}
            saveUnavailableDate={saveUnavailableDate}
            triggerToast={triggerToast}
          />
        )}
        {activeTab === 'settings' && (
          <SettingsSection
            user={user}
            logout={logout}
            savings={savings}
            setSavings={updateSavings}
            orders={orders}
            expenses={expenses}
            viewPreference={viewPreference}
            setViewPreference={setViewPreference}
            showAddressInReceipt={showAddressInReceipt}
            setShowAddressInReceipt={setShowAddressInReceipt}
            showContactInReceipt={showContactInReceipt}
            setShowContactInReceipt={setShowContactInReceipt}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            themeMode={themeMode}
            setThemeMode={setThemeMode}
            isAdmin={isAdmin}
            adminFeedback={adminFeedback}
            deleteFeedback={deleteFeedback}
            triggerConfirm={triggerConfirm}
            triggerToast={triggerToast}
            setGlobalUploadProgress={setGlobalUploadProgress}
            setGlobalUploadLabel={setGlobalUploadLabel}
          />
        )}
      </div>

      {activeTab !== 'orders' && (
        <button
          className="fab-btn"
          onClick={() => { setActiveTab('orders'); setEditingOrder(null); }}
          title="New Order"
        >
          <span className="fab-icon">+</span>
          <span className="fab-text">New Order</span>
        </button>
      )}

      <ConfirmModal
        config={confirmConfig}
        onClose={() => setConfirmConfig(current => current === confirmConfig ? null : current)}
      />

      <PaymentModal
        order={paymentModal}
        onClose={() => setPaymentModal(null)}
        updateOrder={updateOrder}
        triggerToast={triggerToast}
      />

      {/* Toast Notification */}
      {globalUploadProgress !== null && (
        <div className="global-upload-bar">
          <div className="global-upload-progress" style={{ width: `${globalUploadProgress}%` }}></div>
          <div className="upload-toast">
            <span className="spinner-mini"></span>
            {globalUploadLabel || 'Uploading Image...'} {Math.round(globalUploadProgress)}%
          </div>
        </div>
      )}

      {toast && <div className="toast-premium fade-in">{toast}</div>}

      {/* Reschedule Modal */}
      {reschedulingOrder && (
        <RescheduleModal
          order={reschedulingOrder}
          onClose={() => setReschedulingOrder(null)}
          onUpdate={updateOrder}
          orders={orders}
          unavailableDates={savings?.unavailableDates || {}}
        />
      )}

      {/* Fullscreen Image Lightbox */}
      {fullscreenImage && (
        <LightboxViewer
          fullscreenImage={fullscreenImage}
          setFullscreenImage={setFullscreenImage}
        />
      )}
      {showUpdateNotification && (
        <div className="confirm-modal-overlay fade-in" style={{ zIndex: 10001 }}>
          <div className="confirm-modal-content slide-up" style={{ maxWidth: '450px', padding: '28px', borderRadius: '28px', textAlign: 'left' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '3rem', marginBottom: '10px' }}>🚀</div>
              <h3 style={{ fontSize: '1.4rem', fontWeight: '900', color: 'var(--text)', margin: '0 0 4px 0' }}>New Update Available!</h3>
              <span style={{ fontSize: '0.85rem', fontWeight: '800', color: '#94a3b8', background: '#f1f5f9', padding: '4px 12px', borderRadius: '20px', display: 'inline-block' }}>v{APP_VERSION} Installed Successfully</span>
            </div>

            <div style={{ background: 'rgba(244,114,182,0.06)', border: '1px solid rgba(244,114,182,0.15)', borderRadius: '16px', padding: '16px', margin: '0 0 24px 0', fontSize: '0.85rem' }}>
              <strong style={{ color: 'var(--primary)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>✨ What's New in this Update:</strong>
              <ul style={{ margin: 0, paddingLeft: '16px', color: 'var(--text)', lineHeight: '1.6' }}>
                <li><strong>📅 Full Year:</strong> Kita na ang January hanggang December sa Monthly Order Volume.</li>
                <li><strong>📱 Responsive:</strong> Lahat ng 12 month labels ay visible sa desktop at mobile.</li>
              </ul>
            </div>

            <button
              onClick={() => {
                localStorage.setItem('solyminx_last_version', APP_VERSION);
                setShowUpdateNotification(false);
              }}
              className="confirm-btn proceed primary"
              style={{ width: '100%', padding: '14px', borderRadius: '16px', fontSize: '1rem', fontWeight: '800', boxShadow: '0 4px 15px rgba(244,114,182,0.4)' }}
            >
              Awesome, Got it!
            </button>
          </div>
        </div>
      )}

      {showOnboardingModal && (
        <UserProfileOnboardingModal
          user={user}
          sessionDocIdRef={sessionDocIdRef}
          onClose={() => {
            setShowOnboardingModal(false);
          }}
        />
      )}
    </div>
  );
}

// --- Home Component (Landing Page) ---
function Home({ orders, updateOrder, completeOrder, deleteOrder, setActiveTab, triggerConfirm, openPaymentModal, viewPreference, setFullscreenImage, setReschedulingOrder, triggerToast, setEditingOrder, calendarNotes = {}, unavailableDates = {} }) {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeStatusMenu, setActiveStatusMenu] = useState(null);
  const [timelineFilter, setTimelineFilter] = useState('All');

  const [activeFeePayerMenu, setActiveFeePayerMenu] = useState(null);

  useEffect(() => {
    if (selectedOrder) {
      const latest = orders.find(o => o.id === selectedOrder.id);
      if (latest && JSON.stringify(latest) !== JSON.stringify(selectedOrder)) {
        setSelectedOrder(latest);
      }
    }
  }, [orders, selectedOrder]);

  useEffect(() => {
    const handleGlobalClick = () => {
      setActiveStatusMenu(null);
      setActiveFeePayerMenu(null);
    };
    if (activeStatusMenu || activeFeePayerMenu) {
      window.addEventListener('click', handleGlobalClick);
    }
    return () => window.removeEventListener('click', handleGlobalClick);
  }, [activeStatusMenu, activeFeePayerMenu]);

  const pendingOrders = orders.filter(o => o.status === 'Incoming' || o.status === 'Pending Orders' || o.status === 'Preparing');
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const todaysOrders = orders.filter(o => o.deliveryDate === todayStr && o.status !== 'Canceled' && o.status !== 'Did Not Reply' && o.status !== 'Did Not Get');

  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const monthSales = orders.filter(o => {
    if (o.status !== 'Completed') return false;
    const [y, m] = o.deliveryDate.split('-').map(Number);
    return y === currentYear && (m - 1) === currentMonth;
  });
  const bouquetsSoldThisMonth = monthSales.reduce((sum, o) => sum + (o.items?.length || 0), 0);
  const bouquetsSoldToday = todaysOrders.filter(o => o.status === 'Completed').reduce((sum, o) => sum + (o.items?.length || 0), 0);

  // This week (Mon - Sun)
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1));
  startOfWeek.setHours(0, 0, 0, 0);
  const bouquetsSoldThisWeek = orders
    .filter(o => o.status === 'Completed' && o.deliveryDate !== 'TBD' && new Date(o.deliveryDate) >= startOfWeek)
    .reduce((sum, o) => sum + (o.items?.length || 0), 0);

  const sortedPending = [...pendingOrders].sort((a, b) => {
    const parseDate = (d) => {
      if (!d || d === 'TBD') return new Date('9999-12-31');
      return new Date(d);
    };
    const dateDiff = parseDate(a.deliveryDate) - parseDate(b.deliveryDate);
    if (dateDiff !== 0) return dateDiff;
    const timeA = a.preferredTime || "99:99";
    const timeB = b.preferredTime || "99:99";
    return timeA.localeCompare(timeB);
  });
  const nextOrder = sortedPending.length > 0 ? sortedPending[0] : null;
  const nextOrderDate = nextOrder ? nextOrder.deliveryDate : null;
  const nextOrderTime = nextOrder ? nextOrder.preferredTime : null;

  // Status Counts for Summary Header
  const incomingCount = todaysOrders.filter(o => o.status === 'Incoming' || !o.status).length;
  const preparingCount = todaysOrders.filter(o => o.status === 'Preparing').length;
  const readyCount = todaysOrders.filter(o => o.status === 'Bouquet Done').length;
  const completedCount = todaysOrders.filter(o => o.status === 'Completed').length;

  // Timeline Filtering
  const filteredTimelineOrders = todaysOrders.filter(o => {
    if (timelineFilter === 'All') return true;
    if (timelineFilter === 'Preparing') return o.status === 'Preparing' || o.status === 'Incoming' || !o.status;
    if (timelineFilter === 'Ready') return o.status === 'Bouquet Done';
    if (timelineFilter === 'Completed') return o.status === 'Completed';
    return true;
  });

  // Prep Time helper (30 mins per bouquet prior to delivery time)
  const getPrepTime = (timeStr, order) => {
    if (!timeStr || timeStr === 'TBD') return '99:99';
    try {
      const [h, m] = timeStr.split(':').map(Number);
      const deliveryMins = h * 60 + m;
      const count = order?.items?.length || 1;
      const prepDuration = count * 30;
      let prepMins = deliveryMins - prepDuration;
      if (prepMins < 0) prepMins += 24 * 60;
      const prepH = Math.floor(prepMins / 60);
      const prepM = prepMins % 60;
      return `${String(prepH).padStart(2, '0')}:${String(prepM).padStart(2, '0')}`;
    } catch {
      return timeStr || '99:99';
    }
  };

  // Urgent Prep helper (checks if current time is within or past start prep time for incomplete/preparing orders)
  const isUrgentPrep = (o) => {
    if (o.status === 'Completed' || o.status === 'Canceled' || o.status === 'Bouquet Done') return false;
    if (!o.preferredTime || o.preferredTime === 'TBD') return false;
    try {
      const currentMins = now.getHours() * 60 + now.getMinutes();
      const prepTimeStr = getPrepTime(o.preferredTime, o);
      const [h, m] = prepTimeStr.split(':').map(Number);
      const prepMins = h * 60 + m;
      return currentMins >= (prepMins - 15);
    } catch {
      return false;
    }
  };

  return (
    <div className="fade-in home-container">
      <div className="home-hero">
        <div>
          <h2><span className="gradient-text">Welcome Back!</span> 🌸</h2>
          <p className="subtitle">Here's your schedule and business overview.</p>
        </div>
      </div>

      {/* Today's Unavailable Banner */}
      {unavailableDates[todayStr] && (
        <div className="home-today-unavailable mb-20" style={{
          background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
          borderLeft: '4px solid #ef4444',
          borderRadius: '16px',
          padding: '16px 20px',
          marginBottom: '20px',
          boxShadow: '0 4px 15px rgba(239, 68, 68, 0.15)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          animation: 'fadeIn 0.5s ease-out'
        }}>
          <span style={{ fontSize: '1.5rem', marginTop: '-2px' }}>🚫</span>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: '0 0 4px 0', color: '#dc2626', fontSize: '0.8rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Today Marked as Not Available</h4>
            <p style={{ margin: 0, color: '#991b1b', fontSize: '0.95rem', fontWeight: '600', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{unavailableDates[todayStr]}</p>
          </div>
        </div>
      )}

      {/* Today's Calendar Note Banner */}
      {calendarNotes[todayStr] && (
        <div className="home-today-note" style={{
          background: 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)',
          borderLeft: '4px solid #f472b6',
          borderRadius: '16px',
          padding: '16px 20px',
          marginBottom: '20px',
          boxShadow: '0 4px 15px rgba(244, 114, 182, 0.1)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          animation: 'fadeIn 0.5s ease-out'
        }}>
          <span style={{ fontSize: '1.5rem', marginTop: '-2px' }}>📝</span>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: '0 0 4px 0', color: '#be185d', fontSize: '0.8rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Today's Note</h4>
            <p style={{ margin: 0, color: '#831843', fontSize: '0.95rem', fontWeight: '500', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{calendarNotes[todayStr]}</p>
          </div>
        </div>
      )}

      {/* TODAY'S ORDERS - Big Featured Card */}
      <div className="featured-stat-container mb-20" onClick={() => setActiveTab('dashboard')} style={{ cursor: 'pointer' }}>
        <div className="stat-box featured">
          <span className="stat-label">TODAY'S ORDERS</span>
          <span className="stat-value">{todaysOrders.length}</span>

          {/* Done / Left split bar */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '12px', marginBottom: '10px' }}>
            <div className="featured-sub-card">
              <div style={{ fontSize: '1.4rem', fontWeight: '800' }}>{completedCount}</div>
              <div style={{ fontSize: '0.65rem', opacity: 0.85, letterSpacing: '1px', marginTop: '2px' }}>✅ DONE</div>
            </div>
            <div className="featured-sub-card">
              <div style={{ fontSize: '1.4rem', fontWeight: '800' }}>{todaysOrders.length - completedCount}</div>
              <div style={{ fontSize: '0.65rem', opacity: 0.85, letterSpacing: '1px', marginTop: '2px' }}>⏳ LEFT</div>
            </div>
          </div>

          {/* Today's Progress Bar */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', opacity: 0.9, marginBottom: '5px' }}>
              <span>Daily Progress</span>
              <span>{todaysOrders.length > 0 ? Math.round((completedCount / todaysOrders.length) * 100) : 0}%</span>
            </div>
            <div className="featured-progress-container">
              <div
                className="featured-progress-bar"
                style={{ width: `${todaysOrders.length > 0 ? (completedCount / todaysOrders.length) * 100 : 0}%` }}
              ></div>
            </div>
          </div>

          {nextOrderDate && (
            <div style={{ fontSize: '0.78rem', opacity: 0.9, background: 'rgba(255,255,255,0.12)', borderRadius: '8px', padding: '6px 12px', display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
              📅 Next: <strong>{nextOrderDate === todayStr ? 'Today' : new Date(nextOrderDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</strong>
              {nextOrderTime && <span>· ⏰ {formatTime(nextOrderTime)}</span>}
            </div>
          )}
        </div>
      </div>

      {/* STATS ROW */}
      <div className="stats-row mb-30">
        {/* All Upcoming */}
        <div className="stat-box clickable" onClick={() => setActiveTab('calendar')}>
          <span className="stat-label">ALL UPCOMING</span>
          <span className="stat-value amber" style={{ fontSize: '2.5rem' }}>{pendingOrders.length}</span>
          <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>orders still pending</p>
          <div style={{ position: 'absolute', bottom: '10px', right: '12px', fontSize: '1.4rem', opacity: 0.12 }}>🗂️</div>
        </div>

        {/* Bouquets Sold */}
        <div className="stat-box">
          <span className="stat-label">BOUQUETS SOLD THIS:</span>
          <div className="dual-stat">
            <div className="stat-mini">
              <span className="mini-label">Day</span>
              <span className="stat-value blue" style={{ fontSize: '1.6rem' }}>{bouquetsSoldToday}</span>
            </div>
            <div className="stat-mini">
              <span className="mini-label">Week</span>
              <span className="stat-value green" style={{ fontSize: '1.6rem' }}>{bouquetsSoldThisWeek}</span>
            </div>
            <div className="stat-mini">
              <span className="mini-label">Month</span>
              <span className="stat-value" style={{ fontSize: '1.6rem', color: 'var(--primary)' }}>{bouquetsSoldThisMonth}</span>
            </div>
          </div>
        </div>
      </div>

      {/* TIMELINE SECTION */}
      <div className="timeline-section card mt-20">
        <div className="timeline-header-container">
          <div className="timeline-title-row">
            <h3>📅 Today's Timeline</h3>
          </div>

          {/* Unified 4-Column Segmented Timeline Filter Bar */}
          <div className="timeline-segmented-grid">
            <button className={`timeline-seg-btn ${timelineFilter === 'All' ? 'active' : ''}`} onClick={() => setTimelineFilter('All')}>
              <span className="seg-btn-text">📋 All</span>
              <span className="seg-btn-count">{todaysOrders.length}</span>
            </button>
            <button className={`timeline-seg-btn prep ${timelineFilter === 'Preparing' ? 'active' : ''}`} onClick={() => setTimelineFilter('Preparing')}>
              <span className="seg-btn-text">🌸 To Make</span>
              <span className="seg-btn-count">{incomingCount + preparingCount}</span>
            </button>
            <button className={`timeline-seg-btn ready ${timelineFilter === 'Ready' ? 'active' : ''}`} onClick={() => setTimelineFilter('Ready')}>
              <span className="seg-btn-text">✨ Ready</span>
              <span className="seg-btn-count">{readyCount}</span>
            </button>
            <button className={`timeline-seg-btn done ${timelineFilter === 'Completed' ? 'active' : ''}`} onClick={() => setTimelineFilter('Completed')}>
              <span className="seg-btn-text">✅ Done</span>
              <span className="seg-btn-count">{completedCount}</span>
            </button>
          </div>
        </div>

        {filteredTimelineOrders.length === 0 ? (
          <div className="empty-timeline">
            <p>No orders matched this filter. ✨</p>
          </div>
        ) : (
          <div className="timeline-wrapper">
            {filteredTimelineOrders
              .sort((a, b) => {
                // 1. Compare Prep Time first
                const timeA = getPrepTime(a.preferredTime, a);
                const timeB = getPrepTime(b.preferredTime, b);
                const timeDiff = timeA.localeCompare(timeB);
                if (timeDiff !== 0) return timeDiff;

                // 2. Conflict slot: Primary Priority comes FIRST (top) before Secondary Priority (bottom)
                const isSecA = a.isSecondary ? 1 : 0;
                const isSecB = b.isSecondary ? 1 : 0;
                if (isSecA !== isSecB) return isSecA - isSecB;

                // 3. Same priority level: Sort by order placement time (createdAt or id)
                const createdA = a.createdAt || a.id || 0;
                const createdB = b.createdAt || b.id || 0;
                return String(createdA).localeCompare(String(createdB));
              })
              .map((o, idx) => {
                const urgent = isUrgentPrep(o);
                const isSecondary = o.isSecondary;

                return (
                  <div
                    key={idx}
                    className={`timeline-card ${o.status?.toLowerCase().replace(' ', '-') || 'pending'} ${urgent ? 'urgent-prep' : ''} ${isSecondary ? 'secondary-right-card' : ''}`}
                    onClick={() => setSelectedOrder(o)}
                    style={{ cursor: 'pointer', zIndex: activeStatusMenu === o.id ? 100 : 1 }}
                  >
                    <div className="timeline-dot"></div>

                    <div className="timeline-time-col">
                      <span className="time">{o.preferredTime ? formatTime(getPrepTime(o.preferredTime, o)) : 'Anytime'}</span>
                      <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', fontWeight: '600', marginTop: '2px' }}>START PREP</div>
                      {urgent && (
                        <div className="urgent-badge-pulse">⚡ PREP NOW!</div>
                      )}

                      {/* Status Selector Badge Anchored in Left Time Column */}
                      <div className="status-selector-container" style={{ width: '100%', marginTop: '6px' }} onClick={e => e.stopPropagation()}>
                        <button
                          className={`status-badge-mini ${o.status?.toLowerCase().replace(' ', '-') || 'pending'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveStatusMenu(activeStatusMenu === o.id ? null : o.id);
                          }}
                          style={{ border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px', width: '100%', fontSize: '0.68rem', padding: '3px 4px' }}
                        >
                          {o.status === 'Completed' ? '✓ Done' : o.status === 'Incoming' ? '📥 Incoming' : o.status === 'Preparing' ? '🌸 Prep' : o.status === 'Bouquet Done' ? '✨ Ready' : (o.status || 'Incoming')}
                          <span style={{ fontSize: '0.5rem', opacity: 0.7 }}>▼</span>
                        </button>

                        {activeStatusMenu === o.id && (
                          <div className="status-menu-popup fade-in" style={{ width: '180px', left: 0, right: 'auto' }}>
                            <div className="status-menu-top-row">
                              {[
                                { id: 'Incoming', label: 'Incoming', icon: '📥', class: 'incoming' },
                                { id: 'Preparing', label: 'Preparing', icon: '🌸', class: 'preparing' },
                                { id: 'Bouquet Done', label: 'Ready', icon: '✨', class: 'ready' }
                              ].map((s) => (
                                <button
                                  key={s.id}
                                  className={`status-menu-item ${s.class} ${(o.status || 'Incoming') === s.id ? 'active' : ''}`}
                                  onClick={() => {
                                    setActiveStatusMenu(null);
                                    if (s.id === o.status) return;
                                    updateOrder(o.id, { status: s.id });
                                  }}
                                >
                                  <span className="item-icon">{s.icon}</span>
                                </button>
                              ))}
                            </div>
                            <button
                              className={`status-menu-item completed ${o.status === 'Completed' ? 'active' : ''}`}
                              onClick={() => {
                                setActiveStatusMenu(null);
                                if (o.status === 'Completed') return;
                                completeOrder(o.id);
                              }}
                            >
                              <span className="item-icon">✅</span>
                              <span className="item-text">Complete</span>
                            </button>
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', borderTop: '1.5px dashed rgba(244,114,182,0.15)', marginTop: '4px', paddingTop: '4px' }}>
                              {[
                                { id: 'Canceled', label: 'Canceled', icon: '❌' },
                                { id: 'Did Not Reply', label: 'Did Not Reply', icon: '🔇' },
                                { id: 'Did Not Get', label: 'Did Not Get', icon: '🚫' }
                              ].map((s) => (
                                <button
                                  key={s.id}
                                  className={`status-menu-item-text-only ${o.status === s.id ? 'active' : ''}`}
                                  onClick={() => {
                                    setActiveStatusMenu(null);
                                    if (o.status === s.id) return;
                                    updateOrder(o.id, { status: s.id });
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    background: 'transparent',
                                    border: 'none',
                                    width: '100%',
                                    padding: '6px 12px',
                                    cursor: 'pointer',
                                    fontSize: '0.75rem',
                                    fontWeight: '700',
                                    color: o.status === s.id ? 'var(--primary)' : '#64748b',
                                    transition: 'all 0.2s',
                                    borderRadius: '8px',
                                    textAlign: 'left'
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = 'var(--primary)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = o.status === s.id ? 'var(--primary)' : '#64748b'; }}
                                >
                                  <span>{s.icon}</span>
                                  <span>{s.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="timeline-info-col">
                      <div className="customer-name" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <span className="customer-name-text">{o.customer}</span>
                          {o.username && <span className="timeline-username">@{o.username}</span>}
                          {isSecondary && (
                            <span className="secondary-right-tag">🔸 Secondary</span>
                          )}
                        </div>
                        <span className="timeline-delivery-time">📍 Deliver: {o.preferredTime ? formatTime(o.preferredTime) : 'Anytime'}</span>
                      </div>

                      {/* Bouquet Items Summary & Mini Thumbnails */}
                      <div className="items-summary" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span>💐 {o.items?.map(it => typeof it === 'string' ? it : it.name).join(', ')}</span>
                        {o.items && o.items.length > 0 && (
                          <div className="timeline-mini-thumbs-container">
                            {o.items.map((it, i) => {
                              const name = typeof it === 'string' ? it : it.name;
                              const imgSrc = getItemPicture(it);
                              return imgSrc ? (
                                <img
                                  key={i}
                                  src={imgSrc}
                                  alt={name}
                                  className="timeline-card-thumb"
                                  style={{ cursor: 'pointer' }}
                                  onClick={(e) => { e.stopPropagation(); setFullscreenImage(imgSrc); }}
                                />
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>

                      {/* Footer Details: Area, Method Badge, Balance, Compact 1-Click Fee Payer Pill */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px', flexWrap: 'wrap', gap: '6px' }}>
                        <div className="area-label">📍 {o.area}</div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span className="timeline-badge-pill method">
                            {o.deliveryMethod === 'Home Pick-up' ? '🏠 Pick-up' : o.deliveryMethod === 'Meet-up' ? '🤝 Meet-up' : '🛵 Lalamove'}
                          </span>

                          {/* Single Lalamove Fee Payer Pill with 3-choice popup menu (Sender, Recipient, Pending) */}
                          {o.deliveryMethod === 'Lalamove' && o.status !== 'Completed' && (
                            <div style={{ position: 'relative', display: 'inline-block' }} onClick={e => e.stopPropagation()}>
                              <span
                                className={`timeline-badge-pill fee-payer ${o.deliveryPayer === 'Paid by Sender' ? 'sender' : o.deliveryPayer === 'Paid by Recipient' ? 'recipient' : 'pending'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveFeePayerMenu(activeFeePayerMenu === o.id ? null : o.id);
                                }}
                                title="Click to choose who pays for Lalamove delivery"
                                style={{ cursor: 'pointer' }}
                              >
                                {o.deliveryPayer === 'Paid by Sender' ? '💳 Sender Pays Lalamove' : o.deliveryPayer === 'Paid by Recipient' ? '🤝 Recipient Pays Lalamove' : '⚠️ Lalamove: Payer Pending'}
                              </span>

                              {activeFeePayerMenu === o.id && (
                                <div
                                  className="status-menu-popup fade-in"
                                  style={{
                                    width: '210px',
                                    right: 0,
                                    left: 'auto',
                                    bottom: '100%',
                                    top: 'auto',
                                    marginBottom: '6px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px',
                                    padding: '8px'
                                  }}
                                  onClick={e => e.stopPropagation()}
                                >
                                  <div style={{ fontSize: '0.68rem', fontWeight: '800', color: 'var(--text-muted)', marginBottom: '4px', paddingLeft: '4px', textTransform: 'uppercase' }}>
                                    🛵 Lalamove Fee Payer
                                  </div>
                                  {[
                                    { id: 'Paid by Sender', label: '💳 Paid by Sender', class: 'sender' },
                                    { id: 'Paid by Recipient', label: '🤝 Paid by Recipient', class: 'recipient' },
                                    { id: '', label: '⚠️ Payer Pending (TBD)', class: 'pending' }
                                  ].map((opt) => (
                                    <button
                                      key={opt.id}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '8px 12px',
                                        borderRadius: '10px',
                                        border: 'none',
                                        background: (o.deliveryPayer || '') === opt.id ? 'rgba(255, 107, 139, 0.12)' : 'transparent',
                                        fontWeight: (o.deliveryPayer || '') === opt.id ? '800' : '600',
                                        fontSize: '0.8rem',
                                        color: 'var(--text)',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        width: '100%'
                                      }}
                                      onClick={() => {
                                        setActiveFeePayerMenu(null);
                                        const label = opt.id === 'Paid by Sender' ? 'Paid by Sender' : opt.id === 'Paid by Recipient' ? 'Paid by Recipient' : 'Pending (Unassigned)';
                                        triggerConfirm({
                                          title: '🛵 Lalamove Fee Payer',
                                          message: `Set Lalamove delivery fee payer for ${o.customer} to: ${label}?`,
                                          type: 'primary',
                                          confirmText: 'Confirm',
                                          onConfirm: () => updateOrder(o.id, { deliveryPayer: opt.id })
                                        });
                                      }}
                                    >
                                      {opt.label}
                                      {(o.deliveryPayer || '') === opt.id && <span style={{ marginLeft: 'auto', fontSize: '0.75rem' }}>✓</span>}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {o.balance > 0 ? (
                            <span
                              className="timeline-badge-pill balance"
                              style={{ cursor: 'pointer' }}
                              title="Click to record payment"
                              onClick={(e) => {
                                e.stopPropagation();
                                openPaymentModal(o);
                              }}
                            >
                              ⚠️ Bal: ₱{(parseFloat(o.balance) || 0).toFixed(0)}
                            </span>
                          ) : (
                            <span
                              className="timeline-badge-pill paid"
                              style={{ cursor: 'pointer' }}
                              title="Click to revert / restore balance if not fully paid"
                              onClick={(e) => {
                                e.stopPropagation();
                                const prevBal = parseFloat(o.previousBalance) > 0
                                  ? parseFloat(o.previousBalance)
                                  : (parseFloat(o.total) || 0);
                                triggerConfirm({
                                  title: 'Revert Fully Paid Status?',
                                  message: `Mark ${o.customer} as unpaid and restore their remaining balance of ₱${prevBal.toFixed(0)}?`,
                                  type: 'danger',
                                  confirmText: 'Yes, Revert Balance',
                                  onConfirm: async () => {
                                    await updateOrder(o.id, {
                                      balance: prevBal,
                                      amountReceived: money(money(o.total) - prevBal),
                                      payment: o.previousPayment && o.previousPayment !== 'Fully Paid' ? o.previousPayment : 'Downpayment',
                                      previousBalance: 0
                                    });
                                    triggerToast(`↩️ Balance restored: ₱${prevBal.toFixed(0)} for ${o.customer}`);
                                  }
                                });
                              }}
                            >
                              ✅ Fully Paid
                            </span>
                          )}
                        </div>
                      </div>

                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* ✏️ Quick Copy Messages Widget */}
      <QuickMessages triggerToast={triggerToast} />

      {/* Consolidated Order Detail Modal */}
      <OrderDetailModal
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        viewPreference={viewPreference}
        setFullscreenImage={setFullscreenImage}
        setReschedulingOrder={setReschedulingOrder}
        updateOrder={updateOrder}
        completeOrder={completeOrder}
        triggerConfirm={triggerConfirm}
        triggerToast={triggerToast}
        setEditingOrder={setEditingOrder}
        setActiveTab={setActiveTab}
        openPaymentModal={openPaymentModal}
      />
    </div>
  );
}

// --- Quick Messages Component ---
function QuickMessages({ triggerToast }) {
  const STORAGE_KEY = 'solyminx_quick_messages';
  const defaultMessages = [
    {
      id: 1,
      label: 'Order Form',
      text: `🌷 FLOWER ORDER FORM 🌷

Thank you for choosing Solyminx Flowers! Please fill out the details below:
Customer Name (Receiver):
Contact Number (Receiver):
Contact Number (customer):
Delivery Address / Meet-up Location:
Google Map Link:
Date of Delivery / Pick-up:
Preferred Time:
Message for Card:

💰 MODE OF PAYMENT
All payments are accepted ONLY via:
• GCash
• Bank Transfer

NOTE: We do not accept cash, COD for DELIVERY. 

A 50% downpayment is required to secure your order. The remaining 50% balance must be fully paid before delivery or upon pickup.

📌 ORDER & RESERVATION POLICY
• A 50% downpayment is required to secure your order.
• Reservation/booking is only confirmed upon receipt of the required downpayment.
• Prices and availability may change for unpaid reservations.
• No downpayment = No reservation.
• Downpayments are non-refundable once flowers and materials have been purchased or prepared.
• Orders without a confirmed downpayment may be declined, adjusted, or repriced without prior notice.

💳 GCash Payment Details
GCash Number: 09953811294
Account Name: Shaina C.

🏦 Bank Transfer Details
Bank Name: UnionBank (UB)
Account Name: SHAINA MAE DELA CRUZ
Account Number: 1093 3034 4022

Bank Name: GoTyme Bank
Account Name: SHAINA MAE DELA CRUZ
Account Number: 0134 5074 4434

Bank Name: BDO
Account Name: SHAINA MAE DELA CRUZ
Account Number: 0022 5057 5813

📸 Please send a screenshot of your payment as proof of transaction.

Total Amount: ₱
50% Downpayment: ₱
Remaining Balance: ₱

By placing an order, you acknowledge and agree to the payment, reservation, and cancellation policies stated above.

🌷 Thank you for trusting Solyminx Flowers. We look forward to creating something beautiful for you!`
    },
    { id: 2, label: 'Thank You Message', text: "Thank you for reaching out to Solyminx Flowers! 🌷\nWe've received your inquiry/order and our florist will assist you shortly. Please stay tuned! ❤️" },
    { id: 3, label: 'Order Confirmed', text: "🌸 Order Confirmed! 🌸\n\nHi! Your bouquet is officially on our preparation board and will be arranged with love and care. We'll update you once it's ready for dispatch/pickup! 🚐✨" },
    { id: 4, label: 'GCash & Bank Accounts', text: "💳 Solyminx Payment Details 💳\n\n📱 GCash: 09953811294 (Shaina C.)\n\n🏦 UnionBank: 1093 3034 4022 (SHAINA MAE DELA CRUZ)\n🏦 GoTyme: 0134 5074 4434 (SHAINA MAE DELA CRUZ)\n🏦 BDO: 0022 5057 5813 (SHAINA MAE DELA CRUZ)\n\n📸 Please send a screenshot of the payment receipt once done so we can secure your order schedule. Thank you! 🙏" },
  ];

  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : defaultMessages;
    } catch { return defaultMessages; }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [previewMsg, setPreviewMsg] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editText, setEditText] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newText, setNewText] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const save = (updated) => {
    setMessages(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const fallbackCopy = (text) => {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.opacity = '0';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    } catch (err) {
      console.error('Fallback copy failed', err);
    }
  };

  const handleCopy = (msg) => {
    const text = msg.text;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
    setCopiedId(msg.id);
    if (triggerToast) triggerToast('📋 Copied: ' + msg.label);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const startEdit = (msg) => {
    setEditingId(msg.id);
    setEditLabel(msg.label);
    setEditText(msg.text);
    setPreviewMsg(null);
  };

  const saveEdit = () => {
    if (!editLabel.trim() || !editText.trim()) return;
    save(messages.map(m => m.id === editingId ? { ...m, label: editLabel.trim(), text: editText.trim() } : m));
    setEditingId(null);
    if (triggerToast) triggerToast('💾 Template updated!');
  };

  const deleteMsg = (id) => {
    save(messages.filter(m => m.id !== id));
    if (previewMsg && previewMsg.id === id) setPreviewMsg(null);
    if (triggerToast) triggerToast('🗑️ Template deleted');
  };

  const addMsg = () => {
    if (!newLabel.trim() || !newText.trim()) return;
    save([...messages, { id: Date.now(), label: newLabel.trim(), text: newText.trim() }]);
    setNewLabel('');
    setNewText('');
    setIsAdding(false);
    if (triggerToast) triggerToast('✨ New template added!');
  };

  const getEmojiForLabel = (label = '') => {
    const l = label.toLowerCase();
    if (l.includes('form')) return '🌷';
    if (l.includes('thank')) return '💌';
    if (l.includes('confirm')) return '🌸';
    if (l.includes('bank') || l.includes('gcash') || l.includes('pay')) return '💳';
    return '📝';
  };

  return (
    <div style={{
      marginBottom: '20px',
      background: 'var(--card-bg)',
      borderRadius: '20px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
      overflow: 'hidden',
      border: '1.5px solid rgba(244,114,182,0.25)'
    }}>
      {/* Sleek Accordion Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          cursor: 'pointer',
          userSelect: 'none',
          background: 'linear-gradient(135deg, rgba(253,242,248,0.9), rgba(255,255,255,0.95))',
          borderBottom: isOpen ? '1px solid rgba(244,114,182,0.15)' : 'none'
        }}
        onClick={() => setIsOpen(v => !v)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '1.2rem' }}>💬</span>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontWeight: '800', fontSize: '0.92rem', color: 'var(--text)' }}>
                Quick Copy Messages
              </span>
              <span style={{
                fontSize: '0.68rem',
                fontWeight: '800',
                background: '#fce7f3',
                color: '#be185d',
                padding: '1px 7px',
                borderRadius: '10px'
              }}>
                {messages.length}
              </span>
            </div>
            <div style={{ fontSize: '0.72rem', color: '#be185d', fontWeight: '600' }}>
              1-tap copy para sa chat at order form
            </div>
          </div>
        </div>

        <span style={{
          fontSize: '0.78rem',
          fontWeight: '800',
          color: '#be185d',
          background: '#fff',
          padding: '4px 10px',
          borderRadius: '12px',
          border: '1px solid #fbcfe8'
        }}>
          {isOpen ? 'Hide ▲' : 'Open ▼'}
        </span>
      </div>

      {isOpen && (
        <div style={{ padding: '12px', background: 'var(--bg)' }}>
          {/* iOS-Style Compact Button Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '8px'
          }}>
            {messages.map(msg => {
              const isCopied = copiedId === msg.id;
              return (
                <div
                  key={msg.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: isCopied ? '#22c55e' : 'var(--card-bg)',
                    border: isCopied ? '1.5px solid #16a34a' : '1px solid var(--border)',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    boxShadow: isCopied ? '0 3px 12px rgba(34,197,94,0.3)' : '0 2px 6px rgba(0,0,0,0.03)',
                    transition: 'all 0.2s ease',
                    minHeight: '44px'
                  }}
                >
                  {/* Tap to copy main action */}
                  <button
                    onClick={() => handleCopy(msg)}
                    style={{
                      flex: 1,
                      padding: '8px 10px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      textAlign: 'left',
                      outline: 'none',
                      minWidth: 0
                    }}
                  >
                    <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>
                      {isCopied ? '✅' : getEmojiForLabel(msg.label)}
                    </span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{
                        fontWeight: '800',
                        fontSize: '0.8rem',
                        color: isCopied ? '#ffffff' : 'var(--text)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {isCopied ? 'Copied!' : msg.label}
                      </div>
                      <div style={{
                        fontSize: '0.65rem',
                        fontWeight: '700',
                        color: isCopied ? '#dcfce7' : '#db2777'
                      }}>
                        {isCopied ? 'Saved to clipboard' : 'Tap to copy'}
                      </div>
                    </div>
                  </button>

                  {/* Eye / Preview button */}
                  <button
                    onClick={() => setPreviewMsg(msg)}
                    title="Preview / Edit"
                    style={{
                      padding: '8px 8px',
                      background: 'none',
                      border: 'none',
                      borderLeft: isCopied ? '1px solid rgba(255,255,255,0.3)' : '1px solid var(--border)',
                      cursor: 'pointer',
                      fontSize: '0.78rem',
                      color: isCopied ? '#ffffff' : 'var(--text-muted)',
                      opacity: 0.8,
                      flexShrink: 0,
                      alignSelf: 'stretch',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    👁️
                  </button>
                </div>
              );
            })}
          </div>

          {/* Bottom Actions: Add new template button */}
          <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setIsAdding(true)}
              style={{
                flex: 1,
                padding: '8px',
                borderRadius: '12px',
                border: '1.5px dashed var(--primary)',
                background: 'rgba(244,114,182,0.06)',
                color: 'var(--primary)',
                fontWeight: '800',
                fontSize: '0.78rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <span>➕</span>
              <span>Add Custom Template</span>
            </button>
          </div>
        </div>
      )}

      {/* Add New Template Modal/Drawer */}
      {isAdding && (
        <div className="confirm-modal-overlay fade-in" style={{ zIndex: 10005 }}>
          <div className="confirm-modal-content slide-up" style={{ maxWidth: '420px', padding: '20px', borderRadius: '22px', textAlign: 'left' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '1.05rem', color: 'var(--primary)', fontWeight: '900' }}>
              ➕ Create Message Template
            </h3>
            <input
              value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
              placeholder="Title (e.g. Delivery Guidelines, Reminders)"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                marginBottom: '10px',
                padding: '10px 12px',
                borderRadius: '10px',
                border: '1.5px solid var(--primary)',
                fontSize: '0.85rem',
                fontWeight: '700',
                background: 'var(--bg)',
                color: 'var(--text)',
                outline: 'none'
              }}
            />
            <textarea
              value={newText}
              onChange={e => setNewText(e.target.value)}
              rows={6}
              placeholder="Type template message content here..."
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px 12px',
                borderRadius: '10px',
                border: '1.5px solid var(--border)',
                fontSize: '0.82rem',
                resize: 'vertical',
                background: 'var(--bg)',
                color: 'var(--text)',
                outline: 'none',
                fontFamily: 'inherit',
                lineHeight: '1.45'
              }}
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              <button
                onClick={addMsg}
                style={{
                  flex: 1,
                  padding: '11px',
                  background: 'var(--primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: '800',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                ✓ Save Template
              </button>
              <button
                onClick={() => { setIsAdding(false); setNewLabel(''); setNewText(''); }}
                style={{
                  padding: '11px 16px',
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  fontWeight: '700',
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  color: 'var(--text-muted)'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Modal */}
      {editingId && (
        <div className="confirm-modal-overlay fade-in" style={{ zIndex: 10005 }}>
          <div className="confirm-modal-content slide-up" style={{ maxWidth: '420px', padding: '20px', borderRadius: '22px', textAlign: 'left' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '1.05rem', color: 'var(--primary)', fontWeight: '900' }}>
              ✏️ Edit Template
            </h3>
            <input
              value={editLabel}
              onChange={e => setEditLabel(e.target.value)}
              placeholder="Template Title"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                marginBottom: '10px',
                padding: '10px 12px',
                borderRadius: '10px',
                border: '1.5px solid var(--primary)',
                fontSize: '0.85rem',
                fontWeight: '700',
                background: 'var(--bg)',
                color: 'var(--text)',
                outline: 'none'
              }}
            />
            <textarea
              value={editText}
              onChange={e => setEditText(e.target.value)}
              rows={7}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '10px 12px',
                borderRadius: '10px',
                border: '1.5px solid var(--border)',
                fontSize: '0.82rem',
                resize: 'vertical',
                background: 'var(--bg)',
                color: 'var(--text)',
                outline: 'none',
                fontFamily: 'inherit',
                lineHeight: '1.45'
              }}
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              <button
                onClick={saveEdit}
                style={{
                  flex: 1,
                  padding: '11px',
                  background: 'var(--primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: '800',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                💾 Save Changes
              </button>
              <button
                onClick={() => setEditingId(null)}
                style={{
                  padding: '11px 16px',
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  fontWeight: '700',
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  color: 'var(--text-muted)'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Full Message Modal */}
      {previewMsg && (
        <div className="confirm-modal-overlay fade-in" style={{ zIndex: 10004 }}>
          <div className="confirm-modal-content slide-up" style={{ maxWidth: '420px', padding: '20px', borderRadius: '24px', textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.3rem' }}>{getEmojiForLabel(previewMsg.label)}</span>
                <span style={{ fontWeight: '900', fontSize: '1rem', color: 'var(--text)' }}>
                  {previewMsg.label}
                </span>
              </div>
              <button
                onClick={() => setPreviewMsg(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                ✕
              </button>
            </div>

            <div style={{
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: '14px',
              padding: '12px',
              fontSize: '0.8rem',
              color: 'var(--text)',
              whiteSpace: 'pre-wrap',
              lineHeight: '1.5',
              maxHeight: '260px',
              overflowY: 'auto'
            }}>
              {previewMsg.text}
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              <button
                onClick={() => {
                  handleCopy(previewMsg);
                  setPreviewMsg(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'linear-gradient(135deg, #ec4899, #db2777)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '14px',
                  fontWeight: '800',
                  fontSize: '0.86rem',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(219,39,119,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <span>📋</span>
                <span>Copy to Clipboard</span>
              </button>
              <button
                onClick={() => startEdit(previewMsg)}
                style={{
                  padding: '12px 14px',
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '14px',
                  fontWeight: '700',
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  color: 'var(--text)'
                }}
              >
                ✏️ Edit
              </button>
              <button
                onClick={() => deleteMsg(previewMsg.id)}
                style={{
                  padding: '12px 14px',
                  background: '#fee2e2',
                  border: '1px solid #fca5a5',
                  borderRadius: '14px',
                  fontWeight: '700',
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  color: '#dc2626'
                }}
              >
                🗑️
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Enhanced Flower Market Inventory & Shopping List ---

// --- Schedule View (Calendar) ---
function ScheduleView({ orders, updateOrder, calendarNotes = {}, unavailableDates = {}, saveCalendarNote, saveUnavailableDate, triggerToast }) {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Fix: Use local date instead of UTC to avoid "wrong day" issues
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [filter, setFilter] = useState('All');
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [dayNote, setDayNote] = useState('');
  const [isUnavailable, setIsUnavailable] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);

  // Swipe handling
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEndHandler = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) changeMonth(1);
    if (distance < -minSwipeDistance) changeMonth(-1);
  };

  const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const days = [];
  const totalDays = daysInMonth(year, month);
  const startOffset = firstDayOfMonth(year, month);

  // Fill in blank days
  for (let i = 0; i < startOffset; i++) days.push(null);
  // Fill in actual days
  for (let i = 1; i <= totalDays; i++) days.push(i);

  const getOrdersForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return orders.filter(o => o.deliveryDate === dateStr && o.status !== 'Canceled' && o.status !== 'Did Not Reply' && o.status !== 'Did Not Get');
  };

  const selectedDayOrders = getOrdersForDay(parseInt(selectedDate.split('-')[2]));

  const handleDayClick = (day, dateStr) => {
    if (!day) return;
    setSelectedDate(dateStr);
    const existingNote = calendarNotes[dateStr];
    const existingUnavailable = !!unavailableDates[dateStr];
    const reasonText = unavailableDates[dateStr] || existingNote || '';
    setDayNote(reasonText);
    setIsUnavailable(existingUnavailable);
    setShowModal(true);
  };

  const changeMonth = (offset) => {
    const newDate = new Date(year, month + offset, 1);
    setCurrentDate(newDate);
  };

  return (
    <div className="fade-in schedule-container">
      <div className="schedule-header">
        <div className="schedule-header-top">
          <div className="calendar-title-group">
            <h2 className="calendar-main-title">Calendar</h2>
            <button
              className="today-shortcut-btn"
              onClick={() => setCurrentDate(new Date())}
            >
              TODAY
            </button>
          </div>
          <div className="month-nav">
            <button className="nav-arrow" onClick={() => changeMonth(-1)} title="Previous Month">‹</button>
            <h3>{monthNames[month]} {year}</h3>
            <button className="nav-arrow" onClick={() => changeMonth(1)} title="Next Month">›</button>
          </div>
        </div>
        <div className="filter-chips">
          {['All', 'Incoming', 'Preparing', 'Bouquet Done', 'Completed', 'Unpaid'].map(f => (
            <button
              key={f}
              className={`filter-chip ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
              style={{
                background: filter === f ? 'var(--primary)' : 'white',
                color: filter === f ? 'white' : 'var(--text-muted)',
                border: '1px solid ' + (filter === f ? 'var(--primary)' : 'rgba(0,0,0,0.05)'),
                padding: '6px 14px',
                borderRadius: '12px',
                fontSize: '0.8rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
            >
              {f === 'Unpaid' ? '💰 Unpaid' : f}
            </button>
          ))}
        </div>
      </div>

      <div
        className="calendar-grid"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEndHandler}
      >
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
          <div key={day} className="calendar-day-label">{day}</div>
        ))}
        {days.map((day, idx) => {
          const dayOrders = getOrdersForDay(day);
          const isToday = day === new Date().getDate() && month === new Date().getMonth() && year === new Date().getFullYear();
          const dateStr = day ? `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;
          const isSelected = selectedDate === dateStr;
          const dateUnavailableReason = dateStr ? unavailableDates[dateStr] : null;

          const hasMatching = filter === 'All' || dayOrders.some(o =>
            (filter === 'Incoming' && (o.status === 'Incoming' || o.status === 'Pending Orders')) ||
            (filter === 'Preparing' && o.status === 'Preparing') ||
            (filter === 'Bouquet Done' && o.status === 'Bouquet Done') ||
            (filter === 'Completed' && o.status === 'Completed') ||
            (filter === 'Unpaid' && o.balance > 0)
          );
          const isDimmedCell = filter !== 'All' && !hasMatching;

          return (
            <div
              key={idx}
              className={`calendar-cell ${!day ? 'empty' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isDimmedCell ? 'dimmed-cell' : ''} ${dateUnavailableReason ? 'unavailable-cell' : ''}`}
              onClick={() => handleDayClick(day, dateStr)}
            >
              {day && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', position: 'relative' }}>
                    <span className="day-number">{day}</span>
                    {isToday && <span className="today-label">TODAY</span>}
                    {dateUnavailableReason ? (
                      <>
                        <div className="calendar-full-note unavailable-note">
                          🚫 {dateUnavailableReason}
                        </div>
                        <span className="calendar-note-icon unavailable-icon">🚫</span>
                      </>
                    ) : calendarNotes[dateStr] ? (
                      <>
                        <div className="calendar-full-note">
                          📝 {calendarNotes[dateStr]}
                        </div>
                        <span className="calendar-note-icon">📝</span>
                      </>
                    ) : null}
                  </div>
                  {dayOrders.length > 0 && (
                    <div className="day-indicators">
                      <span className="day-count-badge" style={{
                        color: dayOrders.every(o => o.status === 'Completed') ? '#22c55e' : 'var(--primary)',
                        background: dayOrders.every(o => o.status === 'Completed') ? 'rgba(34,197,94,0.12)' : 'rgba(244,114,182,0.12)',
                      }}>
                        {dayOrders.filter(o => o.status !== 'Completed').length > 0
                          ? `${dayOrders.filter(o => o.status !== 'Completed').length}↑`
                          : `✓${dayOrders.length}`}
                      </span>
                      {dayOrders.some(o => o.balance > 0) && <span className="unpaid-alert">💰</span>}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>


      {showModal && (
        <div className="day-modal-overlay fade-in" onClick={() => setShowModal(false)}>
          <div className="day-modal-content slide-up" onClick={e => e.stopPropagation()}>
            <div className="day-modal-header">
              <h4>Orders for {new Date(selectedDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</h4>
              <button className="close-modal-btn" onClick={() => setShowModal(false)}>×</button>
            </div>

            <div className="day-modal-body">
              {/* Unavailable Toggle Box */}
              <div
                className="unavailable-toggle-box"
                style={{
                  background: isUnavailable ? 'rgba(239, 68, 68, 0.08)' : 'rgba(0, 0, 0, 0.02)',
                  border: '1.5px solid ' + (isUnavailable ? 'rgba(239, 68, 68, 0.35)' : 'var(--glass-border)'),
                  borderRadius: '14px',
                  padding: '12px 16px',
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  justify: 'space-between',
                  transition: 'all 0.25s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '1.3rem' }}>🚫</span>
                  <div>
                    <div style={{ fontSize: '0.88rem', fontWeight: '700', color: isUnavailable ? '#ef4444' : 'var(--text)' }}>
                      Mark as Not Available
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      Warns when adding orders on this date
                    </div>
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '700', color: isUnavailable ? '#ef4444' : 'var(--text-muted)' }}>
                  <input
                    type="checkbox"
                    checked={isUnavailable}
                    onChange={e => setIsUnavailable(e.target.checked)}
                    style={{ accentColor: '#ef4444', width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  {isUnavailable ? 'Blocked' : 'Available'}
                </label>
              </div>

              <div className="day-note-section mb-20" style={{ background: '#fff', borderRadius: '12px', padding: '15px', border: '1px solid var(--glass-border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text)' }}>
                    {isUnavailable ? '🚫 Not Available Reason / Note' : '📝 Day Notes'}
                  </label>
                  <button
                    onClick={async () => {
                      setIsSavingNote(true);
                      await saveCalendarNote(selectedDate, dayNote);
                      if (saveUnavailableDate) {
                        await saveUnavailableDate(selectedDate, dayNote, isUnavailable);
                      }
                      setIsSavingNote(false);
                      if (triggerToast) triggerToast(isUnavailable ? 'Date marked as Not Available 🚫' : 'Day note saved! 📝');
                    }}
                    style={{
                      background: isSavingNote ? '#ccc' : isUnavailable ? '#ef4444' : 'var(--primary)',
                      color: 'white',
                      border: 'none',
                      padding: '5px 14px',
                      borderRadius: '8px',
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      boxShadow: isUnavailable ? '0 2px 8px rgba(239, 68, 68, 0.25)' : 'none'
                    }}
                    disabled={isSavingNote}
                  >
                    {isSavingNote ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
                <textarea
                  value={dayNote}
                  onChange={e => setDayNote(e.target.value)}
                  placeholder={isUnavailable ? "e.g. Paalis ako (Cavite trip) / Ipapaship nlang sa tao sa bahay..." : "Add a reminder or note for this day..."}
                  style={{ width: '100%', minHeight: '65px', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem', resize: 'vertical' }}
                />
              </div>

              {selectedDayOrders.length === 0 ? (
                <p className="hint" style={{ fontSize: '1rem', color: 'var(--primary)', fontWeight: '600', textAlign: 'center', padding: '40px 20px' }}>✨ Open for Bookings! Perfect time to accept new orders.</p>
              ) : (
                <div className="scheduled-list">
                  {[...selectedDayOrders].sort((a, b) => (a.preferredTime || '99:99').localeCompare(b.preferredTime || '99:99')).map(o => {
                    const isExpanded = expandedOrderId === o.id;
                    const isDimmedCard = filter !== 'All' && !(
                      (filter === 'Incoming' && (o.status === 'Incoming' || o.status === 'Pending Orders')) ||
                      (filter === 'Preparing' && o.status === 'Preparing') ||
                      (filter === 'Bouquet Done' && o.status === 'Bouquet Done') ||
                      (filter === 'Completed' && o.status === 'Completed') ||
                      (filter === 'Unpaid' && o.balance > 0)
                    );

                    return (
                      <div key={o.id} className={`scheduled-item-card ${isDimmedCard ? 'dimmed-card' : ''}`} onClick={() => setExpandedOrderId(isExpanded ? null : o.id)}>
                        <div className="scheduled-item-header">
                          <div className="item-info">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              {o.preferredTime && (
                                <span style={{
                                  background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))',
                                  color: 'white',
                                  fontSize: '0.72rem',
                                  fontWeight: '700',
                                  padding: '2px 8px',
                                  borderRadius: '20px',
                                  letterSpacing: '0.3px',
                                  flexShrink: 0
                                }}>⏰ {formatTime(o.preferredTime)}</span>
                              )}
                              <span className="item-customer" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                {o.customer}
                                {o.username && <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600', background: '#f1f5f9', padding: '2px 8px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>@{o.username}</span>}
                                {o.isSecondary && (
                                  <span style={{ fontSize: '0.65rem', fontWeight: '800', color: '#b45309', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '20px', padding: '2px 8px' }}>
                                    🔸 Secondary
                                  </span>
                                )}
                              </span>
                              {o.platform && (
                                <span style={{
                                  fontSize: '0.72rem',
                                  fontWeight: '700',
                                  padding: '2px 7px',
                                  borderRadius: '20px',
                                  background: o.platform === 'TikTok' ? '#000' :
                                    o.platform === 'Instagram' ? '#e1306c' :
                                      o.platform === 'Walk-in' ? '#22c55e' :
                                        '#1877f2',
                                  color: 'white',
                                  flexShrink: 0
                                }}>
                                  {o.platform === 'Messenger' ? '💬' :
                                    o.platform === 'TikTok' ? '🎵' :
                                      o.platform === 'Instagram' ? '📸' :
                                        o.platform === 'Walk-in' ? '🚶' : '📱'} {o.platform}
                                </span>
                              )}
                            </div>
                            <span className="item-flowers">
                              💐 {o.items?.map(it => typeof it === 'string' ? it : it.name).join(', ')}
                            </span>
                          </div>
                          <div className="item-status" onClick={e => e.stopPropagation()}>
                            <span className={`status-pill ${o.status.toLowerCase().replace(/\s+/g, '-')}`} style={{ padding: '4px 8px', fontSize: '0.75rem' }}>
                              {o.status === 'Completed' ? '✓ Completed' : 'Pending'}
                            </span>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="scheduled-item-details fade-in">
                            <div className="detail-row"><span className="detail-label">Area:</span> {o.area} {o.landmark && <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>· {o.landmark}</span>}</div>
                            <div className="detail-row"><span className="detail-label">Contact:</span> {o.contact || 'N/A'}</div>
                            <div className="detail-row">
                              <span className="detail-label">Contacted via:</span>{' '}
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: '#f0f4ff',
                                color: '#3b5998',
                                padding: '2px 10px',
                                borderRadius: '20px',
                                fontWeight: '700',
                                fontSize: '0.85rem'
                              }}>
                                {o.platform === 'Facebook' ? '📘' :
                                  o.platform === 'Instagram' ? '📸' :
                                    o.platform === 'Walk-in' ? '🚶' :
                                      o.platform === 'Shopee' ? '🛍️' :
                                        o.platform === 'TikTok' ? '🎵' : '💬'}
                                {' '}{o.platform || 'Messenger'}
                              </span>
                            </div>
                            <div className="detail-row"><span className="detail-label">Preferred Time:</span> {o.preferredTime ? formatTime(o.preferredTime) : 'Anytime'}</div>
                            <div className="detail-row"><span className="detail-label">Balance:</span> ₱{parseFloat(o.balance).toFixed(2)} {o.balance > 0 && '⚠️'}</div>
                            <div className="detail-row"><span className="detail-label">Delivery Fee:</span> ₱{parseFloat(o.deliveryFee || 0).toFixed(2)}</div>
                            <div className="detail-row"><span className="detail-label">Placed:</span> {o.createdAt ? new Date(o.createdAt).toLocaleDateString() : 'N/A'}</div>
                            {o.addOns && o.addOns !== 'None' && <div className="detail-row"><span className="detail-label">Add-ons:</span> {o.addOns}</div>}
                            {o.message && <div className="detail-quote">"{o.message}"</div>}
                            {o.customDescription && <div className="detail-row"><span className="detail-label">🎨 Flowers:</span> {o.customDescription}</div>}
                            {o.notes && (
                              <div className="detail-row" style={{ marginTop: '5px', padding: '8px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a', color: '#92400e' }}>
                                <span className="detail-label">📝 Notes:</span> {o.notes}
                              </div>
                            )}
                            {(o.screenshots && o.screenshots.length > 0) ? (
                              <div className="detail-row mt-10" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                <span className="detail-label" style={{ width: 'auto', marginBottom: '8px' }}>Screenshots & References:</span>
                                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', maxWidth: '100%', paddingBottom: '8px' }}>
                                  {o.screenshots.map((url, idx) => (
                                    <img
                                      key={idx}
                                      src={url}
                                      alt={`Screenshot ${idx + 1}`}
                                      className="reference-preview"
                                      onClick={(e) => { e.stopPropagation(); setFullscreenImage({ images: o.screenshots, currentIndex: idx }); }}
                                      style={{ cursor: 'pointer', flexShrink: 0, height: '120px', width: 'auto', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                    />
                                  ))}
                                </div>
                              </div>
                            ) : o.referenceImage && (
                              <div className="detail-row mt-10" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                <span className="detail-label" style={{ width: 'auto' }}>Client Reference Image:</span>
                                <img
                                  src={o.referenceImage}
                                  alt="Reference"
                                  className="reference-preview"
                                  onClick={(e) => { e.stopPropagation(); setFullscreenImage(o.referenceImage); }}
                                  style={{ cursor: 'pointer' }}
                                />
                              </div>
                            )}

                            {/* Bouquet Items & Reference Photos Grid */}
                            {o.items && o.items.length > 0 && (
                              <div style={{ marginTop: '12px' }}>
                                <span className="detail-label" style={{ width: 'auto', marginBottom: '6px', display: 'block' }}>Bouquet Reference Photos:</span>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '10px' }}>
                                  {o.items.map((it, idx) => {
                                    const name = typeof it === 'string' ? it : it.name;
                                    const imgSrc = getItemPicture(it);

                                    return (
                                      <div key={idx} style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        background: '#fff',
                                        borderRadius: '10px',
                                        padding: '8px',
                                        border: '1px solid #f1f5f9',
                                        textAlign: 'center',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                      }}>
                                        {imgSrc ? (
                                          <img
                                            src={imgSrc}
                                            alt={name}
                                            style={{
                                              width: '90px',
                                              height: '90px',
                                              objectFit: 'cover',
                                              borderRadius: '6px',
                                              cursor: 'pointer',
                                              marginBottom: '6px',
                                              border: '1px solid #e2e8f0'
                                            }}
                                            onClick={(e) => { e.stopPropagation(); setFullscreenImage(imgSrc); }}
                                          />
                                        ) : (
                                          <div style={{
                                            width: '90px',
                                            height: '90px',
                                            background: '#f8fafc',
                                            borderRadius: '6px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#94a3b8',
                                            fontSize: '1.5rem',
                                            marginBottom: '6px',
                                            border: '1px dashed #e2e8f0'
                                          }}>
                                            🌸
                                          </div>
                                        )}
                                        <span style={{
                                          fontSize: '0.7rem',
                                          fontWeight: '700',
                                          color: 'var(--text)',
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                          lineHeight: '1.2'
                                        }}>{name}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Image Lightbox */}
      {fullscreenImage && (
        <LightboxViewer
          fullscreenImage={fullscreenImage}
          setFullscreenImage={setFullscreenImage}
        />
      )}
    </div>
  );
}

// --- Dashboard Section ---
function Dashboard({ orders, allOrders, updateOrder, completeOrder, deleteOrder, setActiveTab, setEditingOrder, triggerConfirm, openPaymentModal, viewPreference, setFullscreenImage, setReschedulingOrder, triggerToast, calendarNotes = {}, unavailableDates = {} }) {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeStatusMenu, setActiveStatusMenu] = useState(null);

  useEffect(() => {
    if (selectedOrder) {
      const latest = allOrders.find(o => o.id === selectedOrder.id);
      if (latest && JSON.stringify(latest) !== JSON.stringify(selectedOrder)) {
        setSelectedOrder(latest);
      }
    }
  }, [allOrders, selectedOrder]);

  useEffect(() => {
    const handleGlobalClick = () => setActiveStatusMenu(null);
    if (activeStatusMenu) {
      window.addEventListener('click', handleGlobalClick);
    }
    return () => window.removeEventListener('click', handleGlobalClick);
  }, [activeStatusMenu]);

  const sortedOrders = [...orders].sort((a, b) => {
    // [AI DEV NOTE]: deliveryDate can be 'TBD'. Guard against new Date('TBD') = Invalid Date.
    const parseDate = (d) => {
      if (!d || d === 'TBD') return new Date('9999-12-31');
      return new Date(d);
    };
    const dateA = parseDate(a.deliveryDate);
    const dateB = parseDate(b.deliveryDate);
    if (dateA - dateB !== 0) return dateA - dateB;

    // Secondary sort by preferredTime (24h format string)
    const timeA = a.preferredTime || "99:99"; // Put items without time at the end
    const timeB = b.preferredTime || "99:99";
    return timeA.localeCompare(timeB);
  });

  const filteredOrders = sortedOrders.filter(o => {
    const s = searchTerm.toLowerCase();
    const cleanSearch = searchTerm.trim().toLowerCase();
    const numericTerm = cleanSearch.replace(/[₱,\s]/g, '');
    const searchNum = parseFloat(numericTerm);
    const hasNumericSearch = numericTerm !== '' && !isNaN(searchNum);

    const matchesItemPrice = hasNumericSearch && (o.items || []).some(item => {
      const itemName = typeof item === 'string' ? item : (item.name || '');
      const rawPrice = typeof item === 'object' ? item.price : bouquetPrices[itemName];
      if (!rawPrice) return false;
      const numPrice = parseFloat(String(rawPrice).replace(/[^\d.]/g, ''));
      if (isNaN(numPrice)) return false;
      return Math.abs(numPrice - searchNum) < 0.01 || String(numPrice).includes(numericTerm);
    });

    const orderTotalNum = parseFloat(String(o.total || '').replace(/[^\d.]/g, ''));
    const matchesTotal = hasNumericSearch && !isNaN(orderTotalNum) && (
      Math.abs(orderTotalNum - searchNum) < 0.01 || String(orderTotalNum).includes(numericTerm)
    );

    return (o.customer && o.customer.toLowerCase().includes(s)) ||
      (o.username && o.username.toLowerCase().includes(s)) ||
      (o.area && o.area.toLowerCase().includes(s)) ||
      (o.items && o.items.some(i => (typeof i === 'string' ? i : (i.name || '')).toLowerCase().includes(s))) ||
      (o.landmark && o.landmark.toLowerCase().includes(s)) ||
      (o.contact && o.contact.toLowerCase().includes(s)) ||
      (o.deliveryDate && o.deliveryDate.includes(s)) ||
      matchesItemPrice ||
      matchesTotal;
  });

  const pendingCount = sortedOrders.length;

  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;

  return (
    <div className="fade-in">
      <div className="dashboard-header">
        <div>
          <h2>Active Orders</h2>
          <p className="subtitle">{pendingCount} orders in progress</p>
        </div>
        <button className="new-order-btn" onClick={() => setActiveTab('orders')}>+ New Order</button>
      </div>

      {/* Today's Unavailable Banner */}
      {unavailableDates[todayStr] && (
        <div className="home-today-unavailable mb-20" style={{
          background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
          borderLeft: '4px solid #ef4444',
          borderRadius: '16px',
          padding: '16px 20px',
          marginBottom: '20px',
          boxShadow: '0 4px 15px rgba(239, 68, 68, 0.15)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          animation: 'fadeIn 0.5s ease-out'
        }}>
          <span style={{ fontSize: '1.5rem', marginTop: '-2px' }}>🚫</span>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: '0 0 4px 0', color: '#dc2626', fontSize: '0.8rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Today Marked as Not Available</h4>
            <p style={{ margin: 0, color: '#991b1b', fontSize: '0.95rem', fontWeight: '600', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{unavailableDates[todayStr]}</p>
          </div>
        </div>
      )}

      {/* Today's Calendar Note Banner */}
      {calendarNotes[todayStr] && (
        <div className="home-today-note" style={{
          background: 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)',
          borderLeft: '4px solid #f472b6',
          borderRadius: '16px',
          padding: '16px 20px',
          marginBottom: '20px',
          boxShadow: '0 4px 15px rgba(244, 114, 182, 0.1)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          animation: 'fadeIn 0.5s ease-out'
        }}>
          <span style={{ fontSize: '1.5rem', marginTop: '-2px' }}>📝</span>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: '0 0 4px 0', color: '#be185d', fontSize: '0.8rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Today's Note</h4>
            <p style={{ margin: 0, color: '#831843', fontSize: '0.95rem', fontWeight: '500', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{calendarNotes[todayStr]}</p>
          </div>
        </div>
      )}

      <div className="search-bar-wrapper">
        <div className="search-input-container">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input-field"
            placeholder="Search by customer, bouquet, or price (e.g. 350)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search-btn" onClick={() => setSearchTerm('')}>×</button>
          )}
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="empty-state">
          <p>{searchTerm ? '🔍 No matches found for your search.' : '🌸 All caught up! No active orders.'}</p>
        </div>
      ) : (
        <>
          <div className="order-list">
            {filteredOrders.map((order, idx) => {
              const prevOrder = idx > 0 ? filteredOrders[idx - 1] : null;
              const showDateHeader = !prevOrder || prevOrder.deliveryDate !== order.deliveryDate;

              return (
                <React.Fragment key={order.id}>
                  {showDateHeader && (
                    <div className="date-group-header">
                      <div className="date-group-line"></div>
                      <div className="date-group-content">
                        <span className="date-icon">📅</span>
                        <span className="date-text">
                          {order.deliveryDate === 'TBD' ? 'Date To Be Decided (TBD)' :
                            order.deliveryDate === todayStr ? 'Today' :
                              order.deliveryDate === tomorrowStr ? 'Tomorrow' :
                                new Date(order.deliveryDate).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                        </span>
                        <span className="date-count">{filteredOrders.filter(o => o.deliveryDate === order.deliveryDate).length} orders</span>
                      </div>
                    </div>
                  )}
                  <div
                    className="order-card"
                    onClick={() => setSelectedOrder(order)}
                    style={{
                      cursor: 'pointer',
                      zIndex: activeStatusMenu === order.id ? 100 : 1
                    }}
                  >
                    <div className="order-header">
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <h3 style={{ margin: 0 }}>{order.customer}</h3>
                              {allOrders && allOrders.filter(o => o.customer === order.customer).length > 1 && (
                                <span className="returning-badge">✨ Returning</span>
                              )}
                            </div>
                            {order.username && <span className="card-username">@{order.username}</span>}
                            {order.isSecondary && (
                              <span style={{ fontSize: '0.67rem', fontWeight: '800', color: '#b45309', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '20px', padding: '2px 10px', marginTop: '4px', display: 'inline-flex', alignItems: 'center', gap: '4px', width: 'fit-content' }}>
                                🔸 Secondary Priority
                              </span>
                            )}
                          </div>
                          {order.payment === 'Fully Paid' ? (
                            <span
                              className="payment-badge-mini paid-mini"
                              style={{ padding: '2px 8px', fontSize: '0.65rem', cursor: 'pointer' }}
                              title="Click to revert / restore balance if not fully paid"
                              onClick={(e) => {
                                e.stopPropagation();
                                const prevBal = parseFloat(order.previousBalance) > 0
                                  ? parseFloat(order.previousBalance)
                                  : (parseFloat(order.total) || 0);
                                triggerConfirm({
                                  title: 'Revert Fully Paid Status?',
                                  message: `Mark ${order.customer} as unpaid and restore their remaining balance of ₱${prevBal.toFixed(0)}?`,
                                  type: 'danger',
                                  confirmText: 'Yes, Revert Balance',
                                  onConfirm: async () => {
                                    await updateOrder(order.id, {
                                      balance: prevBal,
                                      amountReceived: money(money(order.total) - prevBal),
                                      payment: order.previousPayment && order.previousPayment !== 'Fully Paid' ? order.previousPayment : 'Downpayment',
                                      previousBalance: 0
                                    });
                                    triggerToast(`↩️ Balance restored: ₱${prevBal.toFixed(0)} for ${order.customer}`);
                                  }
                                });
                              }}
                            >
                              ✅ Fully Paid
                            </span>
                          ) : (
                            <div className="payment-badges-group" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                              <span className="payment-badge-mini paid-mini" style={{ padding: '2px 8px', fontSize: '0.65rem' }}>💰 Paid: ₱{((parseFloat(order.total) || 0) - (parseFloat(order.balance) || 0)).toFixed(2)}</span>
                              {order.balance > 0 && (
                                <span
                                  className="payment-badge-mini balance-mini"
                                  style={{ padding: '2px 8px', fontSize: '0.65rem', cursor: 'pointer' }}
                                  title="Click to record payment"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openPaymentModal(order);
                                  }}
                                >
                                  ⚠️ Bal: ₱{(parseFloat(order.balance) || 0).toFixed(2)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <span className="order-area">📍 {order.area} {order.landmark && `(${order.landmark})`} · 📞 {order.contact || 'N/A'} · 📱 {order.platform || 'Messenger'}</span>
                      </div>
                      <div className="delivery-date-badge">
                        📅 {order.deliveryDate} {order.preferredTime && <span style={{ display: 'block', fontSize: '0.7rem', opacity: 0.9 }}>⏰ {formatTime(order.preferredTime)}</span>}
                      </div>
                    </div>
                    <div className="order-details">
                      {order.items?.map((item, idx) => {
                        const itemName = typeof item === 'string' ? item : item.name;
                        const itemPrice = typeof item === 'object' ? item.price : bouquetPrices[itemName];
                        return (
                          <React.Fragment key={idx}>
                            <span className="detail-chip">
                              💐 {itemName}
                              {itemPrice && <span style={{ opacity: 0.7, fontSize: '0.8em', marginLeft: '4px', fontWeight: '800' }}>₱{parseFloat(itemPrice).toFixed(0)}</span>}
                            </span>
                            {item.message && (
                              <div style={{ fontSize: '0.75rem', color: '#666', fontStyle: 'italic', marginLeft: '10px', marginBottom: '8px', borderLeft: '2px solid #eee', paddingLeft: '8px' }}>
                                💌 "{item.message}"
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                      {order.addOns && order.addOns !== 'None' && <span className="detail-chip">🎁 {order.addOns}</span>}
                      {parseFloat(order.deliveryFee) > 0 && <span className="detail-chip">🚚 Fee ₱{(parseFloat(order.deliveryFee) || 0).toFixed(2)}</span>}
                      {order.deliveryMethod === 'Lalamove' && order.status !== 'Completed' && (
                        <span
                          className={`detail-chip fee-payer ${order.deliveryPayer === 'Paid by Sender' ? 'sender' : order.deliveryPayer === 'Paid by Recipient' ? 'recipient' : 'pending'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const next = !order.deliveryPayer ? 'Paid by Sender' : order.deliveryPayer === 'Paid by Sender' ? 'Paid by Recipient' : '';
                            const label = next === 'Paid by Sender' ? 'Paid by Sender' : next === 'Paid by Recipient' ? 'Paid by Recipient' : 'Pending (Unassigned)';
                            triggerConfirm({
                              title: '🛵 Lalamove Fee Payer',
                              message: `Set Lalamove delivery fee payer for ${order.customer} to: ${label}?`,
                              type: 'primary',
                              confirmText: 'Confirm',
                              onConfirm: () => updateOrder(order.id, { deliveryPayer: next })
                            });
                          }}
                          style={{ cursor: 'pointer' }}
                          title="Click to change Lalamove delivery fee payer"
                        >
                          {order.deliveryPayer === 'Paid by Sender' ? '🛵 Lalamove: Sender' : order.deliveryPayer === 'Paid by Recipient' ? '🛵 Lalamove: Recipient' : '⚠️ Lalamove: Payer Pending'}
                        </span>
                      )}
                      <span className="detail-chip price">₱{(parseFloat(order.total) || 0).toFixed(2)}</span>
                    </div>
                    {order.message && (
                      <div className="card-letter-section">
                        <span className="letter-label">💌 Letter:</span>
                        <p className="letter-text">"{order.message}"</p>
                      </div>
                    )}

                    <div className="card-footer-actions">
                      <div className="status-selector-container">
                        <button
                          className={`status-selector-btn ${(order.status || 'Incoming').toLowerCase().replace(/ /g, '-')}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveStatusMenu(activeStatusMenu === order.id ? null : order.id);
                          }}
                        >
                          <span className="current-status-label">
                            {order.status === 'Bouquet Done' ? '✨ Bouquet Done' :
                              order.status === 'Completed' ? '✅ Completed' :
                                order.status === 'Preparing' ? '🌸 Preparing' :
                                  '📥 Incoming'}
                          </span>
                          <span className="dropdown-arrow">▼</span>
                        </button>

                        {activeStatusMenu === order.id && (
                          <div className="status-menu-popup fade-in" onClick={e => e.stopPropagation()}>
                            <div className="status-menu-top-row">
                              {[
                                { id: 'Incoming', label: 'Incoming', icon: '📥', class: 'incoming' },
                                { id: 'Preparing', label: 'Preparing', icon: '🌸', class: 'preparing' },
                                { id: 'Bouquet Done', label: 'Bouquet Done', icon: '✨', class: 'ready' }
                              ].map((s) => (
                                <button
                                  key={s.id}
                                  className={`status-menu-item ${s.class} ${(order.status || 'Incoming') === s.id ? 'active' : ''}`}
                                  onClick={() => {
                                    setActiveStatusMenu(null);
                                    const currentStatus = order.status || 'Incoming';
                                    if (s.id === currentStatus) return;

                                    triggerConfirm({
                                      title: `Change Status to ${s.label}?`,
                                      message: `Update order status to ${s.label}?`,
                                      type: 'default',
                                      confirmText: s.label,
                                      onConfirm: () => updateOrder(order.id, { status: s.id })
                                    });
                                  }}
                                >
                                  <span className="item-icon">{s.icon}</span>
                                  <span className="item-text">{s.label}</span>
                                </button>
                              ))}
                            </div>

                            <button
                              className={`status-menu-item completed ${order.status === 'Completed' ? 'active' : ''}`}
                              onClick={() => {
                                setActiveStatusMenu(null);
                                if (order.status === 'Completed') return;

                                triggerConfirm({
                                  title: 'Mark as Completed?',
                                  message: 'Move this order to the completed list?',
                                  type: 'success',
                                  confirmText: 'Complete',
                                  onConfirm: () => completeOrder(order.id)
                                });
                              }}
                            >
                              <span className="item-icon">✅</span>
                              <span className="item-text">Mark as Completed</span>
                            </button>
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', borderTop: '1.5px dashed rgba(244,114,182,0.15)', marginTop: '4px', paddingTop: '4px' }}>
                              {[
                                { id: 'Canceled', label: 'Canceled', icon: '❌' },
                                { id: 'Did Not Reply', label: 'Did Not Reply', icon: '🔇' },
                                { id: 'Did Not Get', label: 'Did Not Get', icon: '🚫' }
                              ].map((s) => (
                                <button
                                  key={s.id}
                                  className={`status-menu-item-text-only ${order.status === s.id ? 'active' : ''}`}
                                  onClick={() => {
                                    setActiveStatusMenu(null);
                                    if (order.status === s.id) return;

                                    triggerConfirm({
                                      title: `Change Status to ${s.label}?`,
                                      message: `Update order status to ${s.label}?`,
                                      type: 'warning',
                                      confirmText: s.label,
                                      onConfirm: () => updateOrder(order.id, { status: s.id })
                                    });
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    background: 'transparent',
                                    border: 'none',
                                    width: '100%',
                                    padding: '6px 12px',
                                    cursor: 'pointer',
                                    fontSize: '0.75rem',
                                    fontWeight: '700',
                                    color: order.status === s.id ? 'var(--primary)' : '#64748b',
                                    transition: 'all 0.2s',
                                    borderRadius: '8px',
                                    textAlign: 'left'
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = 'var(--primary)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = order.status === s.id ? 'var(--primary)' : '#64748b'; }}
                                >
                                  <span>{s.icon}</span>
                                  <span>{s.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="secondary-actions">
                        <button className="reschedule-btn-footer" onClick={(e) => { e.stopPropagation(); setReschedulingOrder(order); }}>📅 Reschedule</button>
                        <button className="edit-btn-footer" onClick={(e) => { e.stopPropagation(); setEditingOrder(order); setActiveTab('orders'); }}>✏️ Edit</button>
                        <button className="delete-btn-footer" onClick={(e) => {
                          e.stopPropagation();
                          triggerConfirm({
                            title: 'Delete Order?',
                            message: 'This action cannot be undone. Are you sure?',
                            type: 'danger',
                            onConfirm: () => deleteOrder(order.id)
                          });
                        }}>🗑️ Delete</button>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>


        </>
      )}
      {/* Consolidated Order Detail Modal */}
      <OrderDetailModal
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        viewPreference={viewPreference}
        setFullscreenImage={setFullscreenImage}
        setReschedulingOrder={setReschedulingOrder}
        updateOrder={updateOrder}
        completeOrder={completeOrder}
        triggerConfirm={triggerConfirm}
        triggerToast={triggerToast}
        setEditingOrder={setEditingOrder}
        setActiveTab={setActiveTab}
        openPaymentModal={openPaymentModal}
      />
    </div>
  );
}

const createDefaultForm = () => {
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return {
    customer: '',
    contact: '',
    username: '',
    area: '',
    landmark: '',
    deliveryMethod: 'Lalamove',
    deliveryPayer: '',
    platform: '',
    items: [{ name: '', price: '', message: '', details: '', isCustomized: false, referenceImage: null, referenceImagePreview: null, referenceImageFile: null }],
    addOns: '',
    payment: 'Downpayment',
    amountReceived: '',
    balance: '',
    total: '',
    message: '',
    deliveryDate: todayStr,
    deliveryFee: '',
    formScreenshots: []
  };
};

const AutoExpandingTextarea = ({ value, onChange, placeholder, className, style }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      style={{ ...style, overflow: 'hidden', resize: 'none' }}
      rows={1}
    />
  );
};

// --- Order Form ---
// --- Helper for Cloudinary Upload with Progress ---
const uploadToCloudinaryWithProgress = (file, onProgress) => {
  return new Promise((resolve, reject) => {
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "duplirojs";
    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "solyminx_preset";
    const url = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;

    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        if (onProgress) onProgress(e.loaded, e.total);
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        const response = JSON.parse(xhr.responseText);
        resolve(response.secure_url);
      } else {
        reject(new Error("Upload failed"));
      }
    };

    xhr.onerror = () => reject(new Error("Upload failed"));

    const fd = new FormData();
    fd.append("file", file);
    fd.append("upload_preset", uploadPreset);
    xhr.send(fd);
  });
};

function OrderForm({ onAdd, onUpdate, orders, setActiveTab, activeTab, editingOrder, setEditingOrder, setFullscreenImage, triggerConfirm, triggerToast, setGlobalUploadProgress, setGlobalUploadLabel, catalogBouquets = [], unavailableDates = {}, onSavingChanged }) {
  const [formData, setFormData] = useState(createDefaultForm());

  const uniqueCustomers = useMemo(() => {
    if (!orders) return {};
    const map = {};
    // Sort orders by date descending to get the latest info
    const sortedOrders = [...orders].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

    sortedOrders.forEach(o => {
      if (o.customer && !map[o.customer]) {
        const orderCount = orders.filter(ord => ord.customer && ord.customer.trim().toLowerCase() === o.customer.trim().toLowerCase()).length;
        map[o.customer] = {
          contact: o.contact || '',
          platform: o.platform || '',
          area: o.area || '',
          username: o.username || '',
          deliveryMethod: o.deliveryMethod || 'Lalamove',
          orderCount: orderCount
        };
      }
    });
    return map;
  }, [orders]);

  const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const getCustomerOrderNumber = () => {
    if (!formData.customer) return null;
    const name = formData.customer.trim().toLowerCase();

    // Find matching customer in a case-insensitive way
    const matchKey = Object.keys(uniqueCustomers).find(k => k.trim().toLowerCase() === name);
    if (!matchKey) return null;

    const customerData = uniqueCustomers[matchKey];
    let count = customerData.orderCount || 0;

    // Check if we are currently editing an order that originally belonged to this customer
    const isOriginalCustomer = editingOrder && editingOrder.customer && editingOrder.customer.trim().toLowerCase() === name;

    if (isOriginalCustomer) {
      return count;
    } else {
      return count + 1;
    }
  };

  const getReturningCustomerData = () => {
    if (!formData.customer) return null;
    const name = formData.customer.trim().toLowerCase();
    const matchKey = Object.keys(uniqueCustomers).find(k => k.trim().toLowerCase() === name);
    return matchKey ? uniqueCustomers[matchKey] : null;
  };

  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);

  useEffect(() => {
    if (editingOrder) {
      setFormData({
        ...editingOrder,
        amountReceived: money(money(editingOrder.total) - money(editingOrder.balance)).toString(),
        items: editingOrder.items?.map(item =>
          typeof item === 'string' ? { name: item, details: '', isCustomized: false, referenceImage: null, referenceImagePreview: null, referenceImageFile: null } : { ...item, isCustomized: item.isCustomized || false, referenceImagePreview: item.referenceImage || null, referenceImageFile: null }
        ) || [{ name: '', details: '', isCustomized: false, referenceImage: null, referenceImagePreview: null, referenceImageFile: null }],
        formScreenshots: (editingOrder.screenshots || (editingOrder.referenceImage ? [editingOrder.referenceImage] : [])).map(url => ({ file: null, url }))
      });
    } else {
      setFormData(createDefaultForm());
    }
  }, [editingOrder]);

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [focusedItemIdx, setFocusedItemIdx] = useState(null);
  const isSubmittingRef = useRef(false);
  const savedOrderIdRef = useRef(null);
  const uploadedFilesRef = useRef(new Map());
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    savedOrderIdRef.current = null;
    uploadedFilesRef.current.clear();
    setSaveError('');
  }, [editingOrder]);

  useEffect(() => {
    if (!isSubmitting) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isSubmitting]);

  const [magicPasteText, setMagicPasteText] = useState('');

  const handleMagicPaste = (e) => {
    e.preventDefault();
    const text = magicPasteText;
    if (!text) return;

    const parsed = { ...formData };

    // Clean up text up to any payment policies / bank details to prevent noise from leaking into fields
    const cleanText = text.split(/(?:💰|📌|💳|💵|💸|📸|🏦|NOTE:)?\s*(?:MODE\s+OF\s+PAYMENT|ORDER\s*(?:&|AND)\s*RESERVATION\s*POLICY|DOWNPAYMENT\s*POLICY|GCash\s*Payment\s*Details|Bank\s*Transfer\s*Details|GCash\s*Number|GCash|Payment\s*Details|All\s*payments\s*are\s*accepted|NOTE:\s*We\s*do\s*not\s*accept|By\s+placing\s+an\s+order|Thank\s+you\s+for\s+trusting|Total\s*Amount|50%\s*Downpayment|Remaining\s*Balance|Account\s*Name)/i)[0].trim();

    const extractField = (synonyms) => {
      for (const regex of synonyms) {
        const match = cleanText.match(regex);
        if (match) {
          let val = match[1].trim();

          // If the matched value contains a subsequent field label followed by a separator (like colon or dash), truncate it!
          const labelRegex = /(?:,\s*|\s+)(?:(?:Customer\s+)?Name(?:\s*\([^)]*\))?|Pangalan|Contact(?:\s*(?:Number|No|#))?(?:\s*\([^)]*\))?|Phone|Cp|Number|Address(?:\s*\/?\s*Meet-?up\s*Location)?|Loc|Location|Meet-?up\s*Location|Lugar|Google\s*Maps?\s*Link|Maps?\s*Link|Link|Date(?:\s+of\s+Delivery(?:\s*\/\s*Pick-?up)?)?|Petsa|Time|Oras|Preferred\s+Time|Message(?:\s+for\s+Card)?|Card|Sulat|Dedication|Notes|Add-ons)/gi;
          let labelMatch;
          let truncateIndex = -1;

          while ((labelMatch = labelRegex.exec(val)) !== null) {
            const index = labelMatch.index;
            const remaining = val.slice(index);
            const sepMatch = remaining.match(/^[^a-zA-Z0-9]*[a-zA-Z0-9]+[^a-zA-Z0-9]*[:=-]/);
            if (sepMatch) {
              truncateIndex = index;
              break;
            }
          }

          if (truncateIndex !== -1) {
            val = val.slice(0, truncateIndex).trim();
            val = val.replace(/[,;:\s-]+$/, '').trim();
          }

          // If the matched value is another label, it means this field was left blank and we skipped to the next label. Ignore it!
          if (val.match(/(?:Name|Contact|Number|Address|Location|Link|Google|Date|Pick-up|Pickup|Time|Message|Card|Notes|Add-ons|💰|📌|💳|🏦|💵)/i)) {
            if (val.length < 50 && val.includes(':')) {
              continue;
            }
          }

          // Clean up any leading/trailing formatting characters
          val = val.replace(/^[*:\s]+|[*:\s]+$/g, '').trim();

          // Clear out placeholders like "none", "n/a", "null"
          const lowerVal = val.toLowerCase();
          if (lowerVal === 'none' || lowerVal === 'n/a' || lowerVal === 'null') {
            return '';
          }

          if (val) return val;
        }
      }
      return null;
    };

    const formatDateISO = (dateObj) => {
      return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
    };

    const parseResilientDate = (dateVal) => {
      if (!dateVal) return null;
      const cleanVal = dateVal.toLowerCase().trim();
      const today = new Date();

      // Handle TBD or undecided dates
      if (cleanVal.includes('tbd') || cleanVal.includes('to be decided') || cleanVal.includes('deciding') || cleanVal.includes('decide') || cleanVal.includes('wala pa') || cleanVal.includes('pending')) {
        return 'TBD';
      }

      // Handle relative dates in English & Tagalog
      if (cleanVal.includes('today') || cleanVal.includes('ngayon')) {
        return formatDateISO(today);
      }
      if (cleanVal.includes('tomorrow') || cleanVal.includes('bukas')) {
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        return formatDateISO(tomorrow);
      }
      if (cleanVal.includes('next day') || cleanVal.includes('sunod na araw')) {
        const nextDay = new Date(today);
        nextDay.setDate(today.getDate() + 1);
        return formatDateISO(nextDay);
      }

      // Remove common weekday names and noise
      let cleanStr = dateVal
        .replace(/\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday|mon|tue|wed|thu|fri|sat|sun)\b/gi, '')
        .replace(/[^a-zA-Z0-9/,\s-]/g, '')
        .trim()
        .replace(/[,;\s-]+$/, '')
        .trim();

      // Try YYYY-MM-DD or YYYY/MM/DD
      const matchISO = cleanStr.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
      if (matchISO) {
        const year = parseInt(matchISO[1], 10);
        const m = parseInt(matchISO[2], 10);
        const d = parseInt(matchISO[3], 10);
        return `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }

      // Try custom slash/dash parsing (MM/DD/YYYY or DD/MM/YYYY)
      const matchSlash = cleanStr.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
      if (matchSlash) {
        let p1 = parseInt(matchSlash[1], 10);
        let p2 = parseInt(matchSlash[2], 10);
        let year = parseInt(matchSlash[3], 10);
        if (year < 100) year += 2000;

        if (p1 >= 1 && p1 <= 12 && p2 >= 1 && p2 <= 31) {
          return `${year}-${String(p1).padStart(2, '0')}-${String(p2).padStart(2, '0')}`;
        }
        if (p1 >= 1 && p1 <= 31 && p2 >= 1 && p2 <= 12) {
          return `${year}-${String(p2).padStart(2, '0')}-${String(p1).padStart(2, '0')}`;
        }
        if (p1 >= 1 && p1 <= 12) {
          return `${year}-${String(p1).padStart(2, '0')}-${String(p2).padStart(2, '0')}`;
        }
      }

      // Try 2-part numeric dates without year (e.g. 8/14, 8-14, 14/8, 14-8)
      const matchTwoPart = cleanStr.match(/^(\d{1,2})[/-](\d{1,2})$/);
      if (matchTwoPart) {
        let p1 = parseInt(matchTwoPart[1], 10);
        let p2 = parseInt(matchTwoPart[2], 10);
        let year = today.getFullYear();

        if (p1 >= 1 && p1 <= 12 && p2 >= 1 && p2 <= 31) {
          return `${year}-${String(p1).padStart(2, '0')}-${String(p2).padStart(2, '0')}`;
        }
        if (p1 >= 1 && p1 <= 31 && p2 >= 1 && p2 <= 12) {
          return `${year}-${String(p2).padStart(2, '0')}-${String(p1).padStart(2, '0')}`;
        }
        if (p1 >= 1 && p1 <= 12 && p2 >= 1 && p2 <= 31) {
          return `${year}-${String(p1).padStart(2, '0')}-${String(p2).padStart(2, '0')}`;
        }
      }

      // Try parsing word-based month like "August 14 2026" or "Aug 14" or "14 Aug"
      const months = {
        jan: 1, january: 1,
        feb: 2, february: 2,
        mar: 3, march: 3,
        apr: 4, april: 4,
        may: 5,
        jun: 6, june: 6,
        jul: 7, july: 7,
        aug: 8, august: 8,
        sep: 9, sept: 9, september: 9,
        oct: 10, october: 10,
        nov: 11, november: 11,
        dec: 12, december: 12
      };

      const monthPattern = /(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)/i;
      const mMatch = cleanStr.match(monthPattern);
      if (mMatch) {
        const monthNum = months[mMatch[1].toLowerCase()];
        const cleanStrNoMonth = cleanStr.replace(monthPattern, '').trim();
        const numbers = cleanStrNoMonth.match(/\d+/g);

        if (numbers && numbers.length > 0) {
          let dayNum = parseInt(numbers[0], 10);
          let yearNum = today.getFullYear();

          if (numbers.length > 1) {
            let secondNum = parseInt(numbers[1], 10);
            if (secondNum >= 2000 && secondNum <= 2100) {
              yearNum = secondNum;
            } else if (secondNum >= 24 && secondNum <= 99) {
              yearNum = 2000 + secondNum;
            }
          }

          if (dayNum >= 1 && dayNum <= 31) {
            return `${yearNum}-${String(monthNum).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
          }
        }
      }

      let d = new Date(cleanStr);
      if (!isNaN(d.getTime())) {
        return formatDateISO(d);
      }
      return null;
    };

    const parseResilientTime = (timeVal) => {
      if (!timeVal) return '';
      const cleanVal = timeVal.toLowerCase().trim();

      if (cleanVal.includes('anytime') || cleanVal.includes('kahit ano') || cleanVal.includes('flexi')) {
        return '';
      }

      let hrs = 0;
      let mins = 0;
      let isPM = cleanVal.includes('pm') || cleanVal.includes('p.m') || cleanVal.includes('afternoon') || cleanVal.includes('evening') || cleanVal.includes('hapon') || cleanVal.includes('gabi');
      let isAM = cleanVal.includes('am') || cleanVal.includes('a.m') || cleanVal.includes('morning') || cleanVal.includes('umaga');
      let isNN = cleanVal.includes('nn') || cleanVal.includes('noon') || cleanVal.includes('tanghali');

      const numMatch = cleanVal.match(/(\d{1,2})(?::(\d{2}))?/);
      if (numMatch) {
        hrs = parseInt(numMatch[1], 10);
        mins = numMatch[2] ? parseInt(numMatch[2], 10) : 0;

        if ((isPM || isNN) && hrs < 12) hrs += 12;
        if (isAM && hrs === 12) hrs = 0;
        if (isNN && hrs === 12) hrs = 12;

        return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
      }

      if (isPM) return '14:00';
      if (isAM) return '09:00';
      if (isNN) return '12:00';

      return '';
    };

    // --- Define Synonyms Arrays ---
    const nameSynonyms = [
      /(?:Customer\s+)?Name\s*(?:\([^)]*\))?\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /(?:Pangalan|Receiver|Recipient|Buyer|Umorder)\s*(?:\([^)]*\))?\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /👤\s*([\r\n]*[^\n]+)/i,
      /(?:^|\n)(?:Name|Customer|Pangalan)\s*(?:\([^)]*\))?\s*:\s*([\r\n]*[^\n]+)/i
    ];

    const receiverContactSynonyms = [
      /Contact\s*(?:Number|No|#)?\s*\(\s*(?:Receiver|Recipient)\s*\)\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /(?:Receiver|Recipient)\s*(?:Contact|Phone|Mobile|CP)\s*(?:Number|No|#)?\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i
    ];

    const customerContactSynonyms = [
      /Contact\s*(?:Number|No|#)?\s*\(\s*(?:Customer|Sender|Buyer)\s*\)\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /(?:Customer|Sender|Buyer)\s*(?:Contact|Phone|Mobile|CP)\s*(?:Number|No|#)?\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i
    ];

    const genericContactSynonyms = [
      /Contact\s*(?:Number|No|#)?(?:\s*\([^)]*\))?\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /(?:Phone|Mobile|Telephon?e?|Cellphone)\s*(?:Number|No|#)?(?:\s*\([^)]*\))?\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /(?:Numero|CP|CP\s*No)\s*(?:\([^)]*\))?\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /[📞📱☎️]\s*([\r\n]*[^\n]+)/,
      /(?:^|\n)(?:Contact|Phone|Cp)\s*(?:\([^)]*\))?\s*:\s*([\r\n]*[^\n]+)/i
    ];

    const normalizePhone = (numStr) => {
      if (!numStr) return '';
      let clean = numStr.replace(/[^0-9+]/g, '');
      if (clean.startsWith('+63')) clean = '0' + clean.slice(3);
      else if (clean.startsWith('63')) clean = '0' + clean.slice(2);
      else if (clean.length === 10 && clean.startsWith('9')) clean = '0' + clean;
      return clean;
    };

    const addressSynonyms = [
      /(?:Delivery Address\s*\/\s*Meet-?up Location|Delivery Address|Meet-?up Location|Pickup\s*\/\s*delivery|Delivery Address Location)\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /(?:Meet\s*[-]?\s*up|Meetup|Pickup|Pick\s*[-]?\s*up|Shipping)\s*(?:Location|Address)?\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /(?:Delivery\s+)?Address\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /(?:Location|Lugar|Deliver\s+to)\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /[📍🏠🚚🗺️]\s*([\r\n]*[^\n]+)/,
      /(?:^|\n)(?:Address|Location|Lugar)\s*:\s*([\r\n]*[^\n]+)/i
    ];

    const mapsSynonyms = [
      /(?:Google\s*Map(?:s)?\s*Link|Maps?\s*Link|Location\s*Link|Google\s*Map(?:s)?)\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /(https?:\/\/(?:maps\.)?(?:app\.)?goo\.gl\/[a-zA-Z0-9_ -]+)/i,
      /(https?:\/\/(?:www\.)?google\.com\/maps[^\s\n]+)/i
    ];

    const dateSynonyms = [
      /(?:Date\s+of\s+Delivery\s*\/\s*Pick-?up|Date\s+of\s+Delivery|Date\s+of\s+Pick-?up|Preferred\s+Date\s+of\s+Delivery)\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /(?:Delivery|Pickup|Pick-up|Pick\s+up)\s+Date\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /Preferred\s+(?:Date|Day)\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /(?:Date|Petsa|Araw|When)\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /[📅🗓️📆]\s*([\r\n]*[^\n]+)/,
      /(?:^|\n)(?:Date|Petsa)\s*:\s*([\r\n]*[^\n]+)/i
    ];

    const timeSynonyms = [
      /Preferred\s+Time\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /(?:Delivery|Pickup|Pick-up|Pick\s+up)\s+Time\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /(?:Time|Oras|Hour)\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /[⏰⏳🕒]\s*([\r\n]*[^\n]+)/,
      /(?:^|\n)(?:Time|Oras)\s*:\s*([\r\n]*[^\n]+)/i
    ];

    const messageSynonyms = [
      /(?:Message\s+for\s+Card|Message\s+on\s+Card|Card\s+Message|Dedication\s+for\s+Card|Card\s+Dedication)\s*[*_~-]*\s*[-*:=#\t]+\s*(.*)/is,
      /(?:Message|Dedication|Mensahe|Sulat|Card|Letter|Note)\s*[*_~-]*\s*[-*:=#\t]+\s*(.*)/is,
      /[💌✉️📝✏️]\s*(.*)/is
    ];

    const notesSynonyms = [
      /Notes?\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i,
      /Special\s*Requests?\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i
    ];

    const addOnsSynonyms = [
      /Add-ons?\s*[*_~-]*\s*[-*:=#\t]+\s*([\r\n]*[^\n]+)/i
    ];

    // --- Perform Extraction ---
    let name = extractField(nameSynonyms);
    if (name) parsed.customer = name;

    let recvContact = extractField(receiverContactSynonyms);
    let custContact = extractField(customerContactSynonyms);
    let genContact = (!recvContact && !custContact) ? extractField(genericContactSynonyms) : null;

    let primaryContact = normalizePhone(recvContact || genContact || custContact);
    let secondaryContact = (recvContact && custContact && normalizePhone(recvContact) !== normalizePhone(custContact)) ? normalizePhone(custContact) : '';

    if (primaryContact) {
      if (secondaryContact) {
        parsed.contact = `${primaryContact} (Receiver) / ${secondaryContact} (Customer)`;
      } else {
        parsed.contact = primaryContact;
      }
    }

    let address = extractField(addressSynonyms);
    if (address) {
      const addressLower = address.toLowerCase();

      // Check the VALUE itself (not the field label) for pickup/meetup keywords.
      const isPickup = addressLower.includes('pick up') || addressLower.includes('pickup') || addressLower.includes('pick-up');
      const looksLikeStreetAddress = /\b(blk?|block|lot|l\d|b\d|street|st\.|ave|avenue|road|rd|brgy|barangay|phase|subd|village|subdivision|purok)\b/i.test(addressLower);
      const isMeetup = !looksLikeStreetAddress && (
        addressLower.includes('meet-up') || addressLower.includes('meetup') || addressLower.includes('meet up') ||
        /\b(sm|robinsons|puregold|savemore|jollibee|mcdo|mcdonald|mercury|waltermart|starbucks|landers|market|plaza|mall|church|hospital|school|gate|terminal|station)\b/i.test(addressLower)
      );

      if (isPickup) {
        parsed.deliveryMethod = 'Home Pick-up';
        parsed.area = '';
      } else if (isMeetup) {
        parsed.deliveryMethod = 'Meet-up';
        parsed.area = address;
      } else {
        parsed.area = address;
        parsed.deliveryMethod = 'Lalamove';
      }
    }

    let mapsPart = extractField(mapsSynonyms);
    if (mapsPart) {
      parsed.landmark = mapsPart.trim();
    }

    let dateStr = extractField(dateSynonyms);
    if (dateStr) {
      const parsedDate = parseResilientDate(dateStr);
      if (parsedDate) parsed.deliveryDate = parsedDate;
    }

    let timeStr = extractField(timeSynonyms);
    if (timeStr) {
      const parsedTime = parseResilientTime(timeStr);
      if (parsedTime !== null) parsed.preferredTime = parsedTime;
    }

    let msgPart = extractField(messageSynonyms);
    if (msgPart) {
      // Split and throw away anything after Notes, Add-ons, Landmark, Maps, or other labels in the message
      const msgLines = msgPart.split(/(?:\n|^)\s*(?:Notes?|Add-ons?|Special\s*Requests?|Google\s*Maps?\s*Link|Maps?\s*Link|Maps?|Landmark|💰|📌|💳|🏦)\s*:/i);
      msgPart = msgLines[0].trim();

      if (msgPart.toLowerCase().includes('ako nalang') || msgPart.toLowerCase() === 'none' || msgPart.toLowerCase() === 'n/a' || msgPart === '') {
        parsed.message = '';
      } else {
        parsed.message = msgPart;
      }
    }

    let notesPart = extractField(notesSynonyms);
    if (notesPart) {
      parsed.notes = notesPart.trim();
    }

    let addOnsPart = extractField(addOnsSynonyms);
    if (addOnsPart) {
      parsed.addOns = addOnsPart.trim();
    }

    // --- Backup line-by-line parser for plain texts ---
    if (!name && !primaryContact && !address) {
      const lines = cleanText.split('\n').map(l => l.trim()).filter(l => l !== '');
      if (lines.length >= 4 && !cleanText.toLowerCase().includes('customer name') && !cleanText.toLowerCase().includes('pangalan')) {
        parsed.customer = lines[0].replace(/^[*:\s]+|[*:\s]+$/g, '').trim();
        parsed.contact = lines[1].replace(/^[*:\s]+|[*:\s]+$/g, '').trim();

        const addrLine = lines[2].replace(/^[*:\s]+|[*:\s]+$/g, '').trim();
        const addrLineLower = addrLine.toLowerCase();
        const addrIsPickup = addrLineLower.includes('pick up') || addrLineLower.includes('pickup') || addrLineLower.includes('pick-up');
        const addrLooksLikeStreet = /\b(blk?|block|lot|l\d|b\d|street|st\.|ave|avenue|road|rd|brgy|barangay|phase|subd|village|subdivision|purok)\b/i.test(addrLineLower);
        const addrIsMeetup = !addrLooksLikeStreet && (
          addrLineLower.includes('meet-up') || addrLineLower.includes('meetup') || addrLineLower.includes('meet up') ||
          /\b(sm|robinsons|puregold|savemore|jollibee|mcdo|mcdonald|mercury|waltermart|starbucks|landers|market|plaza|mall|church|hospital|school|gate|terminal|station)\b/i.test(addrLineLower)
        );
        if (addrIsPickup) {
          parsed.deliveryMethod = 'Home Pick-up';
          parsed.area = '';
        } else if (addrIsMeetup) {
          parsed.deliveryMethod = 'Meet-up';
          parsed.area = addrLine;
        } else {
          parsed.area = addrLine;
          parsed.deliveryMethod = 'Lalamove';
        }

        if (lines[3]) {
          const parsedDate = parseResilientDate(lines[3]);
          if (parsedDate) parsed.deliveryDate = parsedDate;
        }

        if (lines[4]) {
          const parsedTime = parseResilientTime(lines[4]);
          if (parsedTime) parsed.preferredTime = parsedTime;
        }

        // Line 5+ = card message
        if (lines[5]) {
          const msgLines = lines.slice(5).join(' ').trim();
          if (msgLines && !msgLines.toLowerCase().includes('ako nalang')) {
            parsed.message = msgLines;
          }
        }
      }
    }

    setFormData(parsed);
    setMagicPasteText('');
  };



  const allBouquets = useMemo(() => {
    const fallbackBouquets = [
      "Mallows Bouquet (1)", "Mallows Bouquet (2)", "Mallows Bouquet (3)",
      "Mochi Bouquet (1)", "Mochi Bouquet (2)", "Mochi Bouquet (3)", "Mochi Bouquet (4)", "Mochi Bouquet (5)", "Mochi Bouquet (6)", "Mochi Bouquet (7)", "Mochi Bouquet (8)",
      "Munchkin Bouquet (1)", "Munchkin Bouquet (2)", "Munchkin Bouquet (3)", "Munchkin Bouquet (4)", "Munchkin Bouquet (5)", "Munchkin Bouquet (6)", "Munchkin Bouquet (7)", "Munchkin Bouquet (8)", "Munchkin Bouquet (9)",
      "Nala Bouquet", "Nala Bouquet (2)",
      "Nami Bouquet (1)", "Nami Bouquet (2)", "Nami Bouquet (3)", "Nami Bouquet (4)", "Nami Bouquet (5)", "Nami Bouquet (6)", "Nami Bouquet (7)", "Nami Bouquet (8)", "Nami Bouquet (9)",
      "Soft Thumbelina Bouquet (1)", "Soft Thumbelina Bouquet (2)", "Soft Thumbelina Bouquet (3)", "Soft Thumbelina Bouquet (4)",
      "Spring Thumbelina Bouquet (1)",
      "Veto (1)", "Veto (2)", "Veto (3)", "Veto (4)",
      "Polly Bouquet (1)",
      "Solace Bouquet (1)", "Solace Bouquet (2)", "Solace Bouquet (3)", "Solace Bouquet (4)", "Solace Bouquet (5)",
      "Matcha Bouquet (10 pcs)", "Matcha Bouquet (15 pcs)", "Matcha Bouquet (20 pcs)", "Matcha Bouquet (25 pcs)", "Matcha Bouquet (30 pcs)"
    ];
    if (!catalogBouquets || catalogBouquets.length === 0) return fallbackBouquets;
    const nameCounts = {};
    return catalogBouquets.map(b => {
      const base = b.name || '';
      if (/\d/.test(base)) return base;
      const key = base.replace(' Bouquet', '').toLowerCase().trim();
      if (!nameCounts[key]) nameCounts[key] = 0;
      nameCounts[key]++;
      const num = b.number || nameCounts[key];
      return `${base} (${num})`;
    }).filter(Boolean);
  }, [catalogBouquets]);

  const getSuggestions = (input) => {
    if (!input) return [];
    const cleanInput = input.trim().toLowerCase();
    const exactMatch = allBouquets.find(b => b.toLowerCase() === cleanInput);
    if (exactMatch) return [];

    const inputWords = cleanInput.split(/\s+/).filter(Boolean);
    const norm = (str) => str.toLowerCase().replace(/\s*bouquet\s*/gi, '').replace(/[^a-z0-9]/g, '');
    const normClean = norm(cleanInput);

    return allBouquets.filter(b => {
      const bLower = b.toLowerCase();
      if (bLower.includes(cleanInput)) return true;
      if (inputWords.length > 1 && inputWords.every(w => bLower.includes(w))) return true;
      if (normClean && norm(b).includes(normClean)) return true;
      return false;
    }).slice(0, 10);
  };

  // Given a label like "Soft Thumbelina Bouquet (2)" or "Soft Thumbelina 2", find the actual bouquet in the Firebase catalog
  const getCatalogEntryForLabel = (label) => {
    if (!label || !catalogBouquets || catalogBouquets.length === 0) return null;
    const cleanLabel = label.trim().toLowerCase();

    // 1. Direct exact match on b.name
    const exactMatch = catalogBouquets.find(b => (b.name || '').trim().toLowerCase() === cleanLabel);
    if (exactMatch) return exactMatch;

    // 2. Build computed labels identical to allBouquets mapping
    const nameCounts = {};
    const withComputed = catalogBouquets.map(b => {
      const base = (b.name || '').trim();
      const key = base.replace(/\s*bouquet\s*$/i, '').toLowerCase().trim();
      if (!nameCounts[key]) nameCounts[key] = 0;
      nameCounts[key]++;
      const num = b.number || nameCounts[key];
      const computedLabel = /\d/.test(base) ? base : `${base} (${num})`;
      return { bouquet: b, computedLabel, base, num: String(num) };
    });

    // 2a. Match computed label case-insensitively
    const labelMatch = withComputed.find(item => item.computedLabel.toLowerCase() === cleanLabel);
    if (labelMatch) return labelMatch.bouquet;

    // 2b. Normalized alphanumeric match (e.g. "soft thumbelina 2" -> "softthumbelina2" matching "Soft Thumbelina Bouquet (2)")
    const norm = (str) => str.toLowerCase().replace(/\s*bouquet\s*/gi, '').replace(/[^a-z0-9]/g, '');
    const normInput = norm(label);
    if (normInput) {
      const normMatch = withComputed.find(item => norm(item.computedLabel) === normInput);
      if (normMatch) return normMatch.bouquet;
    }

    // 2c. Base name + number pattern e.g. "Soft Thumbelina (2)" or "Soft Thumbelina 2"
    const match = label.match(/^(.+?)\s*\(?(\d+)\)?$/);
    if (match) {
      const [, basePart, numStr] = match;
      const cleanBase = basePart.replace(/\s*bouquet\s*$/i, '').trim().toLowerCase();
      const numMatch = withComputed.find(item => {
        const itemBase = item.base.replace(/\s*bouquet\s*$/i, '').trim().toLowerCase();
        return itemBase === cleanBase && (item.num === numStr || String(item.bouquet.number) === numStr);
      });
      if (numMatch) return numMatch.bouquet;
    }

    return null;
  };

  // Resolve bouquet price: ALWAYS prioritize live Firebase catalog first!
  const getBouquetPrice = (name) => {
    if (!name) return null;

    // 1. LIVE CATALOG PRICE TAKES TOP PRIORITY
    const catalogEntry = getCatalogEntryForLabel(name);
    if (catalogEntry) {
      const priceStr = (catalogEntry.salePrice && catalogEntry.salePrice.trim() !== '' && catalogEntry.salePrice.trim() !== '₱')
        ? catalogEntry.salePrice
        : catalogEntry.price;
      if (priceStr) {
        const rawPrice = parseFloat(String(priceStr).replace(/[^\d.]/g, ''));
        if (!isNaN(rawPrice)) return rawPrice;
      }
    }

    // 2. Fallback to hardcoded bouquetPrices only if not in live catalog or catalog price is unparseable
    if (bouquetPrices[name] !== undefined) {
      return bouquetPrices[name];
    }
    const norm = (str) => str.toLowerCase().replace(/\s*bouquet\s*/gi, '').replace(/[^a-z0-9]/g, '');
    const normTarget = norm(name);
    for (const [key, val] of Object.entries(bouquetPrices)) {
      if (norm(key) === normTarget) {
        return val;
      }
    }

    return null;
  };

  useEffect(() => {
    setFormData(prev => ({ ...prev, balance: calculateBalance(prev) }));
  }, [formData.payment, formData.total, formData.amountReceived]);


  const handleItemChange = (idx, field, value) => {
    const newItems = [...formData.items];
    if (typeof newItems[idx] === 'string') {
      newItems[idx] = { name: newItems[idx], price: '', message: '', details: '', isCustomized: false, referenceImage: null, referenceImagePreview: null, referenceImageFile: null };
    }

    let updatedItem = { ...newItems[idx], [field]: value };

    // Auto-fill price if name matches — check LIVE catalog first, then fallback to hardcoded
    if (field === 'name') {
      const entry = getCatalogEntryForLabel(value);
      if (entry) updatedItem.catalogId = entry.id;
      else delete updatedItem.catalogId;
      const livePrice = getBouquetPrice(value);
      if (livePrice !== null && livePrice !== undefined) {
        updatedItem.price = livePrice.toString();
      }
    }

    newItems[idx] = updatedItem;

    const newTotal = calculateOrderTotal(newItems, formData.deliveryFee);

    setFormData({
      ...formData,
      items: newItems,
      total: newTotal === 0 ? '' : newTotal.toString()
    });
  };

  const addItem = () => setFormData({ ...formData, items: [...formData.items, { name: '', price: '', message: '', details: '', isCustomized: false, referenceImage: null, referenceImagePreview: null, referenceImageFile: null }] });
  const removeItem = (idx) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== idx);
      const newTotal = calculateOrderTotal(newItems, formData.deliveryFee);
      setFormData(prev => ({
        ...prev,
        items: newItems,
        total: newTotal > 0 ? newTotal.toString() : ''
      }));
    }
  };

  const handleItemImageUpload = async (idx, e) => {
    const file = e.target.files[0];
    if (file) {
      setIsProcessingImage(true);
      try {
        const options = {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };
        const compressedFile = await imageCompression(file, options);
        const previewUrl = URL.createObjectURL(compressedFile);
        // Fix: update both fields in ONE atomic setFormData call.
        // Two consecutive handleItemChange calls each close over the same
        // stale formData snapshot, so the second call would silently discard
        // whatever the first call wrote, making the preview never appear.
        setFormData(prev => {
          const newItems = prev.items.map((item, i) => {
            if (i !== idx) return item;
            const base = typeof item === 'string'
              ? { name: item, price: '', message: '', details: '', isCustomized: false, referenceImage: null, referenceImagePreview: null, referenceImageFile: null }
              : { ...item };
            return { ...base, referenceImageFile: compressedFile, referenceImagePreview: previewUrl };
          });
          return { ...prev, items: newItems };
        });
      } catch (error) {
        console.error('Error compressing item image:', error);
      } finally {
        setIsProcessingImage(false);
      }
    }
  };

  const handleScreenshotsUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setIsProcessingImage(true);
      try {
        const newScreenshots = [];
        const options = {
          maxSizeMB: 5,
          maxWidthOrHeight: 2500,
          useWebWorker: true,
        };
        for (const file of files) {
          // SAFETY NET: If file is > 5MB, compress it. Otherwise keep raw to preserve tiny text.
          let finalFile = file;
          if (file.size > 5 * 1024 * 1024) {
            finalFile = await imageCompression(file, options);
          }
          const previewUrl = URL.createObjectURL(finalFile);
          newScreenshots.push({ file: finalFile, url: previewUrl });
        }

        setFormData(prev => ({
          ...prev,
          formScreenshots: [...(prev.formScreenshots || []), ...newScreenshots]
        }));
      } catch (error) {
        console.error('Error processing screenshots:', error);
      } finally {
        setIsProcessingImage(false);
      }
    }
  };

  const removeScreenshot = (index) => {
    setFormData(prev => {
      const newScreenshots = [...(prev.formScreenshots || [])];
      newScreenshots.splice(index, 1);
      return { ...prev, formScreenshots: newScreenshots };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmittingRef.current || submitted || isProcessingImage) return;

    // ── Inner doSubmit: performs the actual save (called directly OR via confirm callback) ──
    const doSubmit = async (extraFields = {}) => {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      setIsSubmitting(true);
      onSavingChanged?.(true);
      setSaveError('');
      setSubmitted(false);
      try {
        const { formScreenshots, id: ignoredId, isUploadingImage, ...orderData } = formData;
        const itemFiles = formData.items.map(item => item.referenceImageFile);
        const originalItemNames = new Set((editingOrder?.items || []).map(item => typeof item === 'string' ? item : item.name));
        orderData.items = formData.items.map(item => {
          const { referenceImageFile, referenceImagePreview, ...cleanItem } = item;
          if (referenceImageFile) cleanItem.referenceImage = uploadedFilesRef.current.get(referenceImageFile) || null;
          if (!(editingOrder && originalItemNames.has(cleanItem.name))) {
            delete cleanItem.catalogId;
            delete cleanItem.catalogImage;
            const entry = !cleanItem.isCustomized && cleanItem.name ? getCatalogEntryForLabel(cleanItem.name) : null;
            if (entry?.image) {
              cleanItem.catalogId = entry.id;
              cleanItem.catalogImage = entry.image;
            }
          }
          return cleanItem;
        });
        Object.assign(orderData, extraFields);
        if (!Number.isFinite(Number(orderData.total)) || Number(orderData.total) < 0 ||
            !Number.isFinite(Number(orderData.deliveryFee || 0)) || Number(orderData.deliveryFee || 0) < 0 ||
            money(orderData.total) < money(orderData.deliveryFee)) {
          throw new Error('Check the total and delivery fee. Total must include delivery.');
        }
        orderData.total = money(orderData.total);
        orderData.deliveryFee = money(orderData.deliveryFee);
        if (orderData.payment !== 'Fully Paid' && orderData.amountReceived !== '' && orderData.amountReceived != null &&
            (!Number.isFinite(Number(orderData.amountReceived)) || Number(orderData.amountReceived) < 0 ||
             money(orderData.amountReceived) > orderData.total)) {
          throw new Error('Amount received must be between zero and the grand total.');
        }
        orderData.balance = calculateBalance(orderData);
        orderData.amountReceived = money(orderData.total - orderData.balance);
        if (orderData.deliveryDate && orderData.deliveryDate !== 'TBD' &&
            orderData.preferredTime && orderData.preferredTime !== 'TBD') {
          const due = new Date(`${orderData.deliveryDate}T${orderData.preferredTime}`);
          if (Number.isNaN(due.getTime())) throw new Error('Check the delivery date and time.');
          orderData.dueTimestamp = due.toISOString();
        } else {
          orderData.dueTimestamp = null;
        }
        await saveOrderWithUploads({
          id: savedOrderIdRef.current || editingOrder?.id,
          data: orderData,
          screenshots: formScreenshots,
          itemFiles,
          cache: uploadedFilesRef.current,
          upload: uploadToCloudinaryWithProgress,
          onAdd,
          onUpdate,
          onSaved: docId => {
            savedOrderIdRef.current = docId;
            if (itemFiles.some(Boolean) || formScreenshots?.some(shot => shot.file)) {
              setGlobalUploadLabel('Order saved. Uploading photos…');
              setGlobalUploadProgress(2);
            }
          },
          onProgress: (finished, total) => {
            setGlobalUploadProgress(Math.round(finished / total * 95));
            setGlobalUploadLabel(`Uploading photos ${finished}/${total}…`);
          }
        });
        setSubmitted(true);
        setFormData(createDefaultForm());
        setMagicPasteText('');
        if (editingOrder) setEditingOrder(null);
        savedOrderIdRef.current = null;
        uploadedFilesRef.current.clear();
        triggerToast?.('✅ Order saved successfully!');
        setTimeout(() => {
          setSubmitted(false);
          setActiveTab('dashboard');
        }, 200);
      } catch (error) {
        const message = savedOrderIdRef.current
          ? 'Order details are saved, but the update/photos could not finish. Your input is kept. Save again to retry the SAME order.'
          : `Order not saved. Your input is kept. ${error.message || 'Please retry.'}`;
        setSaveError(message);
        triggerToast?.('❌ ' + message);
      } finally {
        setGlobalUploadProgress(null);
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        onSavingChanged?.(false);
      }
    };

    const checkTimeConflictAndSubmit = () => {
      // ── Conflict Check (per-bouquet: 30 min each). Skip if time is TBD or unset. ──
      if (formData.deliveryDate && formData.preferredTime && formData.preferredTime !== 'TBD') {
        const getMins = (timeStr) => {
          if (!timeStr) return -1;
          const [h, m] = timeStr.split(':').map(Number);
          return h * 60 + m;
        };

        const newMins = getMins(formData.preferredTime);
        const newBouquets = getBouquetCount({ items: formData.items });
        const newDuration = newBouquets * 30;
        const newEnd = newMins + newDuration;

        // Check if two time ranges overlap: [startA, endA) vs [startB, endB)
        const rangesOverlap = (startA, endA, startB, endB) => startA < endB && startB < endA;

        const conflict = orders.find(o => {
          if (o.id === editingOrder?.id || o.deliveryDate !== formData.deliveryDate || !occupiesSchedule(o) || !o.preferredTime || o.preferredTime === 'TBD') return false;
          const oMins = getMins(o.preferredTime);
          const oDuration = getOrderDuration(o);
          const oEnd = oMins + oDuration;
          return rangesOverlap(newMins, newEnd, oMins, oEnd);
        });

        if (conflict) {
          // Find nearest available slot
          let nearestMins = Math.round(newMins / 30) * 30;
          let nearestTime = '';
          let offset = 0;

          while (offset < 1440) {
            const check = (off) => {
              const testMins = nearestMins + off;
              if (testMins < 0 || testMins >= 1440) return null;
              const testEnd = testMins + newDuration;

              const hasConflict = orders.some(o => {
                if (o.id === editingOrder?.id || o.deliveryDate !== formData.deliveryDate || !occupiesSchedule(o) || !o.preferredTime || o.preferredTime === 'TBD') return false;
                const oMins = getMins(o.preferredTime);
                const oEnd = oMins + getOrderDuration(o);
                return rangesOverlap(testMins, testEnd, oMins, oEnd);
              });

              if (!hasConflict) {
                const h = Math.floor(testMins / 60);
                const m = testMins % 60;
                return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
              }
              return null;
            };

            if (offset === 0) {
              const exact = check(0);
              if (exact) { nearestTime = exact; break; }
            } else {
              const before = check(-offset);
              if (before) { nearestTime = before; break; }
              const after = check(offset);
              if (after) { nearestTime = after; break; }
            }
            offset += 30;
          }

          const format12h = (t) => {
            if (!t) return 'Anytime';
            const [hrs, mins] = t.split(':').map(Number);
            const period = hrs >= 12 ? 'PM' : 'AM';
            const h12 = hrs % 12 || 12;
            return `${h12}:${String(mins).padStart(2, '0')} ${period}`;
          };

          const conflictBouquets = getBouquetCount(conflict);

          if (triggerConfirm) {
            // Non-blocking: give admin the choice to add as Secondary Priority
            triggerConfirm({
              title: '⚠️ Time Conflict Detected',
              message: `${conflict.customer} already has ${conflictBouquets} bouquet${conflictBouquets > 1 ? 's' : ''} at ${format12h(conflict.preferredTime)} (takes ${conflictBouquets * 30} min).\n\nThis new order needs ${newDuration} min and overlaps that slot.\n\nNearest free slot: ${format12h(nearestTime)}\n\nYou can still add it as 🔸 Secondary Priority — visible to the whole team on all order views.`,
              type: 'warning',
              confirmText: '🔸 Add as Secondary',
              onConfirm: () => doSubmit({ isSecondary: true })
            });
          } else {
            // Fallback for environments without triggerConfirm
            if (window.confirm(`⚠️ TIME CONFLICT!\n\n${conflict.customer} has ${conflictBouquets} bouquet(s) at ${format12h(conflict.preferredTime)}.\n\nNearest free slot: ${format12h(nearestTime)}\n\nAdd as Secondary Priority anyway?`)) {
              doSubmit({ isSecondary: true });
            }
          }
          return; // Wait for confirm callback; doSubmit() will fire if user confirms
        }
      }

      // No conflict — proceed normally
      doSubmit();
    };

    // ── Unavailable Date Check ──
    const dateUnavailableReason = (formData.deliveryDate && formData.deliveryDate !== 'TBD') ? unavailableDates[formData.deliveryDate] : null;
    if (dateUnavailableReason) {
      if (triggerConfirm) {
        triggerConfirm({
          title: '🚫 Date Marked as Not Available',
          message: `Delivery date (${formData.deliveryDate}) is marked on calendar as Not Available:\n\n"${dateUnavailableReason}"\n\nDo you still want to proceed with saving this order?`,
          type: 'warning',
          confirmText: 'Yes, Save Order',
          onConfirm: () => checkTimeConflictAndSubmit()
        });
        return;
      }
    }

    checkTimeConflictAndSubmit();
  };

  return (
    <div className="fade-in">
      <h2>{editingOrder ? 'Edit Order' : 'New Order'}</h2>

      {!editingOrder && (
        <div className="card magic-paste-section mb-20" style={{ background: '#fdf4ff', border: '1px dashed #f472b6', padding: '18px', borderRadius: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
            <h4 style={{ color: '#be185d', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>✨ Magic Paste & Forms</h4>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const text = await navigator.clipboard.readText();
                    if (text) {
                      setMagicPasteText(text);
                      if (triggerToast) triggerToast('Pasted! Click Extract to auto-fill. ✨');
                    }
                  } catch (err) {
                    alert('Could not read clipboard. Please long-press and paste manually in the box.');
                  }
                }}
                className="copy-blank-form-btn magic-paste-btn"
                style={{
                  background: '#fdf4ff',
                  color: '#be185d',
                  border: '1px solid #fbcfe8',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(244, 114, 182, 0.1)'
                }}
              >
                📥 Paste
              </button>
              <button
                type="button"
                onClick={() => {
                  const blankForm = `🌷 FLOWER ORDER FORM 🌷

Thank you for choosing Solyminx Flowers! Please fill out the details below:
Customer Name (Receiver):
Contact Number (Receiver):
Contact Number (customer):
Delivery Address / Meet-up Location:
Google Map Link:
Date of Delivery / Pick-up:
Preferred Time:
Message for Card:

💰 MODE OF PAYMENT
All payments are accepted ONLY via:
• GCash
• Bank Transfer

NOTE: We do not accept cash, COD for DELIVERY. 

A 50% downpayment is required to secure your order. The remaining 50% balance must be fully paid before delivery or upon pickup.

📌 ORDER & RESERVATION POLICY
• A 50% downpayment is required to secure your order.
• Reservation/booking is only confirmed upon receipt of the required downpayment.
• Prices and availability may change for unpaid reservations.
• No downpayment = No reservation.
• Downpayments are non-refundable once flowers and materials have been purchased or prepared.
• Orders without a confirmed downpayment may be declined, adjusted, or repriced without prior notice.

💳 GCash Payment Details
GCash Number: 09953811294
Account Name: Shaina C.

🏦 Bank Transfer Details
Bank Name: UnionBank (UB)
Account Name: SHAINA MAE DELA CRUZ
Account Number: 1093 3034 4022

Bank Name: GoTyme Bank
Account Name: SHAINA MAE DELA CRUZ
Account Number: 0134 5074 4434

Bank Name: BDO
Account Name: SHAINA MAE DELA CRUZ
Account Number: 0022 5057 5813

📸 Please send a screenshot of your payment as proof of transaction.

Total Amount: ₱
50% Downpayment: ₱
Remaining Balance: ₱

By placing an order, you acknowledge and agree to the payment, reservation, and cancellation policies stated above.

🌷 Thank you for trusting Solyminx Flowers. We look forward to creating something beautiful for you!`;
                  navigator.clipboard.writeText(blankForm);
                  if (triggerToast) triggerToast('Blank Form Copied! 📋✨');
                }}
                className="copy-blank-form-btn magic-paste-btn"
                style={{
                  background: '#fdf4ff',
                  color: '#be185d',
                  border: '1px solid #fbcfe8',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(244, 114, 182, 0.1)'
                }}
              >
                📋 Copy Blank Form
              </button>
            </div>
          </div>
          <p style={{ fontSize: '0.85rem', color: '#831843', marginBottom: '12px' }}>
            Paste the customer's completed order form below and click <strong>Extract</strong> to instantly pre-fill all form fields!
          </p>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
            <textarea
              style={{ flex: 1, minHeight: '80px', padding: '12px', borderRadius: '10px', border: '1px solid #fbcfe8', outline: 'none', fontFamily: 'inherit', fontSize: '0.9rem', color: '#4a044e' }}
              placeholder="Paste the order form text here..."
              value={magicPasteText}
              onChange={(e) => setMagicPasteText(e.target.value)}
            />
            <button
              onClick={handleMagicPaste}
              disabled={!magicPasteText.trim()}
              style={{
                background: magicPasteText.trim() ? 'linear-gradient(135deg, #db2777 0%, #be185d 100%)' : '#f9a8d4',
                color: 'white',
                border: 'none',
                padding: '0 20px',
                borderRadius: '10px',
                cursor: magicPasteText.trim() ? 'pointer' : 'not-allowed',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                transition: 'all 0.3s ease',
                boxShadow: magicPasteText.trim() ? '0 4px 12px rgba(219, 39, 119, 0.2)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (magicPasteText.trim()) e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                if (magicPasteText.trim()) e.target.style.transform = 'translateY(0)';
              }}
            >
              Extract ✨
            </button>
          </div>
        </div>
      )}

      <form className="card order-form" onSubmit={handleSubmit}>
        {saveError && <p role="alert" style={{ color: '#b91c1c' }}>{saveError}</p>}
        <fieldset disabled={isSubmitting} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

        <section className="form-section">
          <h4>👤 Customer Info</h4>
          <div className="form-grid">
            <div className="input-group" style={{ position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label>Name</label>
                {getReturningCustomerData() && (() => {
                  const orderNum = getCustomerOrderNumber();
                  return (
                    <span className="returning-customer-notif">
                      ✨ Returning Customer ({orderNum ? getOrdinal(orderNum) : '2nd'} Order)
                    </span>
                  );
                })()}
              </div>
              <input
                required
                type="text"
                placeholder="e.g. Wey"
                value={formData.customer}
                onChange={e => {
                  setFormData({ ...formData, customer: e.target.value });
                  setShowCustomerSuggestions(true);
                }}
                onFocus={() => setShowCustomerSuggestions(true)}
                onBlur={() => setTimeout(() => setShowCustomerSuggestions(false), 200)}
                autoComplete="off"
              />
              {showCustomerSuggestions && formData.customer && Object.keys(uniqueCustomers).filter(name => name.toLowerCase().includes(formData.customer.toLowerCase()) && name.toLowerCase() !== formData.customer.toLowerCase()).length > 0 && (
                <div className="suggestions-dropdown customer-suggestions">
                  {Object.keys(uniqueCustomers)
                    .filter(name => name.toLowerCase().includes(formData.customer.toLowerCase()) && name.toLowerCase() !== formData.customer.toLowerCase())
                    .slice(0, 5)
                    .map(name => {
                      const details = uniqueCustomers[name];
                      return (
                        <div
                          key={name}
                          className="suggestion-item customer-suggestion-item"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              customer: name,
                              contact: details.contact,
                              platform: details.platform,
                              area: details.area,
                              username: details.username,
                              deliveryMethod: details.deliveryMethod
                            });
                            setShowCustomerSuggestions(false);
                          }}
                        >
                          <div style={{ fontWeight: '700', color: 'var(--primary)' }}>{name}</div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                            {details.area && `📍 ${details.area}`} {details.platform && ` · ${details.platform}`}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
            <div className="input-group">
              <label>Contact (Optional)</label>
              <input type="text" placeholder="09XXXXXXXXX" value={formData.contact} onChange={e => setFormData({ ...formData, contact: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Contacted via</label>
              <select required value={formData.platform} onChange={e => setFormData({ ...formData, platform: e.target.value })}>
                <option value="">-- Select Platform --</option>
                <option value="Messenger">Messenger</option>
                <option value="TikTok">TikTok</option>
                <option value="Instagram">Instagram</option>
                <option value="Walk-in">Walk-in</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="input-group">
              <label>Username / Handle (Optional)</label>
              <input type="text" placeholder="@tiktok_user" value={formData.username} onChange={e => setFormData({ ...formData, username: e.target.value })} />
            </div>
            <div className="input-group">
              <label>🚚 Delivery Method</label>
              <select value={formData.deliveryMethod} onChange={e => setFormData({ ...formData, deliveryMethod: e.target.value, deliveryPayer: e.target.value === 'Lalamove' ? formData.deliveryPayer : '' })}>
                <option value="Lalamove">🛵 Lalamove</option>
                <option value="Home Pick-up">🏠 Home Pick-up</option>
                <option value="Meet-up">🤝 Meet-up</option>
                <option value="Own Delivery">🚗 Own Delivery</option>
              </select>
            </div>

            {formData.deliveryMethod === 'Lalamove' && (
              <div className="input-group">
                <label>💳 Delivery Fee Payer <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.8rem' }}>(Optional)</span></label>
                <div className="delivery-payer-toggle">
                  <button
                    type="button"
                    className={`payer-btn ${formData.deliveryPayer === 'Paid by Sender' ? 'active sender' : ''}`}
                    onClick={() => setFormData({ ...formData, deliveryPayer: formData.deliveryPayer === 'Paid by Sender' ? '' : 'Paid by Sender' })}
                  >
                    💳 Paid by Sender
                  </button>
                  <button
                    type="button"
                    className={`payer-btn ${formData.deliveryPayer === 'Paid by Recipient' ? 'active recipient' : ''}`}
                    onClick={() => setFormData({ ...formData, deliveryPayer: formData.deliveryPayer === 'Paid by Recipient' ? '' : 'Paid by Recipient' })}
                  >
                    🤝 Paid by Recipient
                  </button>
                </div>
                {!formData.deliveryPayer && (
                  <p className="hint" style={{ fontSize: '0.72rem', marginTop: '4px', color: '#f59e0b', fontWeight: '600' }}>
                    ⏳ Leave blank if customer hasn't confirmed yet. You can set this later from Home or Orders.
                  </p>
                )}
              </div>
            )}

            <div className="input-group">
              <label>Delivery Area {formData.deliveryMethod === 'Home Pick-up' && <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.8rem' }}>(Optional)</span>}</label>
              <AutoExpandingTextarea
                placeholder={formData.deliveryMethod === 'Home Pick-up' ? 'Leave blank for pick-up at shop' : 'Enter delivery address...'}
                value={formData.area}
                onChange={e => setFormData({ ...formData, area: e.target.value })}
                style={{ width: '100%' }}
              />
            </div>
            <div className="input-group">
              <label>Landmark (Optional)</label>
              <input type="text" placeholder="e.g. Near church, red gate" value={formData.landmark} onChange={e => setFormData({ ...formData, landmark: e.target.value })} />
            </div>

            <div className="input-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ margin: 0 }}>Delivery Date</label>
                {/* [AI DEV NOTE]: TBD checkbox — when checked, deliveryDate is stored as the string 'TBD'. 
                    Make sure any new logic using deliveryDate checks for 'TBD' before calling new Date(). */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: '600', color: formData.deliveryDate === 'TBD' ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.deliveryDate === 'TBD'}
                    onChange={e => setFormData({ ...formData, deliveryDate: e.target.checked ? 'TBD' : '' })}
                    style={{ accentColor: 'var(--primary)', width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  📅 Date TBD
                </label>
              </div>
              {formData.deliveryDate !== 'TBD' && (
                <input required type="date" value={formData.deliveryDate} onChange={e => setFormData({ ...formData, deliveryDate: e.target.value })} />
              )}
              {formData.deliveryDate && formData.deliveryDate !== 'TBD' && unavailableDates[formData.deliveryDate] && (
                <div className="unavailable-warning-banner" style={{
                  marginTop: '8px',
                  padding: '10px 14px',
                  background: 'rgba(239, 68, 68, 0.08)',
                  border: '1.5px solid rgba(239, 68, 68, 0.35)',
                  borderRadius: '12px',
                  fontSize: '0.82rem',
                  color: '#dc2626',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  <div style={{ fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>⚠️ Marked as Not Available on Calendar</span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text)', opacity: 0.9 }}>
                    Reason: <strong>{unavailableDates[formData.deliveryDate]}</strong>
                  </div>
                  <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '2px', lineHeight: '1.3' }}>
                    💡 You can still proceed with this order if you plan to prepare it early or ship via someone at home.
                  </div>
                </div>
              )}
              {formData.deliveryDate === 'TBD' && (
                <div style={{ padding: '10px 14px', background: 'rgba(255,107,139,0.06)', border: '1.5px dashed rgba(255,107,139,0.3)', borderRadius: '10px', fontSize: '0.85rem', color: 'var(--primary)', fontWeight: '600' }}>
                  📅 Customer is still deciding on the date.
                </div>
              )}
            </div>
            <div className="input-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ margin: 0 }}>Delivery / Shipping Time</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: '600', color: formData.preferredTime === 'TBD' ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.preferredTime === 'TBD'}
                    onChange={e => setFormData({ ...formData, preferredTime: e.target.checked ? 'TBD' : '' })}
                    style={{ accentColor: 'var(--primary)', width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  ⏰ Time TBD
                </label>
              </div>
              {formData.preferredTime !== 'TBD' && (
                <>
                  <input
                    type="time"
                    value={formData.preferredTime}
                    onChange={e => setFormData({ ...formData, preferredTime: e.target.value })}
                  />
                  <p className="hint" style={{ fontSize: '0.72rem', marginTop: '4px', color: 'var(--primary)', fontWeight: '600' }}>
                    ✨ Prep time will be set automatically in timeline.
                  </p>
                </>
              )}
              {formData.preferredTime === 'TBD' && (
                <div style={{ padding: '10px 14px', background: 'rgba(255,107,139,0.06)', border: '1.5px dashed rgba(255,107,139,0.3)', borderRadius: '10px', fontSize: '0.85rem', color: 'var(--primary)', fontWeight: '600' }}>
                  ⏰ Customer is still deciding on the time.
                </div>
              )}
            </div>
            <div className="input-group" style={{ gridColumn: '1 / -1' }}>
              <label>📝 Notes (Optional)</label>
              <AutoExpandingTextarea
                placeholder="e.g. Handle with care, fragile. Customer will call before delivery..."
                value={formData.notes || ''}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: '1.5px solid #eee',
                  borderRadius: '10px',
                  fontFamily: 'Outfit, sans-serif',
                  fontSize: '0.95rem',
                  color: 'var(--text)',
                  background: '#fafafa'
                }}
              />
            </div>
          </div>
        </section>

        <section className="form-section">
          <h4>💐 Bouquets Selection</h4>
          <div className="items-list">
            {formData.items.map((item, idx) => {
              const itemName = item.name;
              const itemDetails = item.details;
              const isItemCustomized = item.isCustomized;

              return (
                <div key={idx} className={`item-row-container ${isItemCustomized ? 'customized' : ''}`}>
                  <div className="item-row" style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <div className="input-group flex-1" style={{ position: 'relative', marginBottom: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <label style={{ fontSize: '0.75rem', color: isItemCustomized ? 'var(--primary)' : '#666', fontWeight: '700', marginBottom: 0 }}>
                          {isItemCustomized ? `✨ Custom Bouquet #${idx + 1}` : `Bouquet Name #${idx + 1}`}
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: isItemCustomized ? 'var(--primary)' : '#e2e8f0', padding: '2px 8px', borderRadius: '20px', transition: 'all 0.2s' }}>
                          <input
                            type="checkbox"
                            id={`custom-${idx}`}
                            checked={isItemCustomized}
                            onChange={(e) => handleItemChange(idx, 'isCustomized', e.target.checked)}
                            style={{ width: '12px', height: '12px', accentColor: 'white', cursor: 'pointer' }}
                          />
                          <label htmlFor={`custom-${idx}`} style={{ fontSize: '0.65rem', fontWeight: '800', color: isItemCustomized ? 'white' : '#666', cursor: 'pointer', marginBottom: 0 }}>CUSTOM</label>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                          <input
                            required={!isItemCustomized}
                            type="text"
                            placeholder={isItemCustomized ? 'Brief description (e.g. Mixed Pink & White)' : 'e.g. Mochi Bouquet (2)'}
                            value={itemName}
                            onChange={e => handleItemChange(idx, 'name', e.target.value)}
                            onFocus={() => setFocusedItemIdx(idx)}
                            onBlur={() => setTimeout(() => setFocusedItemIdx(null), 200)}
                            autoComplete="off"
                            style={{ background: 'white', borderColor: isItemCustomized ? 'var(--primary-light)' : '#eee' }}
                          />
                          {focusedItemIdx === idx && itemName && getSuggestions(itemName).length > 0 && (
                            <div className="suggestions-dropdown" style={{ zIndex: 10 }}>
                              {getSuggestions(itemName).map((s, sIdx) => (
                                <div key={sIdx} className="suggestion-item" onClick={() => handleItemChange(idx, 'name', s)}>
                                  {(() => {
                                    const price = getBouquetPrice(s);
                                    return price !== null && price !== undefined ? `${s} - ₱${price}` : s;
                                  })()}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="item-price-wrapper">
                          <span className="currency-symbol">₱</span>
                          <input
                            type="number"
                            placeholder="Price"
                            className="item-price-input"
                            value={item.price || ''}
                            onChange={e => handleItemChange(idx, 'price', e.target.value)}
                          />
                        </div>
                      </div>
                      <div style={{ marginTop: '10px' }}>
                        <AutoExpandingTextarea
                          placeholder="💌 Card Message for this bouquet..."
                          value={item.message || ''}
                          onChange={e => handleItemChange(idx, 'message', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1.5px solid #edf2f7',
                            borderRadius: '10px',
                            fontSize: '0.85rem',
                            fontFamily: 'Outfit, sans-serif',
                            background: '#fff',
                            color: 'var(--text)',
                            fontStyle: 'italic'
                          }}
                        />
                      </div>
                      <div style={{ marginTop: '10px' }}>
                        {item.referenceImagePreview ? (
                          <div style={{ position: 'relative', width: 'fit-content' }}>
                            <img
                              src={item.referenceImagePreview}
                              alt="Preview"
                              style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid var(--primary-light)', cursor: 'pointer' }}
                              onClick={() => setFullscreenImage(item.referenceImagePreview)}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                handleItemChange(idx, 'referenceImage', null);
                                handleItemChange(idx, 'referenceImageFile', null);
                                handleItemChange(idx, 'referenceImagePreview', null);
                              }}
                              style={{ position: 'absolute', top: '-5px', right: '-5px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: 'var(--primary)', fontWeight: '600', cursor: 'pointer', background: 'white', padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--primary-light)', width: 'fit-content' }}>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleItemImageUpload(idx, e)}
                              style={{ display: 'none' }}
                            />
                            📸 Add Reference Image
                          </label>
                        )}
                        {isProcessingImage && (
                          <div className="processing-indicator">
                            <span className="spinner-mini"></span>
                            <span>Optimizing Image...</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {formData.items.length > 1 && (
                      <button type="button" className="remove-item-btn" onClick={() => removeItem(idx)} style={{ marginTop: '24px' }}>×</button>
                    )}
                  </div>
                  <div className="input-group" style={{ marginTop: '10px', marginBottom: 0 }}>
                    <AutoExpandingTextarea
                      placeholder="Add specific details for this bouquet (e.g. color, wrapper, special requests...)"
                      value={itemDetails}
                      onChange={e => handleItemChange(idx, 'details', e.target.value)}
                      style={{
                        fontSize: '0.85rem',
                        padding: '8px 12px',
                        background: 'white',
                        border: '1px dashed #ccc',
                        width: '100%'
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <button type="button" className="add-item-btn" onClick={addItem}>+ Add Another Bouquet</button>

          <div className="input-group mt-20">
            <label>Add-ons (Optional)</label>
            <input type="text" placeholder="e.g. Ferrero, Blue Balloon" value={formData.addOns} onChange={e => setFormData({ ...formData, addOns: e.target.value })} />
          </div>
        </section>

        <section className="form-section card-message-section">
          <h4>💌 Card Message</h4>
          <AutoExpandingTextarea
            className="card-message-textarea"
            value={formData.message}
            onChange={e => setFormData({ ...formData, message: e.target.value })}
            placeholder="Write a sweet message for the recipient..."
            style={{ width: '100%' }}
          />
        </section>

        <section className="form-section">
          <h4>📸 Screenshots & References (Optional)</h4>
          <div className="image-upload-zone">
            {editingOrder?.isUploadingImage && (!formData.formScreenshots || formData.formScreenshots.length === 0) ? (
              <div style={{ textAlign: 'center', padding: '24px 20px', color: '#be185d', background: '#fdf4ff', borderRadius: '12px', border: '1px dashed #fbcfe8' }}>
                <span className="spinner-mini" style={{ display: 'inline-block', marginBottom: '8px', transform: 'scale(1.5)' }}></span>
                <p style={{ margin: 0, fontWeight: '700', fontSize: '0.9rem' }}>Uploading Image...</p>
              </div>
            ) : (
              <>
                {(formData.formScreenshots && formData.formScreenshots.length > 0) && (
                  <div className="screenshot-gallery" style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '10px', marginBottom: '15px' }}>
                    {formData.formScreenshots.map((shot, idx) => (
                      <div key={idx} style={{ position: 'relative', flexShrink: 0 }}>
                        <img
                          src={shot.url}
                          alt={`Screenshot ${idx + 1}`}
                          style={{ height: '100px', width: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb', cursor: 'pointer' }}
                          onClick={() => setFullscreenImage({ images: formData.formScreenshots.map(s => s.url), currentIndex: idx })}
                        />
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removeScreenshot(idx); }}
                          style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: '22px', height: '22px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                        >✕</button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="custom-file-wrapper">
                  <label className="custom-file-label">
                    <input type="file" accept="image/*" multiple onChange={handleScreenshotsUpload} className="hidden-file-input" />
                    <span>📸 Add Screenshots/References</span>
                  </label>
                </div>
              </>
            )}

            {isProcessingImage && (
              <div className="processing-indicator" style={{ marginTop: '12px' }}>
                <span className="spinner-mini"></span>
                <span>Processing Screenshots...</span>
              </div>
            )}
          </div>
        </section>

        <section className="form-section">
          <h4>💳 Payment</h4>
          <div className="form-grid">
            <div className="input-group">
              <label>Payment Status</label>
              <select value={formData.payment} onChange={e => setFormData({ ...formData, payment: e.target.value })}>
                <option>Downpayment</option>
                <option>Fully Paid</option>
                <option>Other</option>
              </select>
            </div>
            <div className="input-group">
              <label>Total Price including Delivery (₱)</label>
              <input type="number" step="0.01" min="0" value={formData.total} onChange={e => setFormData({ ...formData, total: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Delivery Fee (₱) (Optional)</label>
              <input type="number" step="0.01" min="0" value={formData.deliveryFee} onChange={e => setFormData(prev => ({ ...prev, deliveryFee: e.target.value, total: money(money(prev.total) - money(prev.deliveryFee) + money(e.target.value)).toString() }))} />
            </div>
            <div className="input-group">
              <label>Amount Received / Downpayment (₱)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="Optional (Defaults to 50%)"
                value={formData.amountReceived}
                onChange={e => setFormData({ ...formData, amountReceived: e.target.value })}
              />
              {formData.amountReceived === '' && parseFloat(formData.total) > 0 && <span className="hint">Using 50% default: ₱{((parseFloat(formData.total) || 0) * 0.5).toFixed(2)}</span>}
            </div>
            <div className="input-group">
              <label>Remaining Balance (₱)</label>
              <input
                readOnly
                type="number"
                step="0.01"
                min="0"
                value={formData.balance}
                style={{ background: '#f8fafc', color: '#64748b' }}
              />
              <span className="hint">Auto-computed (Total - Received)</span>
            </div>
          </div>
        </section>

        <button
          type="submit"
          disabled={isSubmitting || submitted || isProcessingImage}
          className={`submit-btn ${submitted ? 'success' : ''}`}
          onTouchEnd={(e) => {
            if (!isSubmitting) {
              e.preventDefault();
              e.currentTarget.click();
            }
          }}
        >
          {isSubmitting ? 'Saving Order...' : submitted ? '✅ Order Saved!' : 'Save Order'}
        </button>
        </fieldset>
      </form>
    </div>
  );
}

// --- History Section ---
function HistorySection({ orders, allOrders, deleteOrder, revertOrder, triggerConfirm, viewPreference, setFullscreenImage, setReschedulingOrder, updateOrder, completeOrder, triggerToast, setEditingOrder, setActiveTab }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'delivery'

  useEffect(() => {
    if (selectedOrder) {
      const latest = allOrders.find(o => o.id === selectedOrder.id);
      if (latest && JSON.stringify(latest) !== JSON.stringify(selectedOrder)) {
        setSelectedOrder(latest);
      }
    }
  }, [allOrders, selectedOrder]);

  const cleanSearch = searchTerm.trim().toLowerCase();
  const numericTerm = cleanSearch.replace(/[₱,\s]/g, '');
  const searchNum = parseFloat(numericTerm);
  const hasNumericSearch = numericTerm !== '' && !isNaN(searchNum);

  const filteredOrders = orders.filter(o => {
    const s = searchTerm.toLowerCase();

    // Match individual item prices (e.g. searching 350 finds an order containing a 350 bouquet)
    const matchesItemPrice = hasNumericSearch && (o.items || []).some(item => {
      const itemName = typeof item === 'string' ? item : (item.name || '');
      const rawPrice = typeof item === 'object' ? item.price : bouquetPrices[itemName];
      if (!rawPrice) return false;
      const numPrice = parseFloat(String(rawPrice).replace(/[^\d.]/g, ''));
      if (isNaN(numPrice)) return false;
      return Math.abs(numPrice - searchNum) < 0.01 || String(numPrice).includes(numericTerm);
    });

    // Match order total price
    const orderTotalNum = parseFloat(String(o.total || '').replace(/[^\d.]/g, ''));
    const matchesTotal = hasNumericSearch && !isNaN(orderTotalNum) && (
      Math.abs(orderTotalNum - searchNum) < 0.01 || String(orderTotalNum).includes(numericTerm)
    );

    return (o.customer && o.customer.toLowerCase().includes(s)) ||
      (o.username && o.username.toLowerCase().includes(s)) ||
      (o.area && o.area.toLowerCase().includes(s)) ||
      (o.items && o.items.some(i => (typeof i === 'string' ? i : (i.name || '')).toLowerCase().includes(s))) ||
      (o.deliveryDate && o.deliveryDate.includes(s)) ||
      matchesItemPrice ||
      matchesTotal;
  }).sort((a, b) => {
    // [AI DEV NOTE]: deliveryDate can be 'TBD'. Guard against new Date('TBD') = Invalid Date.
    const safeDate = (o) => {
      if (o.completedAt) return new Date(o.completedAt);
      if (!o.deliveryDate || o.deliveryDate === 'TBD') return new Date(0); // TBD sorts to bottom
      return new Date(o.deliveryDate);
    };
    const safeDeliveryDate = (o) => {
      if (!o.deliveryDate || o.deliveryDate === 'TBD') return new Date(0);
      return new Date(o.deliveryDate);
    };
    if (sortBy === 'newest') {
      return safeDate(b) - safeDate(a);
    } else if (sortBy === 'oldest') {
      return safeDate(a) - safeDate(b);
    } else if (sortBy === 'delivery') {
      const diff = safeDeliveryDate(b) - safeDeliveryDate(a);
      if (diff !== 0) return diff;
      return (b.preferredTime || '').localeCompare(a.preferredTime || '');
    }
    return 0;
  });

  return (
    <div className="fade-in">
      <div className="dashboard-header">
        <div>
          <h2>Order History</h2>
          <p className="subtitle">{orders.length} completed orders</p>
        </div>
        <div className="history-filters-row">
          <div className="sort-container">
            <span className="sort-label">Sort by:</span>
            <select
              className="history-sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Latest Completed</option>
              <option value="oldest">Oldest Completed</option>
              <option value="delivery">Delivery Date</option>
            </select>
          </div>
        </div>
      </div>

      <div className="search-bar-wrapper">
        <div className="search-input-container">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input-field"
            placeholder="Search by customer, bouquet, or price (e.g. 350)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search-btn" onClick={() => setSearchTerm('')}>✕</button>
          )}
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="empty-state">
          <p>{searchTerm ? '🔍 No matches found in history.' : '📜 No completed orders yet.'}</p>
        </div>
      ) : (
        <div className="order-list">
          {filteredOrders.map(order => (
            <div key={order.id} className="order-card history-card" onClick={() => setSelectedOrder(order)} style={{ cursor: 'pointer' }}>
              <button className="delete-order-btn" onClick={(e) => {
                e.stopPropagation();
                triggerConfirm({
                  title: 'Delete History?',
                  message: 'Permanently remove this order from history? This cannot be undone.',
                  type: 'danger',
                  onConfirm: () => deleteOrder(order.id)
                });
              }}>✕</button>
              <div className="order-header">
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h3 style={{ margin: 0 }}>{order.customer}</h3>
                    {allOrders && allOrders.filter(o => o.customer === order.customer).length > 1 && (
                      <span className="returning-badge">✨ Returning</span>
                    )}
                  </div>
                  <span className="order-area">📍 {order.area} · 📅 {order.deliveryDate === 'TBD' ? 'Date TBD' : `${order.status === 'Completed' ? 'Delivered on' : 'Scheduled on'} ${order.deliveryDate}`} {order.deliveryDate !== 'TBD' && order.preferredTime && `at ${order.preferredTime}`}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                  <span className={`status-tag-${order.status === 'Completed' ? 'completed' : order.status === 'Canceled' ? 'cancelled' : order.status === 'Did Not Reply' ? 'unresponsive' : 'unclaimed'}`}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      fontWeight: '800',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'inline-block',
                      color: 'white',
                      background: order.status === 'Completed' ? '#10b981' : order.status === 'Canceled' ? '#ef4444' : order.status === 'Did Not Reply' ? '#64748b' : '#78716c'
                    }}>
                    {order.status === 'Completed' ? 'Completed ✓' : order.status === 'Canceled' ? 'Canceled ❌' : order.status === 'Did Not Reply' ? 'No Reply 🔇' : 'No Claim 🚫'}
                  </span>
                  <button
                    className="submit-btn outline"
                    style={{ padding: '4px 8px', fontSize: '0.75rem', marginTop: '5px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      triggerConfirm({
                        title: 'Revert Order?',
                        message: 'This will move the order back to the active list and restore its previous status and balance.',
                        type: 'primary',
                        onConfirm: () => revertOrder(order.id)
                      });
                    }}
                  >
                    Revert to Active
                  </button>
                </div>
              </div>

              {/* Order Items & Price Chips */}
              {order.items && order.items.length > 0 && (
                <div className="order-details" style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                  {order.items.map((item, idx) => {
                    const itemName = typeof item === 'string' ? item : (item.name || '');
                    const rawPrice = typeof item === 'object' ? item.price : bouquetPrices[itemName];
                    const numPrice = rawPrice ? parseFloat(String(rawPrice).replace(/[^\d.]/g, '')) : null;
                    const isPriceMatch = hasNumericSearch && numPrice !== null && !isNaN(numPrice) && (
                      Math.abs(numPrice - searchNum) < 0.01 || String(numPrice).includes(numericTerm)
                    );

                    return (
                      <span
                        key={idx}
                        className="detail-chip"
                        style={isPriceMatch ? { border: '1.5px solid var(--primary)', background: '#fff0f3', fontWeight: '800' } : {}}
                      >
                        💐 {itemName}
                        {numPrice !== null && !isNaN(numPrice) && (
                          <span style={{ opacity: 0.85, fontSize: '0.85em', marginLeft: '5px', fontWeight: '800', color: isPriceMatch ? 'var(--primary)' : 'inherit' }}>
                            ₱{numPrice.toFixed(0)}
                          </span>
                        )}
                      </span>
                    );
                  })}
                  {parseFloat(order.total) > 0 && (
                    <span className="detail-chip price">
                      Total: ₱{(parseFloat(order.total) || 0).toFixed(2)}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <OrderDetailModal
        isOpen={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        order={selectedOrder}
        viewPreference={viewPreference}
        setFullscreenImage={setFullscreenImage}
        setReschedulingOrder={setReschedulingOrder}
        updateOrder={updateOrder}
        completeOrder={completeOrder}
        triggerConfirm={triggerConfirm}
        triggerToast={triggerToast}
        setEditingOrder={setEditingOrder}
        setActiveTab={setActiveTab}
      />
    </div>
  );
}
function SalesTracker({ orders, expenses, savings, setSavings, addExpense, deleteExpense, triggerConfirm }) {
  const [searchTerm, setSearchTerm] = useState('');

  // --- ANALYTICS DATA ---
  // 'orders' here is ALL orders passed from App.js (active + completed). Filter for completed ones.
  const completedOrders = useMemo(() => orders.filter(o => o.status === 'Completed'), [orders]);

  const analytics = useMemo(() => {
    const totalRevenue = completedOrders.reduce((sum, o) => sum + (parseFloat(o.total) || 0), 0);
    const avgOrderValue = completedOrders.length > 0 ? totalRevenue / completedOrders.length : 0;
    const uniqueCustomers = new Set(completedOrders.map(o => o.customer)).size;

    // Best selling bouquets
    const bouquetCounts = {};
    completedOrders.forEach(o => {
      (o.items || []).forEach(item => {
        const name = typeof item === 'string' ? item : (item.name || '');
        if (name) {
          bouquetCounts[name] = (bouquetCounts[name] || 0) + 1;
        }
      });
    });
    const bestSelling = Object.entries(bouquetCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
    const maxCount = bestSelling.length > 0 ? bestSelling[0][1] : 1;

    const analyticsYear = new Date().getFullYear();
    const monthlyData = getMonthlyOrderVolume(completedOrders, analyticsYear);
    const maxMonthly = Math.max(...monthlyData.map(m => m.count), 1);

    const topCustomers = getTopReturningCustomers(completedOrders);

    return { totalRevenue, avgOrderValue, uniqueCustomers, bestSelling, maxCount, monthlyData, maxMonthly, analyticsYear, topCustomers };
  }, [completedOrders]);

  const barColors = [
    'linear-gradient(135deg, #ff6b8b, #e05270)',
    'linear-gradient(135deg, #f472b6, #db2777)',
    'linear-gradient(135deg, #a78bfa, #7c3aed)',
    'linear-gradient(135deg, #38bdf8, #0284c7)',
    'linear-gradient(135deg, #34d399, #059669)',
    'linear-gradient(135deg, #fbbf24, #d97706)',
    'linear-gradient(135deg, #fb923c, #ea580c)',
    'linear-gradient(135deg, #94a3b8, #64748b)',
  ];

  const monthBarColors = [
    '#fecdd3', '#fda4af', '#fb7185', '#f43f5e', '#e11d48', '#be123c'
  ];

  const filteredOrders = orders.filter(o => {
    const s = searchTerm.toLowerCase();
    return (o.customer && o.customer.toLowerCase().includes(s)) ||
      (o.username && o.username.toLowerCase().includes(s)) ||
      (o.deliveryDate && o.deliveryDate.includes(s)) ||
      (o.payment && o.payment.toLowerCase().includes(s)) ||
      (o.status && o.status.toLowerCase().includes(s));
  });

  return (
    <div className="fade-in">
      <h2>Sales & Analytics</h2>

      {completedOrders.length > 0 ? (
        <div className="history-analytics">
          <div className="analytics-summary-cards">
            <div className="analytics-stat-card">
              <span className="stat-icon">📦</span>
              <div className="stat-value">{completedOrders.length}</div>
              <div className="stat-label">Completed</div>
            </div>
            <div className="analytics-stat-card">
              <span className="stat-icon">💰</span>
              <div className="stat-value">₱{analytics.totalRevenue.toLocaleString()}</div>
              <div className="stat-label">Total Revenue</div>
            </div>
            <div className="analytics-stat-card">
              <span className="stat-icon">📈</span>
              <div className="stat-value">₱{Math.round(analytics.avgOrderValue).toLocaleString()}</div>
              <div className="stat-label">Avg Order</div>
            </div>
            <div className="analytics-stat-card">
              <span className="stat-icon">👥</span>
              <div className="stat-value">{analytics.uniqueCustomers}</div>
              <div className="stat-label">Customers</div>
            </div>
          </div>

          {analytics.bestSelling.length > 0 && (
            <div className="analytics-chart-section">
              <h4>🏆 Best Selling Bouquets</h4>
              <div className="bar-chart">
                {analytics.bestSelling.map(([name, count], idx) => (
                  <div key={name} className="bar-chart-item">
                    <div className="bar-chart-label" title={name}>{name.replace(' Bouquet', '')}</div>
                    <div className="bar-chart-track">
                      <div
                        className="bar-chart-fill"
                        style={{
                          width: `${(count / analytics.maxCount) * 100}%`,
                          background: barColors[idx % barColors.length]
                        }}
                      >
                        <span className="bar-chart-value">{count}</span>
                      </div>
                    </div>
                    <span className="bar-chart-count">{count}×</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="analytics-chart-section">
            <h4>📅 Monthly Order Volume <span className="monthly-chart-year">{analytics.analyticsYear}</span></h4>
            <div className="monthly-chart">
              {analytics.monthlyData.map((m, idx) => (
                <div key={`${m.month}-${m.year}`} className="monthly-bar-wrapper">
                  <span className="monthly-bar-count">{m.count > 0 ? m.count : ''}</span>
                  <div
                    className="monthly-bar"
                    style={{
                      height: `${Math.max((m.count / analytics.maxMonthly) * 100, 5)}%`,
                      background: monthBarColors[idx % monthBarColors.length]
                    }}
                    title={`${m.label}: ${m.count} orders`}
                  />
                  <span className="monthly-bar-label">{m.label}</span>
                </div>
              ))}
            </div>
          </div>

          {analytics.topCustomers.length > 0 && (
            <div className="analytics-chart-section">
              <h4>✨ Top Returning Customers</h4>
              <div className="top-customers-list">
                {analytics.topCustomers.map((c, idx) => (
                  <div key={c.name} className="top-customer-item">
                    <div className={`top-customer-rank ${idx === 0 ? 'gold' : idx === 1 ? 'silver' : idx === 2 ? 'bronze' : 'default'}`}>
                      {idx + 1}
                    </div>
                    <div className="top-customer-info">
                      <div className="top-customer-name">{c.name}</div>
                      {c.tiktokUsername && (
                        <div className="top-customer-tiktok">🎵 @{c.tiktokUsername}</div>
                      )}
                      <div className="top-customer-orders">{c.count} order{c.count !== 1 ? 's' : ''}</div>
                    </div>
                    <div className="top-customer-revenue">₱{c.revenue.toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="empty-state">
          <p>📈 Analytics will appear here once you complete orders.</p>
        </div>
      )}

      {/* Marketing Insights Section */}
      <div className="card marketing-insights-card mt-20" style={{ margin: 0 }}>
        <div className="insights-header">
          <h3>📊 Marketing Insights</h3>
          <span className="insights-tag">Top Channels</span>
        </div>
        <div className="platform-stats-container">
          {(() => {
            const stats = orders.reduce((acc, o) => {
              const p = o.platform || 'Other';
              if (!acc[p]) acc[p] = { count: 0, revenue: 0 };
              acc[p].count += 1;
              acc[p].revenue += parseFloat(o.total || 0);
              return acc;
            }, {});

            const sorted = Object.entries(stats).sort((a, b) => b[1].revenue - a[1].revenue);
            const maxRev = Math.max(...Object.values(stats).map(p => p.revenue), 1);

            if (sorted.length === 0) return <p className="hint">No platform data available yet.</p>;

            return sorted.map(([platform, data]) => (
              <div key={platform} className="platform-stat-row">
                <div className="platform-info">
                  <span className="platform-name">
                    {platform === 'Messenger' ? '💬 Messenger' :
                      platform === 'TikTok' ? '🎵 TikTok' :
                        platform === 'Instagram' ? '📸 Instagram' :
                          platform === 'Walk-in' ? '🚶 Walk-in' : `📱 ${platform}`}
                  </span>
                  <span className="platform-count">{data.count} orders</span>
                </div>
                <div className="platform-bar-wrapper">
                  <div
                    className={`platform-bar ${platform.toLowerCase()}`}
                    style={{ width: `${(data.revenue / maxRev) * 100}%` }}
                  ></div>
                </div>
                <div className="platform-revenue">₱{data.revenue.toLocaleString()}</div>
              </div>
            ));
          })()}
        </div>
        <p className="hint mt-10">Revenue breakdown by customer source.</p>
      </div>

      <div className="search-bar-wrapper mt-20">
        <div className="search-input-container">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input-field"
            placeholder="Search transactions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search-btn" onClick={() => setSearchTerm('')}>×</button>
          )}
        </div>
      </div>

      <div className="card mt-20">
        <h3>📜 Order Transaction Log</h3>
        {filteredOrders.length === 0 ? <p className="hint">{searchTerm ? 'No matching transactions found.' : 'No transactions yet.'}</p> : (
          <div className="table-responsive">
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map(o => (
                  <tr key={o.id}>
                    <td data-label="Customer">{o.customer}</td>
                    <td data-label="Date">{o.deliveryDate}</td>
                    <td data-label="Total">₱{parseFloat(o.total || 0).toFixed(2)}</td>
                    <td data-label="Payment"><span className={`tag ${(o.payment || '').replace(' ', '-').toLowerCase()}`}>{o.payment || ''}</span></td>
                    <td data-label="Status">
                      <span className={`status-pill ${(o.status || '').toLowerCase().replace(/\s+/g, '-')}`}>{o.status || ''}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}



// --- Admin Section ---
// =============================================================================
// 🖥️ SESSIONS LOG COMPONENT
// Displays all sign-in sessions: IP, location, time, duration, device
// =============================================================================
function SessionsLog() {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'active' | 'ended'
  const [searchTerm, setSearchTerm] = useState('');
  const [sessionNow, setSessionNow] = useState(() => Date.now());

  useEffect(() => {
    const q = query(
      collection(db, 'sessions'),
      orderBy('startedAt', 'desc'),
      limit(100)
    );
    const unsub = onSnapshot(q, (snap) => {
      setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoading(false);
    }, () => setIsLoading(false));

    // Force re-render every 15s to update ongoing status / duration
    const interval = setInterval(() => setSessionNow(Date.now()), 15000);

    return () => {
      unsub();
      clearInterval(interval);
    };
  }, []);

  const isSessionActive = (s, now = sessionNow) => {
    return sessionIsActive(s, now);
  };

  const getEffectiveEndTime = (s) => {
    if (s.endedAt) return s.endedAt;
    if (isSessionActive(s)) return null;
    return s.lastActiveAt || s.startedAt;
  };

  const calcDuration = (startedAt, effectiveEndTime) => {
    const start = new Date(startedAt);
    const end = effectiveEndTime ? new Date(effectiveEndTime) : new Date();
    const mins = Math.max(1, Math.floor((end - start) / 60000));
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`;
  };

  const fmtTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const fmtDateGroup = (iso) => {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'TODAY';
    if (d.toDateString() === yesterday.toDateString()) return 'YESTERDAY';
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const activeCount = sessions.filter(s => isSessionActive(s)).length;
  const endedCount = sessions.length - activeCount;

  // Filtered & Searched sessions
  const filteredSessions = useMemo(() => {
    return sessions.filter(s => {
      const active = sessionIsActive(s, sessionNow);
      if (filterMode === 'active' && !active) return false;
      if (filterMode === 'ended' && active) return false;

      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const str = `${s.username || ''} ${s.userEmail || ''} ${s.ip || ''} ${s.location || ''} ${s.os || ''} ${s.browser || ''}`.toLowerCase();
        if (!str.includes(term)) return false;
      }
      return true;
    });
  }, [sessions, filterMode, searchTerm, sessionNow]);

  // Grouped with ACTIVE ALWAYS AT THE TOP
  const grouped = useMemo(() => {
    const activeItems = [];
    const endedByDate = {};

    filteredSessions.forEach(s => {
      const active = sessionIsActive(s, sessionNow);
      if (active && filterMode !== 'ended') {
        activeItems.push(s);
      } else {
        const key = new Date(s.startedAt).toDateString();
        if (!endedByDate[key]) endedByDate[key] = { label: fmtDateGroup(s.startedAt), items: [] };
        endedByDate[key].items.push(s);
      }
    });

    const result = [];
    if (activeItems.length > 0) {
      result.push({
        label: '🟢 ACTIVE NOW',
        isPinnedActive: true,
        items: activeItems
      });
    }

    Object.values(endedByDate).forEach(g => result.push(g));
    return result;
  }, [filteredSessions, filterMode, sessionNow]);

  const getBrowserIcon = (b) => ({ Chrome: '🌐', Firefox: '🦊', Safari: '🧭', Edge: '🔷', Opera: '🔴' }[b] || '💻');
  const getOSIcon = (o) => ({ Windows: '🪟', macOS: '🍎', iOS: '📱', Android: '🤖', Linux: '🐧' }[o] || '💻');

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--text-muted)' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>⏳</div>
        <p style={{ fontFamily: 'monospace' }}>Loading session logs...</p>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--text-muted)' }}>
        <div style={{ fontSize: '3rem', marginBottom: '14px' }}>🖥️</div>
        <p style={{ fontWeight: 700, marginBottom: '6px', fontFamily: 'monospace' }}>No sessions recorded yet.</p>
        <p style={{ fontSize: '0.82rem', fontFamily: 'monospace', opacity: 0.7 }}>Sessions will appear here automatically.</p>
      </div>
    );
  }

  return (
    <div className="sessions-log-container" style={{ maxWidth: '780px', margin: '0 auto' }}>
      {/* Search & Filter Header */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
        {/* Quick Filter Tabs */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={() => setFilterMode('all')}
            style={{
              background: filterMode === 'all' ? 'rgba(0, 255, 65, 0.25)' : 'rgba(0, 255, 65, 0.05)',
              border: `1.5px solid ${filterMode === 'all' ? '#00ff41' : 'rgba(0, 255, 65, 0.2)'}`,
              color: '#00ff41',
              padding: '6px 14px',
              borderRadius: '20px',
              fontFamily: "'Share Tech Mono', monospace",
              fontWeight: '700',
              fontSize: '0.8rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            📋 All ({sessions.length})
          </button>

          <button
            onClick={() => setFilterMode('active')}
            style={{
              background: filterMode === 'active' ? 'rgba(0, 255, 65, 0.35)' : 'rgba(0, 255, 65, 0.08)',
              border: `1.5px solid ${filterMode === 'active' ? '#00ff41' : 'rgba(0, 255, 65, 0.3)'}`,
              color: '#00ff41',
              padding: '6px 14px',
              borderRadius: '20px',
              fontFamily: "'Share Tech Mono', monospace",
              fontWeight: '700',
              fontSize: '0.8rem',
              cursor: 'pointer',
              boxShadow: activeCount > 0 ? '0 0 12px rgba(0, 255, 65, 0.3)' : 'none',
              transition: 'all 0.2s ease',
            }}
          >
            🟢 Active ({activeCount})
          </button>

          <button
            onClick={() => setFilterMode('ended')}
            style={{
              background: filterMode === 'ended' ? 'rgba(255, 0, 85, 0.25)' : 'rgba(255, 255, 255, 0.05)',
              border: `1.5px solid ${filterMode === 'ended' ? '#ff0055' : 'rgba(255, 255, 255, 0.15)'}`,
              color: filterMode === 'ended' ? '#ff5577' : 'var(--text-muted)',
              padding: '6px 14px',
              borderRadius: '20px',
              fontFamily: "'Share Tech Mono', monospace",
              fontWeight: '700',
              fontSize: '0.8rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            ⏹️ Past ({endedCount})
          </button>

          <div style={{ flex: 1, minWidth: '160px' }}>
            <input
              type="text"
              placeholder="🔍 Filter by user, IP, location..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                background: 'rgba(0, 255, 65, 0.05)',
                border: '1px solid rgba(0, 255, 65, 0.2)',
                borderRadius: '16px',
                padding: '6px 14px',
                color: '#d1ffd1',
                fontSize: '0.8rem',
                fontFamily: "'Share Tech Mono', monospace",
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
      </div>

      {/* No matching result */}
      {grouped.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
          <p style={{ fontFamily: 'monospace' }}>No sessions matching your filter.</p>
        </div>
      )}

      {/* Clean Timeline Session List */}
      {grouped.map((group, gi) => (
        <div key={gi} style={{ marginBottom: '24px' }}>
          {/* Section Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <span
              style={{
                fontSize: '0.74rem',
                fontWeight: '800',
                fontFamily: "'Share Tech Mono', monospace",
                letterSpacing: '0.5px',
                color: group.isPinnedActive ? '#00ff41' : 'var(--text-muted)',
                background: group.isPinnedActive ? 'rgba(0, 255, 65, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                border: `1px solid ${group.isPinnedActive ? 'rgba(0, 255, 65, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
                padding: '3px 10px',
                borderRadius: '12px',
              }}
            >
              {group.label}
            </span>
            <div style={{ flex: 1, height: '1px', background: group.isPinnedActive ? 'rgba(0, 255, 65, 0.3)' : 'rgba(255, 255, 255, 0.1)' }} />
            <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
              {group.items.length} {group.items.length === 1 ? 'session' : 'sessions'}
            </span>
          </div>

          {/* Clean Compact Cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {group.items.map((s) => {
              const isActive = isSessionActive(s);
              const effectiveEndTime = getEffectiveEndTime(s);
              const durationStr = calcDuration(s.startedAt, effectiveEndTime);

              return (
                <div
                  key={s.id}
                  style={{
                    background: isActive ? 'rgba(0, 255, 65, 0.06)' : 'rgba(255, 255, 255, 0.025)',
                    border: `1px solid ${isActive ? 'rgba(0, 255, 65, 0.35)' : 'rgba(255, 255, 255, 0.08)'}`,
                    borderRadius: '14px',
                    padding: '12px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    transition: 'all 0.2s ease',
                    boxShadow: isActive ? '0 0 15px rgba(0, 255, 65, 0.08)' : 'none',
                  }}
                >
                  {/* Top Line: Status + User + Duration */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {/* Status indicator dot */}
                      {isActive ? (
                        <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: '#00ff41', display: 'inline-block', boxShadow: '0 0 8px #00ff41', animation: 'pulse 1.5s infinite' }} />
                      ) : (
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#64748b', display: 'inline-block' }} />
                      )}

                      {/* Username */}
                      <strong style={{ fontSize: '0.92rem', color: isActive ? '#00ff41' : '#e2e8f0', fontFamily: "'Share Tech Mono', monospace" }}>
                        @{s.username || 'unknown_user'}
                      </strong>

                      {/* Active / Duration Badge */}
                      <span
                        style={{
                          fontSize: '0.7rem',
                          fontWeight: '700',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontFamily: "'Share Tech Mono', monospace",
                          background: isActive ? 'rgba(0, 255, 65, 0.2)' : 'rgba(255, 255, 255, 0.08)',
                          color: isActive ? '#00ff41' : '#94a3b8',
                          border: `1px solid ${isActive ? 'rgba(0, 255, 65, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
                        }}
                      >
                        {isActive ? '● ONLINE NOW' : `⏱ ${durationStr}`}
                      </span>
                    </div>

                    {/* Time Range Pill (e.g. 8:00 AM - 9:00 AM) */}
                    <div style={{ fontSize: '0.78rem', color: isActive ? '#ffea00' : '#cbd5e1', fontFamily: "'Share Tech Mono', monospace", fontWeight: '600' }}>
                      🕒 {fmtTime(s.startedAt)} – {isActive ? 'Ongoing' : fmtTime(effectiveEndTime)}
                    </div>
                  </div>

                  {/* Bottom Line: Device + Location + IP */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: "'Share Tech Mono', monospace" }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{getOSIcon(s.os)} {s.os || 'Unknown OS'}</span>
                      <span>·</span>
                      <span>{getBrowserIcon(s.browser)} {s.browser || 'Browser'}</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>📍 {s.location || 'Unknown Location'}</span>
                      <span>·</span>
                      <span style={{ opacity: 0.7 }}>{s.ip || '—'}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// 👥 USER PROFILES LIST (Admin View)
// =============================================================================
function UserProfilesList() {
  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, 'user_profiles'),
      orderBy('createdAt', 'desc'),
      limit(100)
    );
    const unsub = onSnapshot(q, (snap) => {
      setProfiles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoading(false);
    }, () => setIsLoading(false));
    return () => unsub();
  }, []);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--text-muted)' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>⏳</div>
        <p style={{ fontFamily: 'monospace' }}>Loading user profiles...</p>
      </div>
    );
  }

  if (profiles.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--text-muted)' }}>
        <div style={{ fontSize: '3rem', marginBottom: '14px' }}>👥</div>
        <p style={{ fontWeight: 700, marginBottom: '6px', fontFamily: 'monospace' }}>No user profiles created yet.</p>
        <p style={{ fontSize: '0.82rem', fontFamily: 'monospace', opacity: 0.7 }}>User profiles will appear here once users complete profile setup.</p>
      </div>
    );
  }

  return (
    <div className="sessions-log-container">
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <span className="session-stat-pill active-pill">👥 {profiles.length} REGISTERED PROFILES</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {profiles.map(p => (
          <div key={p.id} className="session-card-item session-active" style={{ padding: '14px 18px' }}>
            <div className="session-accent-bar accent-active" />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <strong style={{ fontSize: '1rem', color: '#00ff41', fontFamily: 'monospace' }}>
                  @{p.username || 'unknown'}
                </strong>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                  {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}
                </span>
              </div>
              <div style={{ fontSize: '0.82rem', color: '#d1ffd1', fontFamily: 'monospace', marginBottom: '4px' }}>
                📧 {p.userEmail || 'No email'}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                ID: {p.userId || p.id}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// 🛠️ ADMIN SECTION COMPONENT (with Feedback + Sessions + Profiles tabs)
// =============================================================================
function AdminSection({ feedback, deleteFeedback, triggerConfirm, triggerToast }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isPendingExpanded, setIsPendingExpanded] = useState(true);
  const [isResolvedExpanded, setIsResolvedExpanded] = useState(false);
  const [adminTab, setAdminTab] = useState('feedback');

  const updateFeedbackStatus = async (id, status) => {
    await updateDoc(doc(db, "feedback", id), { status });
  };

  const handleCopyFeedback = (text) => {
    safeCopyToClipboard(text);
    if (triggerToast) triggerToast('Feedback text copied! 📋');
  };

  const filteredFeedback = useMemo(() => {
    if (!searchTerm.trim()) return feedback;
    const s = searchTerm.toLowerCase();
    return feedback.filter(f =>
      (f.userName && f.userName.toLowerCase().includes(s)) ||
      (f.userEmail && f.userEmail.toLowerCase().includes(s)) ||
      (f.text && f.text.toLowerCase().includes(s)) ||
      (f.status && f.status.toLowerCase().includes(s)) ||
      (f.createdAt && new Date(f.createdAt).toLocaleDateString().includes(s))
    );
  }, [feedback, searchTerm]);

  const pendingFeedback = filteredFeedback.filter(f => f.status !== 'resolved');
  const resolvedFeedback = filteredFeedback.filter(f => f.status === 'resolved');

  return (
    <div className="fade-in admin-container-premium">
      <div className="admin-header-premium" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: '900', marginBottom: '4px', color: 'var(--text)' }}>🛠️ Admin Dashboard</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0 }}>System feedback & feature requests overview</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <span style={{ background: 'rgba(251, 191, 36, 0.15)', color: '#b45309', padding: '6px 12px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '700' }}>
            ⏳ {pendingFeedback.length} Pending
          </span>
          <span style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#065f46', padding: '6px 12px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '700' }}>
            ✓ {resolvedFeedback.length} Resolved
          </span>
        </div>
      </div>

      {/* ── Admin Tab Switcher ─────────────────────────────────────── */}
      <div className="admin-tab-switcher">
        <button
          className={`admin-tab-btn ${adminTab === 'feedback' ? 'active' : ''}`}
          onClick={() => setAdminTab('feedback')}
        >
          📬 Feedback
          {pendingFeedback.length > 0 && (
            <span className="admin-tab-badge">{pendingFeedback.length}</span>
          )}
        </button>
        <button
          className={`admin-tab-btn ${adminTab === 'sessions' ? 'active' : ''}`}
          onClick={() => setAdminTab('sessions')}
        >
          🖥️ Sessions Log
        </button>
        <button
          className={`admin-tab-btn ${adminTab === 'profiles' ? 'active' : ''}`}
          onClick={() => setAdminTab('profiles')}
        >
          👥 User Profiles
        </button>
      </div>

      {adminTab === 'feedback' ? (
        <>
          <div className="search-bar-wrapper" style={{ marginBottom: '24px' }}>
            <div className="search-input-container">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="search-input-field"
                placeholder="Search feedback by name, email, text or status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ fontSize: '16px' }}
              />
              {searchTerm && (
                <button className="clear-search-btn" onClick={() => setSearchTerm('')}>×</button>
              )}
            </div>
          </div>

          {/* Pending Section */}
          <div className="admin-section-block-premium">
            <div
              className="section-title-premium pending clickable-header"
              onClick={() => setIsPendingExpanded(!isPendingExpanded)}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span className="section-icon-premium">⏳</span>
                <h3>Pending Requests</h3>
                <span className="section-count-badge-premium pending">{pendingFeedback.length}</span>
              </div>
              <span className="collapse-chevron" style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-muted)' }}>
                {isPendingExpanded ? '▼' : '►'}
              </span>
            </div>

            {isPendingExpanded && (
              <div className="feedback-list-premium fade-in">
                {pendingFeedback.length === 0 ? (
                  <div className="empty-state-premium">
                    <div className="empty-icon-premium">{searchTerm ? '🔍' : '🎉'}</div>
                    <p style={{ fontWeight: 600 }}>{searchTerm ? 'No matches found for your search.' : 'All caught up! No pending requests.'}</p>
                  </div>
                ) : (
                  pendingFeedback.map(f => (
                    <div key={f.id} className="feedback-card-premium pending">
                      <div className="card-header-premium">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div className="avatar-circle-premium">
                            {f.userName ? f.userName[0].toUpperCase() : '👤'}
                          </div>
                          <div className="user-info-premium">
                            <strong>{f.userName}</strong>
                            <span>{f.userEmail}</span>
                          </div>
                        </div>
                        <div className="status-pill-premium pending">⏳ Pending</div>
                      </div>

                      <div className="card-body-premium">
                        <p className="feedback-text-premium">{f.text}</p>
                        {f.imageUrl && (
                          <div className="feedback-media-premium">
                            <img src={f.imageUrl} alt="Feedback Media" onClick={() => window.open(f.imageUrl, '_blank')} />
                          </div>
                        )}
                      </div>

                      <div className="card-footer-premium">
                        <span className="timestamp-premium">🕒 {new Date(f.createdAt).toLocaleDateString()} · {new Date(f.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        <div className="action-group-premium">
                          <button className="copy-mini-btn" onClick={() => handleCopyFeedback(f.text)} style={{ padding: '6px 12px', fontSize: '0.8rem' }}>📋 Copy</button>
                          <button className="resolve-btn-premium" onClick={() => updateFeedbackStatus(f.id, 'resolved')}>Resolve</button>
                          <button className="trash-btn-premium" onClick={() => {
                            triggerConfirm({
                              title: 'Delete Feedback?',
                              message: 'This entry will be permanently removed from the system.',
                              type: 'danger',
                              onConfirm: () => deleteFeedback(f.id)
                            });
                          }}>🗑️</button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Finished / Resolved Section */}
          <div className="admin-section-block-premium resolved-block">
            <div
              className="section-title-premium resolved clickable-header"
              onClick={() => setIsResolvedExpanded(!isResolvedExpanded)}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span className="section-icon-premium">✓</span>
                <h3>Finished / Resolved</h3>
                <span className="section-count-badge-premium resolved">{resolvedFeedback.length}</span>
              </div>
              <span className="collapse-chevron" style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-muted)' }}>
                {isResolvedExpanded ? '▼' : '►'}
              </span>
            </div>

            {isResolvedExpanded && (
              <div className="feedback-list-premium resolved-list fade-in">
                {resolvedFeedback.length === 0 ? (
                  <div className="empty-state-premium">
                    <div className="empty-icon-premium">{searchTerm ? '🔍' : '📭'}</div>
                    <p style={{ fontWeight: 600 }}>{searchTerm ? 'No matches found for your search.' : 'No resolved requests yet.'}</p>
                  </div>
                ) : (
                  resolvedFeedback.map(f => (
                    <div key={f.id} className="feedback-card-premium resolved">
                      <div className="card-header-premium">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div className="avatar-circle-premium">
                            {f.userName ? f.userName[0].toUpperCase() : '👤'}
                          </div>
                          <div className="user-info-premium">
                            <strong>{f.userName}</strong>
                            <span>{f.userEmail}</span>
                          </div>
                        </div>
                        <div className="status-pill-premium resolved">✓ Resolved</div>
                      </div>

                      <div className="card-body-premium">
                        <p className="feedback-text-premium">{f.text}</p>
                        {f.imageUrl && (
                          <div className="feedback-media-premium">
                            <img src={f.imageUrl} alt="Feedback Media" onClick={() => window.open(f.imageUrl, '_blank')} />
                          </div>
                        )}
                      </div>

                      <div className="card-footer-premium">
                        <span className="timestamp-premium">🕒 {new Date(f.createdAt).toLocaleDateString()} · {new Date(f.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        <div className="action-group-premium">
                          <button className="copy-mini-btn" onClick={() => handleCopyFeedback(f.text)} style={{ padding: '6px 12px', fontSize: '0.8rem' }}>📋 Copy</button>
                          <button className="undo-btn-premium" onClick={() => updateFeedbackStatus(f.id, 'pending')}>Undo</button>
                          <button className="trash-btn-premium" onClick={() => {
                            triggerConfirm({
                              title: 'Delete Feedback?',
                              message: 'This entry will be permanently removed from the system.',
                              type: 'danger',
                              onConfirm: () => deleteFeedback(f.id)
                            });
                          }}>🗑️</button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </>
      ) : adminTab === 'sessions' ? (
        <SessionsLog />
      ) : (
        <UserProfilesList />
      )}
    </div>
  );
}

function CatalogManager({ orders, setGlobalUploadLabel, setGlobalUploadProgress, triggerToast, triggerConfirm }) {
  const [bouquets, setBouquets] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCatalogBouquet, setSelectedCatalogBouquet] = useState(null);

  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  const [price, setPrice] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [mainFlowers, setMainFlowers] = useState('');
  const [fillers, setFillers] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);
  const [isNovemberFeatured, setIsNovemberFeatured] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);

  const [isUploading, setIsUploading] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [showNovemberArchive, setShowNovemberArchive] = useState(false);

  // Catalog Down / Maintenance State
  const [catalogStatus, setCatalogStatus] = useState({
    isDown: false,
    title: "We're Refreshing Our Blooms! 🌸",
    message: "We are currently updating our flower catalog with fresh new arrangements and stock. We'll be back shortly!"
  });
  const [downTitleInput, setDownTitleInput] = useState("We're Refreshing Our Blooms! 🌸");
  const [downMessageInput, setDownMessageInput] = useState("We are currently updating our flower catalog with fresh new arrangements and stock. We'll be back shortly!");
  const [showDownNoticeCustomizer, setShowDownNoticeCustomizer] = useState(false);
  const [isSavingCatalogStatus, setIsSavingCatalogStatus] = useState(false);

  useEffect(() => {
    const unsubStatus = onSnapshot(doc(db, "bouquets", "_catalog_status"), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setCatalogStatus({
          isDown: !!data.isDown,
          title: data.title || "We're Refreshing Our Blooms! 🌸",
          message: data.message || "We are currently updating our flower catalog with fresh new arrangements and stock. We'll be back shortly!",
          updatedAt: data.updatedAt
        });
        setDownTitleInput(data.title || "We're Refreshing Our Blooms! 🌸");
        setDownMessageInput(data.message || "We are currently updating our flower catalog with fresh new arrangements and stock. We'll be back shortly!");
      }
    });
    return () => unsubStatus();
  }, []);

  useEffect(() => {
    const q = query(collection(db, "bouquets"));
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs
        .filter(d => !d.id.startsWith('_') && d.id !== 'catalog_status' && d.data().name)
        .map(doc => ({ id: doc.id, ...doc.data() }));
      data.sort((a, b) => {
        if (a.position !== undefined && b.position !== undefined) return a.position - b.position;
        if (a.position !== undefined) return -1;
        if (b.position !== undefined) return 1;
        return (a.createdAt || '') > (b.createdAt || '') ? -1 : 1;
      });
      setBouquets(data);
    });
    return () => unsub();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setIsEditModalOpen(false);
    setName('');
    setNumber('');
    setPrice('');
    setSalePrice('');
    setMainFlowers('');
    setFillers('');
    setIsAvailable(true);
    setIsNovemberFeatured(false);
    setImageFile(null);
    setImagePreview(null);
    setExistingImage(null);
  };

  const handlePriceInput = (val, setter) => {
    let v = val.trim();
    if (v === '' || v === '₱') { setter(''); return; }
    if (!v.startsWith('₱')) v = '₱' + v.replace(/₱/g, '');
    setter(v);
  };

  const handleEdit = (b) => {
    setEditingId(b.id);
    setName(b.name);
    setNumber(b.number || '');
    setPrice(b.price);
    setSalePrice(b.salePrice || '');
    setMainFlowers(b.mainFlowers ? b.mainFlowers.join(', ') : '');
    setFillers(b.fillers ? b.fillers.join(', ') : '');
    setIsAvailable(b.isAvailable !== false);
    setIsNovemberFeatured(!!b.isNovemberFeatured);
    setExistingImage(b.image);
    setImageFile(null);
    setImagePreview(null);
    setIsEditModalOpen(true);
  };

  const handleToggleCatalogDown = async (shouldBeDown) => {
    triggerConfirm({
      title: shouldBeDown ? 'Take Down Flower Catalog?' : 'Bring Flower Catalog Online?',
      message: shouldBeDown 
        ? 'The public flower brochure will display a beautiful maintenance screen. Customers won\'t be able to browse bouquets until you turn it back online.'
        : 'The flower brochure will immediately be live and accessible to all customers.',
      type: shouldBeDown ? 'danger' : 'primary',
      onConfirm: async () => {
        try {
          setIsSavingCatalogStatus(true);
          const payload = {
            isDown: shouldBeDown,
            title: downTitleInput.trim() || "We're Refreshing Our Blooms! 🌸",
            message: downMessageInput.trim() || "We are currently updating our flower catalog with fresh new arrangements and stock. We'll be back shortly!",
            updatedAt: new Date().toISOString()
          };
          await Promise.all([
            setDoc(doc(db, "bouquets", "_catalog_status"), payload, { merge: true }),
            setDoc(doc(db, "settings", "catalog_status"), payload, { merge: true })
          ]);
          setCatalogStatus(prev => ({ ...prev, isDown: shouldBeDown }));
          triggerToast(shouldBeDown ? '🔴 Catalog is now DOWN (Maintenance Mode active)' : '🟢 Catalog is now LIVE and online!');
        } catch (err) {
          console.error(err);
          triggerToast('Error updating catalog status: ' + (err.message || ''));
        } finally {
          setIsSavingCatalogStatus(false);
        }
      }
    });
  };

  const handleSaveDownNotice = async () => {
    try {
      setIsSavingCatalogStatus(true);
      const payload = {
        title: downTitleInput.trim() || "We're Refreshing Our Blooms! 🌸",
        message: downMessageInput.trim() || "We are currently updating our flower catalog with fresh new arrangements and stock. We'll be back shortly!",
        updatedAt: new Date().toISOString()
      };
      await Promise.all([
        setDoc(doc(db, "bouquets", "_catalog_status"), payload, { merge: true }),
        setDoc(doc(db, "settings", "catalog_status"), payload, { merge: true })
      ]);
      triggerToast('Maintenance notice message updated!');
      setShowDownNoticeCustomizer(false);
    } catch (err) {
      console.error(err);
      triggerToast('Error saving maintenance message.');
    } finally {
      setIsSavingCatalogStatus(false);
    }
  };

  const handleToggleAvailability = async (bouquet, e) => {
    if (e) e.stopPropagation();
    const currentAvailable = bouquet.isAvailable !== false;
    const nextAvailable = !currentAvailable;
    try {
      await updateDoc(doc(db, "bouquets", bouquet.id), { isAvailable: nextAvailable });
      if (selectedCatalogBouquet && selectedCatalogBouquet.id === bouquet.id) {
        setSelectedCatalogBouquet(prev => ({ ...prev, isAvailable: nextAvailable }));
      }
      triggerToast(nextAvailable ? `"${bouquet.name}" marked as Available! ✅` : `"${bouquet.name}" marked as Not Available (Sold Out)! 🚫`);
    } catch (err) {
      console.error(err);
      triggerToast("Error updating availability status.");
    }
  };

  const handleDelete = async (id) => {
    triggerConfirm({
      title: 'Delete Flower?',
      message: 'This will remove the flower from the brochure catalog permanently.',
      type: 'danger',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, "bouquets", id));
          triggerToast("Flower deleted from brochure.");
          if (editingId === id) resetForm();
        } catch (e) {
          console.error(e);
          triggerToast("Error deleting flower.");
        }
      }
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name || !price || (!imageFile && !existingImage)) {
      triggerToast("Please provide name, price, and an image.");
      return;
    }

    setIsUploading(true);
    let finalImageUrl = existingImage;

    try {
      if (imageFile) {
        setGlobalUploadLabel("Uploading flower image...");
        finalImageUrl = await uploadToCloudinaryWithProgress(imageFile, (loaded, total) => {
          const progress = (loaded / total) * 100;
          setGlobalUploadProgress(progress);
        });
        setGlobalUploadProgress(100);
      }

      setGlobalUploadLabel("Saving to database...");
      const bouquetData = {
        name,
        number,
        price,
        salePrice,
        mainFlowers: mainFlowers.split(',').map(s => s.trim()).filter(Boolean),
        fillers: fillers.split(',').map(s => s.trim()).filter(Boolean),
        isAvailable,
        isNovemberFeatured,
        image: finalImageUrl,
      };

      if (editingId) {
        await updateDoc(doc(db, "bouquets", editingId), bouquetData);
        triggerToast("Flower successfully updated!");
      } else {
        bouquetData.createdAt = new Date().toISOString();
        await addDoc(collection(db, "bouquets"), bouquetData);
        triggerToast("Flower successfully added to brochure!");
      }
      resetForm();
    } catch (err) {
      console.error(err);
      triggerToast("Error saving flower.");
    } finally {
      setGlobalUploadProgress(null);
      setIsUploading(false);
    }
  };

  const topBouquets = useMemo(() => {
    if (!orders) return [];
    const counts = {};
    orders.filter(o => o.status === 'Completed').forEach(o => {
      (o.items || []).forEach(item => {
        const itemName = typeof item === 'string' ? item : (item.name || '');
        if (itemName) {
          // Normalize to match bouquet name, often items have " Bouquet" in the name or don't
          const normName = itemName.replace(' Bouquet', '').toLowerCase().trim();
          counts[normName] = (counts[normName] || 0) + 1;
        }
      });
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(entry => ({ name: entry[0], count: entry[1] }));
  }, [orders]);

  // Sync isBestSeller flag back to each bouquet doc so the public brochure
  // can display the badge without needing access to the orders collection.
  useEffect(() => {
    if (bouquets.length === 0) return;
    bouquets.forEach(b => {
      const normName = (b.name || '').replace(' Bouquet', '').toLowerCase().trim();
      const isBestSeller = topBouquets.some(tb => tb.name === normName);
      // Only write if value changed to avoid unnecessary Firestore writes
      if ((b.isBestSeller || false) !== isBestSeller) {
        updateDoc(doc(db, 'bouquets', b.id), { isBestSeller }).catch(() => { });
      }
    });
  }, [topBouquets, bouquets]);

  const regularBouquets = bouquets.filter(item => !item.isNovemberFeatured);
  const archivedNovemberBouquets = bouquets.filter(item => item.isNovemberFeatured);
  const displayedBouquets = showNovemberArchive ? archivedNovemberBouquets : regularBouquets;

  return (
    <div className="fade-in settings-container-premium catalog-wide">
      <div className="settings-main-header-premium">
        <h1>Brochure Catalog</h1>
        <p>Manage the flowers and stock availability displayed in your brochure.</p>
      </div>

      {/* ===== CATALOG LIVE STATUS & MAINTENANCE SWITCH ===== */}
      <div className={`catalog-status-banner-card ${catalogStatus.isDown ? 'is-down' : 'is-live'}`}>
        <div className="status-banner-left">
          <div className="status-indicator-pill">
            <span className={`status-dot-pulse ${catalogStatus.isDown ? 'dot-down' : 'dot-live'}`} />
            <span className="status-text-badge">
              {catalogStatus.isDown ? 'CATALOG IS DOWN (MAINTENANCE)' : 'CATALOG IS LIVE (ONLINE)'}
            </span>
          </div>
          <h2 className="status-banner-title">
            {catalogStatus.isDown 
              ? '🌸 Flower Brochure is currently taken down' 
              : '✨ Flower Brochure is live and accessible to customers'}
          </h2>
          <p className="status-banner-desc">
            {catalogStatus.isDown
              ? 'Customers visiting the brochure will see your customized maintenance screen with your contact info & social links.'
              : 'Customers can freely browse all active bouquets and order details.'}
          </p>
          {catalogStatus.isDown && (
            <div className="status-notice-preview">
              <strong>Active Notice:</strong> "{catalogStatus.title}" — <em>{catalogStatus.message}</em>
            </div>
          )}
        </div>

        <div className="status-banner-right">
          <div className="status-action-row">
            <div className="toggle-switch-wrapper">
              <span className="toggle-label-text">
                {catalogStatus.isDown ? 'Down Status:' : 'Catalog Status:'}
              </span>
              <button
                type="button"
                className={`catalog-maintenance-toggle-btn ${catalogStatus.isDown ? 'active-down' : 'active-live'}`}
                onClick={() => handleToggleCatalogDown(!catalogStatus.isDown)}
                disabled={isSavingCatalogStatus}
                title={catalogStatus.isDown ? 'Click to bring catalog online' : 'Click to take catalog down'}
              >
                <span className="toggle-slider-knob">
                  {catalogStatus.isDown ? '🔴' : '🟢'}
                </span>
                <span className="toggle-state-text">
                  {catalogStatus.isDown ? 'DOWN' : 'LIVE'}
                </span>
              </button>
            </div>
            <button
              type="button"
              className="customize-down-notice-btn"
              onClick={() => setShowDownNoticeCustomizer(!showDownNoticeCustomizer)}
            >
              {showDownNoticeCustomizer ? '▲ Close Notice Settings' : '⚙️ Notice Settings'}
            </button>
          </div>
        </div>
      </div>

      {showDownNoticeCustomizer && (
        <div className="card down-notice-customizer-card fade-in">
          <div className="customizer-header">
            <h4>⚙️ Customize Maintenance Notice Screen</h4>
            <p>This message is shown to customers when the catalog is taken down.</p>
          </div>
          <div className="quick-presets-row">
            <span className="presets-label">Quick Presets:</span>
            <button
              type="button"
              className="preset-chip"
              onClick={() => {
                setDownTitleInput("We're Refreshing Our Blooms! 🌸");
                setDownMessageInput("We are currently updating our flower catalog with fresh new arrangements and stock. We'll be back shortly!");
              }}
            >
              🌸 Updating Catalog
            </button>
            <button
              type="button"
              className="preset-chip"
              onClick={() => {
                setDownTitleInput("Restocking Fresh Flowers 🌿");
                setDownMessageInput("We are currently restocking our freshest stems and arrangements. Please check back soon or message us directly!");
              }}
            >
              🌿 Restocking
            </button>
            <button
              type="button"
              className="preset-chip"
              onClick={() => {
                setDownTitleInput("Taking a Short Break ✨");
                setDownMessageInput("Our online catalog is briefly paused. For urgent bouquet inquiries, feel free to send us a direct message on Facebook!");
              }}
            >
              ✨ Short Break
            </button>
          </div>
          <div className="form-grid mt-10">
            <div className="input-group">
              <label>Notice Headline / Title</label>
              <input
                type="text"
                value={downTitleInput}
                onChange={(e) => setDownTitleInput(e.target.value)}
                placeholder="e.g. We're Refreshing Our Blooms! 🌸"
              />
            </div>
            <div className="input-group full-width">
              <label>Notice Description / Message</label>
              <textarea
                rows={3}
                value={downMessageInput}
                onChange={(e) => setDownMessageInput(e.target.value)}
                placeholder="e.g. We are currently updating our flower catalog..."
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '12px',
                  border: '1.5px solid #cbd5e1',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowDownNoticeCustomizer(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveDownNotice}
              disabled={isSavingCatalogStatus}
            >
              💾 Save Notice Message
            </button>
          </div>
        </div>
      )}

      {/* ===== ADD NEW FLOWER (inline, always visible) ===== */}
      <div className="card mt-20">
        <h3>Add New Flower</h3>
        <form onSubmit={handleSave} className="form-grid mt-10">
          <div className="input-group">
            <label>Flower/Bouquet Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} required placeholder="e.g. Soft Thumbelina Bouquet" />
          </div>
          <div className="input-group">
            <label>Number / Variant</label>
            <input type="text" value={number} onChange={e => setNumber(e.target.value)} placeholder="e.g. No. 1" />
          </div>
          <div className="input-group">
            <label>Price</label>
            <input type="text" value={price} onChange={e => handlePriceInput(e.target.value, setPrice)} required placeholder="₱500" />
          </div>
          <div className="input-group">
            <label>Sale Price (Optional)</label>
            <input type="text" value={salePrice} onChange={e => handlePriceInput(e.target.value, setSalePrice)} placeholder="₱400" />
          </div>
          <div className="input-group">
            <label>Main Flowers (comma separated)</label>
            <input type="text" value={mainFlowers} onChange={e => setMainFlowers(e.target.value)} placeholder="e.g. 10 pcs Gerbera" />
          </div>
          <div className="input-group">
            <label>Fillers (comma separated)</label>
            <input type="text" value={fillers} onChange={e => setFillers(e.target.value)} placeholder="e.g. Aster, Chamomile" />
          </div>
          <div className="input-group">
            <label>Stock / Availability Status</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setIsAvailable(true)}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: '12px',
                  border: isAvailable ? '2px solid #10b981' : '1.5px solid #cbd5e1',
                  background: isAvailable ? 'linear-gradient(135deg, #dcfce7, #bbf7d0)' : 'transparent',
                  color: isAvailable ? '#166534' : 'var(--text-muted)',
                  fontWeight: isAvailable ? '800' : '600',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                ✅ Available
              </button>
              <button
                type="button"
                onClick={() => setIsAvailable(false)}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: '12px',
                  border: !isAvailable ? '2px solid #ef4444' : '1.5px solid #cbd5e1',
                  background: !isAvailable ? 'linear-gradient(135deg, #fee2e2, #fecaca)' : 'transparent',
                  color: !isAvailable ? '#991b1b' : 'var(--text-muted)',
                  fontWeight: !isAvailable ? '800' : '600',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                🚫 Not Available
              </button>
            </div>
          </div>
          <div className="input-group" style={{ gridColumn: '1 / -1' }}>
            <label>Upload Image</label>
            <label className="catalog-file-upload-btn">
              <input type="file" accept="image/*" onChange={handleImageChange} required={!existingImage} style={{ display: 'none' }} />
              📷 {imageFile ? imageFile.name : 'Choose Image'}
            </label>
            {imagePreview && (
              <img src={imagePreview} alt="preview" style={{ width: '120px', height: '120px', marginTop: '10px', borderRadius: '10px', objectFit: 'cover', border: '2px solid var(--primary-light)' }} />
            )}
          </div>
          <div style={{ gridColumn: '1 / -1', marginTop: '10px' }}>
            <button type="submit" className="submit-btn primary" disabled={isUploading} style={{ width: '100%' }}>
              {isUploading ? 'Saving...' : 'Add to Brochure'}
            </button>
          </div>
        </form>
      </div>

      {/* ===== EDIT FLOWER MODAL ===== */}
      {isEditModalOpen && (
        <div
          onClick={resetForm}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--card-bg, #fff)', borderRadius: '24px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 30px 80px rgba(0,0,0,0.25)', padding: '28px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '800' }}>✏️ Edit Flower</h3>
              <button onClick={resetForm} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: 'var(--text-muted)' }}>✕</button>
            </div>
            <form onSubmit={e => { handleSave(e); }} className="form-grid">
              <div className="input-group">
                <label>Flower/Bouquet Name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} required placeholder="e.g. Soft Thumbelina Bouquet" />
              </div>
              <div className="input-group">
                <label>Number / Variant</label>
                <input type="text" value={number} onChange={e => setNumber(e.target.value)} placeholder="e.g. No. 1" />
              </div>
              <div className="input-group">
                <label>Price</label>
                <input type="text" value={price} onChange={e => handlePriceInput(e.target.value, setPrice)} required placeholder="₱500" />
              </div>
              <div className="input-group">
                <label>Sale Price (Optional)</label>
                <input type="text" value={salePrice} onChange={e => handlePriceInput(e.target.value, setSalePrice)} placeholder="₱400" />
              </div>
              <div className="input-group">
                <label>Main Flowers (comma separated)</label>
                <input type="text" value={mainFlowers} onChange={e => setMainFlowers(e.target.value)} placeholder="e.g. 10 pcs Gerbera" />
              </div>
              <div className="input-group">
                <label>Fillers (comma separated)</label>
                <input type="text" value={fillers} onChange={e => setFillers(e.target.value)} placeholder="e.g. Aster, Chamomile" />
              </div>
              <div className="input-group">
                <label>Stock / Availability Status</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setIsAvailable(true)}
                    style={{
                      flex: 1,
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: isAvailable ? '2px solid #10b981' : '1.5px solid #cbd5e1',
                      background: isAvailable ? 'linear-gradient(135deg, #dcfce7, #bbf7d0)' : 'transparent',
                      color: isAvailable ? '#166534' : 'var(--text-muted)',
                      fontWeight: isAvailable ? '800' : '600',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    ✅ Available
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsAvailable(false)}
                    style={{
                      flex: 1,
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: !isAvailable ? '2px solid #ef4444' : '1.5px solid #cbd5e1',
                      background: !isAvailable ? 'linear-gradient(135deg, #fee2e2, #fecaca)' : 'transparent',
                      color: !isAvailable ? '#991b1b' : 'var(--text-muted)',
                      fontWeight: !isAvailable ? '800' : '600',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    🚫 Not Available
                  </button>
                </div>
              </div>
              <div className="input-group" style={{ gridColumn: '1 / -1' }}>
                <label>Catalog Location</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setIsNovemberFeatured(true)}
                    style={{
                      flex: '1 1 210px',
                      padding: '11px 14px',
                      borderRadius: '12px',
                      border: isNovemberFeatured ? '2px solid #8b5e3c' : '1.5px solid #cbd5e1',
                      background: isNovemberFeatured ? 'linear-gradient(135deg, #f5e7d2, #ead2ae)' : 'transparent',
                      color: isNovemberFeatured ? '#68432c' : 'var(--text-muted)',
                      fontWeight: isNovemberFeatured ? '800' : '600',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    🗂 Move to November 1 Archive
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsNovemberFeatured(false)}
                    style={{
                      flex: '1 1 210px',
                      padding: '11px 14px',
                      borderRadius: '12px',
                      border: !isNovemberFeatured ? '2px solid var(--primary)' : '1.5px solid #cbd5e1',
                      background: !isNovemberFeatured ? 'var(--primary-light)' : 'transparent',
                      color: !isNovemberFeatured ? 'var(--primary-dark)' : 'var(--text-muted)',
                      fontWeight: !isNovemberFeatured ? '800' : '600',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    🌸 Restore to Public Catalog
                  </button>
                </div>
              </div>
              <div className="input-group" style={{ gridColumn: '1 / -1' }}>
                <label>Upload Image (Leave blank to keep existing)</label>
                <label className="catalog-file-upload-btn">
                  <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                  📷 {imageFile ? imageFile.name : 'Change Image'}
                </label>
                {(imagePreview || existingImage) && (
                  <img src={imagePreview || existingImage} alt="preview" style={{ width: '120px', height: '120px', marginTop: '10px', borderRadius: '10px', objectFit: 'cover', border: '2px solid var(--primary-light)' }} />
                )}
              </div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button type="submit" className="submit-btn primary" disabled={isUploading} style={{ flex: 1 }}>
                  {isUploading ? 'Saving...' : 'Update Flower'}
                </button>
                <button type="button" className="submit-btn secondary" onClick={resetForm} disabled={isUploading} style={{ flex: 1 }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card mt-20">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
          <div>
            <h3 style={{ marginBottom: '2px' }}>
              {showNovemberArchive ? 'November 1 Archive' : 'Current Catalog'}{' '}
              <span style={{ fontSize: '0.8rem', fontWeight: '500', color: 'var(--text-muted)' }}>({displayedBouquets.length} flowers)</span>
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
              {showNovemberArchive
                ? 'Saved arrangements and images are kept here but hidden from the public brochure.'
                : 'Drag cards to reorder. Changes sync to the brochure in real-time.'}
            </p>
          </div>
          <button
            type="button"
            className={`catalog-archive-folder ${showNovemberArchive ? 'is-open' : ''}`}
            onClick={() => { setShowNovemberArchive(value => !value); setCatalogSearch(''); }}
          >
            <span className="catalog-archive-folder-icon" aria-hidden="true">{showNovemberArchive ? '🌸' : '📁'}</span>
            <span>
              <strong>{showNovemberArchive ? 'Back to Current Catalog' : 'November 1 Archive'}</strong>
              <small>{showNovemberArchive ? `${regularBouquets.length} active flowers` : `${archivedNovemberBouquets.length} saved flowers`}</small>
            </span>
          </button>
        </div>
        {/* Search bar */}
        <div className="search-input-container" style={{ marginBottom: '16px' }}>
          <span className="search-icon">🔍</span>
          <input
            className="search-input-field"
            type="text"
            placeholder="Search by name, price, or flowers..."
            value={catalogSearch}
            onChange={e => setCatalogSearch(e.target.value)}
          />
          {catalogSearch && (
            <button className="clear-search-btn" onClick={() => setCatalogSearch('')}>✕</button>
          )}
        </div>
        {displayedBouquets.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', marginTop: '15px' }}>
            {showNovemberArchive ? 'The November 1 archive is empty.' : 'No flowers added to the catalog yet.'}
          </p>
        ) : (() => {
          const nameCounts = {};
          const bouquetsWithNumbers = displayedBouquets.map(b => {
            const n = (b.name || '').replace(' Bouquet', '').toLowerCase().trim();
            if (!nameCounts[n]) nameCounts[n] = 0;
            nameCounts[n]++;
            return { ...b, _computedNumber: b.number || nameCounts[n] };
          });

          const q = catalogSearch.toLowerCase();
          const filtered = bouquetsWithNumbers.filter(b =>
            !q ||
            b.name?.toLowerCase().includes(q) ||
            b.price?.toLowerCase().includes(q) ||
            (b.mainFlowers || []).some(f => f.toLowerCase().includes(q)) ||
            (b.fillers || []).some(f => f.toLowerCase().includes(q))
          );
          if (filtered.length === 0) return <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '30px 0' }}>No flowers match "{catalogSearch}".</p>;
          return (
            <div className="catalog-brochure-grid">
              {filtered.map((b) => {
                const normBouquetName = (b.name || '').replace(' Bouquet', '').toLowerCase().trim();
                const bestSellerData = topBouquets.find(tb => tb.name === normBouquetName);
                const isBestSeller = !!bestSellerData;
                const isItemAvailable = b.isAvailable !== false;

                return (
                  <div
                    key={b.id}
                    className={`catalog-brochure-card ${editingId === b.id ? 'editing' : ''} ${!isItemAvailable ? 'is-not-available' : ''}`}
                    draggable
                    onDragStart={(e) => {
                      const origIndex = bouquets.findIndex(orig => orig.id === b.id);
                      e.dataTransfer.setData('dragIndex', origIndex);
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={async (e) => {
                      e.preventDefault();
                      const fromIndex = parseInt(e.dataTransfer.getData('dragIndex'));
                      const toIndex = bouquets.findIndex(orig => orig.id === b.id);
                      if (isNaN(fromIndex) || fromIndex === toIndex || toIndex === -1) return;
                      const reordered = [...bouquets];
                      const [moved] = reordered.splice(fromIndex, 1);
                      reordered.splice(toIndex, 0, moved);
                      setBouquets(reordered);
                      try {
                        for (let i = 0; i < reordered.length; i++) {
                          await updateDoc(doc(db, "bouquets", reordered[i].id), { position: i });
                        }
                      } catch (err) { console.error(err); }
                    }}
                    onClick={() => setSelectedCatalogBouquet(b)}
                  >
                    {/* Image + Price + Position badges */}
                    <div className="catalog-brochure-card-image-wrap">
                      {isBestSeller && (
                        <div className="catalog-brochure-badge-bestseller">🔥 Best Seller ({bestSellerData.count})</div>
                      )}
                      {b.isNovemberFeatured && (
                        <div style={{ position: 'absolute', top: isBestSeller ? '48px' : '12px', left: '12px', zIndex: 6, padding: '6px 11px', borderRadius: '999px', background: 'linear-gradient(135deg, #6b4d3b, #9a6847)', color: '#fff8ed', boxShadow: '0 4px 14px rgba(54,37,30,0.3)', fontSize: '0.67rem', fontWeight: '800', letterSpacing: '0.05em' }}>
                          📁 Archived
                        </div>
                      )}
                      {!isItemAvailable && (
                        <div className="catalog-card-blocking-overlay">
                          <div className="catalog-card-blocking-banner">
                            🚫 NOT AVAILABLE
                          </div>
                        </div>
                      )}
                      <img
                        src={b.image}
                        alt={b.name}
                        style={{ filter: !isItemAvailable ? 'grayscale(45%) brightness(0.72)' : 'none' }}
                        onError={e => { e.target.style.background = '#f8f0f3'; e.target.src = ''; }}
                      />
                      <div className="catalog-brochure-badge-price" style={{ background: !isItemAvailable ? '#64748b' : undefined }}>
                        {b.salePrice ? (
                          <>
                            <span style={{ textDecoration: 'line-through', fontSize: '0.8rem', color: '#a0a0a0', marginRight: '6px' }}>{b.price}</span>
                            <span style={{ color: '#e11d48' }}>{b.salePrice}</span>
                          </>
                        ) : (
                          b.price
                        )}
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="catalog-brochure-card-body">
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                          <h4 className="catalog-brochure-name">{b.name} {b._computedNumber}</h4>
                          <span
                            style={{
                              fontSize: '0.68rem',
                              fontWeight: '800',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              background: isItemAvailable ? '#dcfce7' : '#fee2e2',
                              color: isItemAvailable ? '#166534' : '#991b1b',
                              border: isItemAvailable ? '1px solid #86efac' : '1px solid #fca5a5'
                            }}
                          >
                            {isItemAvailable ? 'Available' : 'Sold Out'}
                          </span>
                        </div>
                        <div className="catalog-brochure-divider" />
                      </div>

                      {b.mainFlowers && b.mainFlowers.length > 0 && (
                        <div className="catalog-brochure-flower-section">
                          <div className="catalog-brochure-flower-label main-label">Main Flowers</div>
                          <div className="catalog-brochure-tags">
                            {b.mainFlowers.slice(0, 3).map((f, i) => (
                              <span key={i} className="catalog-brochure-tag">{f}</span>
                            ))}
                            {b.mainFlowers.length > 3 && (
                              <span className="catalog-brochure-tag more">+{b.mainFlowers.length - 3} more</span>
                            )}
                          </div>
                        </div>
                      )}

                      {b.fillers && b.fillers.length > 0 && (
                        <div className="catalog-brochure-flower-section">
                          <div className="catalog-brochure-flower-label filler-label">Fillers</div>
                          <div className="catalog-brochure-tags">
                            {b.fillers.slice(0, 3).map((f, i) => (
                              <span key={i} className="catalog-brochure-tag filler">{f}</span>
                            ))}
                            {b.fillers.length > 3 && (
                              <span className="catalog-brochure-tag more">+{b.fillers.length - 3} more</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="catalog-brochure-actions" onClick={e => e.stopPropagation()}>
                        <button
                          className={`availability-toggle-btn ${!isItemAvailable ? 'is-sold' : 'is-avail'}`}
                          onClick={(e) => handleToggleAvailability(b, e)}
                          title={isItemAvailable ? "Click to mark as Not Available" : "Click to mark as Available"}
                        >
                          {isItemAvailable ? '🚫 Mark Sold' : '✅ Mark Available'}
                        </button>
                        <button
                          className={`edit-btn ${editingId === b.id ? 'active' : ''}`}
                          onClick={() => handleEdit(b)}
                        >
                          {editingId === b.id ? '✏️ Editing...' : '✏️ Edit'}
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(b.id)}
                        >
                          🗑 Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Brochure-style detail modal */}
      {selectedCatalogBouquet && (
        <div
          onClick={() => setSelectedCatalogBouquet(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '24px', width: '100%', maxWidth: '680px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 30px 80px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}
          >
            {/* Modal image */}
            <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '24px 24px 0 0' }}>
              <img
                src={selectedCatalogBouquet.image}
                alt={selectedCatalogBouquet.name}
                style={{
                  width: '100%',
                  height: '300px',
                  objectFit: 'cover',
                  display: 'block',
                  filter: selectedCatalogBouquet.isAvailable === false ? 'grayscale(50%) brightness(0.7)' : 'none',
                  transition: 'filter 0.3s ease'
                }}
              />
              <button onClick={() => setSelectedCatalogBouquet(null)} style={{ position: 'absolute', top: '14px', right: '14px', background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', borderRadius: '50%', width: '36px', height: '36px', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>✕</button>
              
              {/* Bold Blocking Center Overlay */}
              {selectedCatalogBouquet.isAvailable === false && (
                <div className="unavailable-modal-overlay-block">
                  <div className="unavailable-stamp-badge">
                    <span className="stamp-icon">🚫</span>
                    <div className="stamp-text">
                      <div className="stamp-main">NOT AVAILABLE</div>
                      <div className="stamp-sub">CURRENTLY OUT OF STOCK</div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ position: 'absolute', bottom: '14px', right: '14px', background: selectedCatalogBouquet.isAvailable === false ? '#64748b' : 'linear-gradient(135deg, var(--primary), var(--primary-dark))', color: '#fff', borderRadius: '20px', padding: '6px 16px', fontWeight: '700', fontSize: '1rem', boxShadow: '0 2px 10px rgba(0,0,0,0.3)', zIndex: 5 }}>
                {selectedCatalogBouquet.price}
              </div>
            </div>
            {/* Modal body */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              {/* Prominent Out of Stock Warning Banner */}
              {selectedCatalogBouquet.isAvailable === false && (
                <div className="unavailable-warning-alert">
                  <span style={{ fontSize: '1.3rem' }}>⚠️</span>
                  <div>
                    <div style={{ fontWeight: '800', fontSize: '0.92rem', color: '#991b1b' }}>Currently Out of Stock</div>
                    <div style={{ fontSize: '0.78rem', color: '#b91c1c', marginTop: '2px' }}>This flower arrangement is marked as not available in your brochure catalog.</div>
                  </div>
                </div>
              )}

              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                  <h2 style={{ fontSize: '1.4rem', fontWeight: '800', margin: 0, background: 'rgba(192, 84, 106, 0.08)', borderRadius: '20px', padding: '6px 16px', display: 'inline-block' }}>
                    {selectedCatalogBouquet.name} {selectedCatalogBouquet._computedNumber || selectedCatalogBouquet.number}
                  </h2>
                  <span
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: '800',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      background: selectedCatalogBouquet.isAvailable !== false ? 'linear-gradient(135deg, #dcfce7, #bbf7d0)' : 'linear-gradient(135deg, #fee2e2, #fecaca)',
                      color: selectedCatalogBouquet.isAvailable !== false ? '#166534' : '#991b1b',
                      border: selectedCatalogBouquet.isAvailable !== false ? '1.5px solid #86efac' : '1.5px solid #f87171'
                    }}
                  >
                    {selectedCatalogBouquet.isAvailable !== false ? '✅ Available' : '🚫 Not Available (Sold Out)'}
                  </span>
                </div>
                <div style={{ height: '3px', width: '40px', background: 'linear-gradient(90deg, var(--primary), var(--primary-light))', borderRadius: '4px', marginTop: '8px' }} />
              </div>
              {selectedCatalogBouquet.mainFlowers?.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Main Flowers</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {selectedCatalogBouquet.mainFlowers.map((f, i) => <span key={i} style={{ background: '#fdf2f8', color: '#9d174d', borderRadius: '20px', padding: '4px 12px', fontSize: '0.82rem', fontWeight: '500' }}>{f}</span>)}
                  </div>
                </div>
              )}
              {selectedCatalogBouquet.fillers?.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#34d399', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Fillers</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {selectedCatalogBouquet.fillers.map((f, i) => <span key={i} style={{ background: '#ecfdf5', color: '#065f46', borderRadius: '20px', padding: '4px 12px', fontSize: '0.82rem', fontWeight: '500' }}>{f}</span>)}
                  </div>
                </div>
              )}
              
              {/* Modal Actions: Toggle Availability + Edit + Delete */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
                <button
                  onClick={(e) => handleToggleAvailability(selectedCatalogBouquet, e)}
                  style={{
                    width: '100%',
                    padding: '13px',
                    background: selectedCatalogBouquet.isAvailable === false ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #fee2e2, #fecaca)',
                    color: selectedCatalogBouquet.isAvailable === false ? '#ffffff' : '#991b1b',
                    border: selectedCatalogBouquet.isAvailable === false ? 'none' : '1.5px solid #f87171',
                    borderRadius: '12px',
                    fontWeight: '800',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: selectedCatalogBouquet.isAvailable === false ? '0 4px 12px rgba(16,185,129,0.3)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {selectedCatalogBouquet.isAvailable === false ? '✅ Mark as Available' : '🚫 Mark as Not Available / Sold Out'}
                </button>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => { setSelectedCatalogBouquet(null); handleEdit(selectedCatalogBouquet); }}
                    style={{ flex: 1, padding: '12px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}
                  >
                    ✏️ Edit This Flower
                  </button>
                  <button
                    onClick={() => { handleDelete(selectedCatalogBouquet.id); setSelectedCatalogBouquet(null); }}
                    style={{ flex: 1, padding: '12px', background: '#fff5f5', color: '#ef4444', border: '1.5px solid #fecaca', borderRadius: '12px', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}
                  >
                    🗑 Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}




function SettingsSection({ user, logout, savings, setSavings, orders, expenses, viewPreference, setViewPreference, showAddressInReceipt, setShowAddressInReceipt, showContactInReceipt, setShowContactInReceipt, darkMode, setDarkMode, themeMode, setThemeMode, isAdmin, adminFeedback, deleteFeedback, triggerConfirm, triggerToast, setGlobalUploadProgress, setGlobalUploadLabel }) {
  const [shopName, setShopName] = useState(() => localStorage.getItem('shopName') || 'Solyminx');
  const [shopAddress, setShopAddress] = useState(() => localStorage.getItem('shopAddress') || '');
  const [shopContact, setShopContact] = useState(() => localStorage.getItem('shopContact') || '');
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState(savings?.target || 5000);
  const [saved, setSaved] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackImageFile, setFeedbackImageFile] = useState(null);
  const [feedbackImagePreview, setFeedbackImagePreview] = useState(null);
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  const [activeSubView, setActiveSubView] = useState(null);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [showAppInfoModal, setShowAppInfoModal] = useState(false);

  useEffect(() => {
    // Delay so the DOM has painted the new sub-view before we scroll it
    const t = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
      const scrollContainers = document.querySelectorAll(
        '.subview-scroll-premium, .settings-container-premium, .admin-container-premium'
      );
      scrollContainers.forEach(container => {
        container.scrollTop = 0;
      });
    }, 30);
    return () => clearTimeout(t);
  }, [activeSubView]);

  const handleFeedbackTextChange = (e) => {
    const val = e.target.value;
    if (isAdmin && val === '/admin') {
      setActiveSubView('admin');
      setFeedbackText('');
      return;
    }
    setFeedbackText(val);
  };

  const saveShopInfo = () => {
    localStorage.setItem('shopName', shopName);
    localStorage.setItem('shopAddress', shopAddress);
    localStorage.setItem('shopContact', shopContact);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const saveGoal = () => {
    if (!isNaN(goalInput) && goalInput > 0) {
      setSavings({ ...savings, target: parseInt(goalInput) });
      setEditingGoal(false);
    }
  };

  const handleFeedbackImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsProcessingImage(true);
      try {
        const options = { maxSizeMB: 0.5, maxWidthOrHeight: 1200, useWebWorker: true };
        const compressedFile = await imageCompression(file, options);
        const previewUrl = URL.createObjectURL(compressedFile);
        setFeedbackImageFile(compressedFile);
        setFeedbackImagePreview(previewUrl);
      } catch (error) {
        console.error('Error compressing feedback image:', error);
      } finally {
        setIsProcessingImage(false);
      }
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackText.trim() && !feedbackImageFile) return;
    setIsSendingFeedback(true);

    try {
      let imageUrl = null;
      if (feedbackImageFile) {
        setGlobalUploadLabel("Sending feedback image...");
        const url = await uploadToCloudinaryWithProgress(feedbackImageFile, (progress) => {
          setGlobalUploadProgress(progress);
          if (progress >= 95) setGlobalUploadLabel("Finalizing feedback...");
        });
        imageUrl = url;
        setGlobalUploadProgress(100);
        setGlobalUploadLabel("Image sent! ✅");
        setTimeout(() => setGlobalUploadProgress(null), 1000);
      }

      await addDoc(collection(db, "feedback"), {
        userId: user.uid,
        userName: user.displayName,
        userEmail: user.email,
        text: feedbackText,
        imageUrl: imageUrl,
        createdAt: new Date().toISOString(),
        status: 'pending'
      });

      setFeedbackText('');
      setFeedbackImageFile(null);
      setFeedbackImagePreview(null);
      triggerToast("Feedback sent successfully! 🚀✨");
    } catch (error) {
      setGlobalUploadProgress(null);
      console.error("Error sending feedback:", error);
      triggerToast("Failed to send feedback. ❌");
    } finally {
      setIsSendingFeedback(false);
    }
  };

  const totalOrders = orders?.length || 0;
  const pendingOrders = orders?.filter(o => o.status === 'Incoming' || o.status === 'Pending Orders' || o.status === 'Preparing').length || 0;
  const totalExpenses = expenses?.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0) || 0;
  const grossEarned = orders?.reduce((sum, o) => sum + (parseFloat(o.total || 0) - parseFloat(o.balance || 0)), 0) || 0;

  if (activeSubView === 'admin') {
    return (
      <div className="fade-in settings-container-premium">
        <div className="subview-header-premium">
          <div className="back-nav-premium" onClick={() => setActiveSubView(null)}>
            <button className="back-btn-premium">‹</button>
            <span>Settings</span>
          </div>
          <h2 style={{ fontSize: '1.4rem', fontWeight: '800', margin: 0 }}>Admin Dashboard</h2>
        </div>
        <div className="subview-scroll-premium" style={{ paddingTop: '20px' }}>
          <AdminSection feedback={adminFeedback} deleteFeedback={deleteFeedback} triggerConfirm={triggerConfirm} triggerToast={triggerToast} />
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in settings-container-premium">
      <div className="settings-main-header-premium">
        <h1>Settings</h1>
        <p>Solyminx Flower Shop • v{APP_VERSION}</p>
      </div>

      {/* --- ACCOUNT SECTION --- */}
      <div className="settings-group-premium">
        <div className="profile-card-premium">
          <img src={user.photoURL} alt={user.displayName} className="profile-img-premium" />
          <div className="profile-info-premium">
            <h3>{user.displayName}</h3>
            <p>{user.email}</p>
          </div>
          {isAdmin && <span style={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '800', marginLeft: 'auto', textTransform: 'uppercase' }}>Owner</span>}
        </div>
      </div>

      {/* --- PREFERENCES --- */}
      <h5 className="group-label-premium">PREFERENCES</h5>
      <div className="settings-group-premium">
        <div className="settings-row-premium">
          <div className="row-icon-wrap" style={{ background: 'linear-gradient(135deg, #F472B6, #10B981)' }}>🎨</div>
          <div className="row-label-premium">
            <span>App Theme Style</span>
            <p>Switch between Noob Theme by Shaina and Hacker Theme by Pro Wey</p>
          </div>
          <div className="style-toggle-premium">
            <button
              className={themeMode === 'normal' ? 'active' : ''}
              onClick={() => {
                if (themeMode === 'hacker') {
                  setThemeMode('normal');
                  triggerToast('Switched to Noob Theme by Shaina 🌸');
                } else {
                  triggerToast('Already on Noob Theme by Shaina 🌸');
                }
              }}
            >
              🌸 Noob Theme by Shaina
            </button>
            <button
              className={themeMode === 'hacker' ? 'active' : ''}
              onClick={() => {
                if (themeMode === 'normal') {
                  setThemeMode('hacker');
                  triggerToast('Switched to Hacker Theme by Pro Wey 🟢');
                } else {
                  triggerToast('Already on Hacker Theme by Pro Wey 🟢');
                }
              }}
            >
              🟢 Hacker Theme by Pro Wey
            </button>
          </div>
        </div>
        <div className="settings-row-premium">
          <div className="row-icon-wrap" style={{ background: 'linear-gradient(135deg, #A2A2D0, #7E7EBC)' }}>🌙</div>
          <div className="row-label-premium">
            <span>Dark Mode</span>
            <p>Switch to a night-friendly theme</p>
          </div>
          <label className="native-switch">
            <input type="checkbox" checked={darkMode} onChange={(e) => setDarkMode(e.target.checked)} />
            <span className="native-slider"></span>
          </label>
        </div>
        <div className="settings-row-premium">
          <div className="row-icon-wrap" style={{ background: 'linear-gradient(135deg, #B2D7FF, #8EBAFF)' }}>📱</div>
          <div className="row-label-premium">
            <span>Layout Style</span>
            <p>Choose your preferred dashboard view</p>
          </div>
          <div className="style-toggle-premium">
            <button className={viewPreference === 'classic' ? 'active' : ''} onClick={() => setViewPreference('classic')}>Classic</button>
            <button className={viewPreference === 'receipt' ? 'active' : ''} onClick={() => setViewPreference('receipt')}>Receipt</button>
          </div>
        </div>
      </div>

      {/* --- SHOP SETUP --- */}
      <h5 className="group-label-premium">SHOP SETUP</h5>
      <div className="settings-group-premium">
        <div className="settings-row-premium">
          <div className="row-icon-wrap" style={{ background: 'linear-gradient(135deg, #B8E1C4, #94CBA3)' }}>🏠</div>
          <div className="row-label-premium">
            <span>Business Information</span>
            <p>Manage your shop name and contact</p>
          </div>
        </div>
        <div className="row-content-form-premium">
          <div className="native-input-group">
            <label>Shop Name</label>
            <input type="text" value={shopName} onChange={e => setShopName(e.target.value)} placeholder="Shop Name" />
          </div>
          <div className="native-input-group">
            <label>Address</label>
            <input type="text" value={shopAddress} onChange={e => setShopAddress(e.target.value)} placeholder="Address" />
          </div>
          <div className="native-input-group">
            <label>Contact Number</label>
            <input type="text" value={shopContact} onChange={e => setShopContact(e.target.value)} placeholder="Contact #" />
          </div>
          <button className="native-save-btn" onClick={saveShopInfo}>{saved ? '✓ Saved' : 'Update Details'}</button>
        </div>
        <div className="settings-row-premium">
          <div className="row-icon-wrap" style={{ background: 'linear-gradient(135deg, #FFD1B3, #FFB38E)' }}>🎯</div>
          <div className="row-label-premium">
            <span>Monthly Target</span>
            <p>Set your monthly financial goal</p>
          </div>
          {editingGoal ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              <input type="number" value={goalInput} onChange={e => setGoalInput(e.target.value)} style={{ width: '80px', padding: '8px', borderRadius: '10px', border: '1px solid #ddd' }} />
              <button onClick={saveGoal} style={{ padding: '8px 15px', background: '#94CBA3', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '700' }}>Set</button>
            </div>
          ) : (
            <span style={{ color: '#7E7EBC', fontWeight: '800', cursor: 'pointer', fontSize: '1.1rem' }} onClick={() => setEditingGoal(true)}>₱{savings?.target?.toLocaleString()} ›</span>
          )}
        </div>
      </div>

      {/* --- PRINTING --- */}
      <h5 className="group-label-premium">PRINTING</h5>
      <div className="settings-group-premium">
        <div className="settings-row-premium">
          <div className="row-icon-wrap" style={{ background: 'linear-gradient(135deg, #CBD5E1, #94A3B8)' }}>📍</div>
          <div className="row-label-premium">
            <span>Show Address on Receipt</span>
          </div>
          <label className="native-switch">
            <input type="checkbox" checked={showAddressInReceipt} onChange={(e) => setShowAddressInReceipt(e.target.checked)} />
            <span className="native-slider"></span>
          </label>
        </div>
        <div className="settings-row-premium">
          <div className="row-icon-wrap" style={{ background: 'linear-gradient(135deg, #CBD5E1, #94A3B8)' }}>📞</div>
          <div className="row-label-premium">
            <span>Show Contact on Receipt</span>
          </div>
          <label className="native-switch">
            <input type="checkbox" checked={showContactInReceipt} onChange={(e) => setShowContactInReceipt(e.target.checked)} />
            <span className="native-slider"></span>
          </label>
        </div>
      </div>

      {/* --- STATISTICS --- */}
      <h5 className="group-label-premium">INSIGHTS</h5>
      <div className="settings-group-premium">
        <div className="stats-container-premium">
          <div className="stat-pill-premium"><span>Orders</span><strong>{totalOrders}</strong></div>
          <div className="stat-pill-premium"><span>Pending</span><strong style={{ color: '#FFB38E' }}>{pendingOrders}</strong></div>
          <div className="stat-pill-premium"><span>Sales</span><strong style={{ color: '#94CBA3' }}>₱{grossEarned.toLocaleString()}</strong></div>
          <div className="stat-pill-premium"><span>Net Sales</span><strong style={{ color: '#7E7EBC' }}>₱{(grossEarned - totalExpenses).toLocaleString()}</strong></div>
        </div>
      </div>

      {/* --- SUPPORT --- */}
      <h5 className="group-label-premium">SUPPORT</h5>
      <div className="settings-group-premium">
        <div className="settings-row-premium">
          <div className="row-icon-wrap" style={{ background: 'linear-gradient(135deg, #D4B3FF, #B38EFF)' }}>💡</div>
          <div className="row-label-premium">
            <span>Feedback and support</span>
          </div>
        </div>
        <div className="feedback-box-premium">
          <textarea
            className="native-textarea-premium"
            placeholder="Tell us what you want to improve..."
            value={feedbackText}
            onChange={handleFeedbackTextChange}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
            {feedbackImagePreview ? (
              <div style={{ position: 'relative' }}>
                <img src={feedbackImagePreview} alt="Preview" style={{ width: '50px', height: '50px', borderRadius: '12px', objectFit: 'cover' }} />
                <button onClick={() => { setFeedbackImageFile(null); setFeedbackImagePreview(null); }} style={{ position: 'absolute', top: '-8px', right: '-8px', background: '#FF8E8E', color: 'white', borderRadius: '50%', width: '20px', height: '20px', border: 'none', cursor: 'pointer', fontSize: '10px' }}>✕</button>
              </div>
            ) : (
              <label style={{ color: '#7E7EBC', fontWeight: '700', cursor: 'pointer', fontSize: '0.95rem' }}>
                <input type="file" accept="image/*" onChange={handleFeedbackImageUpload} style={{ display: 'none' }} />
                📸 Add Image
              </label>
            )}
            {isProcessingImage && (
              <div className="processing-indicator">
                <span className="spinner-mini"></span>
                <span>Processing...</span>
              </div>
            )}
            <button className="native-save-btn" style={{ width: 'auto', padding: '10px 30px', background: 'linear-gradient(135deg, #A2A2D0, #7E7EBC)' }} disabled={isSendingFeedback || isProcessingImage || (!feedbackText.trim() && !feedbackImageFile)} onClick={handleFeedbackSubmit}>
              {isSendingFeedback ? '...' : 'Send'}
            </button>
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <p
          onClick={() => setShowAppInfoModal(true)}
          style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', display: 'inline-block', transition: 'var(--transition)' }}
          className="version-footer-text"
        >
          Solyminx App v{APP_VERSION}
        </p>
        <br />
        <button onClick={logout} style={{ background: 'none', border: 'none', color: '#94A3B8', fontWeight: '800', fontSize: '1.1rem', marginTop: '12px', cursor: 'pointer' }}>🚪 Sign Out</button>
      </div>

      {/* Play Store App Info Modal */}
      {showAppInfoModal && (
        <div className="confirm-modal-overlay fade-in" onClick={() => setShowAppInfoModal(false)} style={{ zIndex: 10000 }}>
          <div className="confirm-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px', maxHeight: '85vh', overflowY: 'auto', padding: '28px', borderRadius: '28px', textAlign: 'left' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '3rem', marginBottom: '10px' }}>🌸</div>
              <h3 style={{ fontSize: '1.4rem', fontWeight: '900', color: 'var(--text)', margin: '0 0 4px 0' }}>Solyminx Orders</h3>
              <span style={{ fontSize: '0.85rem', fontWeight: '800', color: '#94a3b8', background: '#f1f5f9', padding: '4px 12px', borderRadius: '20px', display: 'inline-block' }}>v{APP_VERSION} (Full-Year Monthly Volume)</span>
            </div>

            {/* [AI DEV NOTE]: Always list "What's new in this update" directly below the version badge in settings about modal */}
            <div style={{ background: 'rgba(244,114,182,0.06)', border: '1px solid rgba(244,114,182,0.15)', borderRadius: '16px', padding: '12px 16px', margin: '0 0 20px 0', fontSize: '0.82rem' }}>
              <strong style={{ color: 'var(--primary)', display: 'block', marginBottom: '4px', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.5px' }}>✨ What's New in this Update:</strong>
              <ul style={{ margin: 0, paddingLeft: '16px', color: 'var(--text)', lineHeight: '1.5' }}>
                <li><strong>📅 Full Year:</strong> January hanggang December na ang Monthly Order Volume chart.</li>
                <li><strong>📱 Responsive:</strong> Visible ang lahat ng 12 months sa desktop at mobile.</li>
              </ul>
            </div>

            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: '1.5', marginBottom: '24px', textAlign: 'center' }}>
              Welcome to the ultimate Floral Business Management Suite. Designed from the ground up to empower flower shops with seamless automation, gorgeous aesthetics, and intelligent workflows.
            </p>

            <h4 style={{ fontSize: '0.9rem', fontWeight: '800', color: 'var(--text)', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🔑 Key Features & Capabilities</h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '1.2rem' }}>🌸</span>
                <div>
                  <strong style={{ fontSize: '0.9rem', color: 'var(--text)' }}>Smart Order Parser (Auto-Paste)</strong>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '2px 0 0 0', lineHeight: '1.4' }}>
                    Copy-paste raw order forms from Facebook Messenger, SMS, or GCash. The system automatically extracts customer details, delivery info, and bouquet items with 100% precision.
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '1.2rem' }}>📋</span>
                <div>
                  <strong style={{ fontSize: '0.9rem', color: 'var(--text)' }}>Elite Orders Dashboard</strong>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '2px 0 0 0', lineHeight: '1.4' }}>
                    Track active orders through highly visible stages: Incoming ⏳, Preparing 🎨, Bouquet Done 💐, Out for Delivery 🚚, and Completed.
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '1.2rem' }}>📅</span>
                <div>
                  <strong style={{ fontSize: '0.9rem', color: 'var(--text)' }}>Smart Schedule & Buffer Warnings</strong>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '2px 0 0 0', lineHeight: '1.4' }}>
                    Interactive calendar featuring automatic scheduling conflict alerts for overlapping deliveries with a 30-minute buffer window.
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '1.2rem' }}>🖨️</span>
                <div>
                  <strong style={{ fontSize: '0.9rem', color: 'var(--text)' }}>Thermal Receipt Generator</strong>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '2px 0 0 0', lineHeight: '1.4' }}>
                    Generate itemized, professional receipts with customizable shop headers for printing or instant image sharing.
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '1.2rem' }}>📊</span>
                <div>
                  <strong style={{ fontSize: '0.9rem', color: 'var(--text)' }}>Live Analytics & Goals</strong>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '2px 0 0 0', lineHeight: '1.4' }}>
                    Real-time sales tracking, active downpayments, operating expenses, and monthly profits with milestone visual tracking.
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '1.2rem' }}>🎨</span>
                <div>
                  <strong style={{ fontSize: '0.9rem', color: 'var(--text)' }}>Premium Adaptive UI/UX</strong>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '2px 0 0 0', lineHeight: '1.4' }}>
                    HSL light/dark theme overrides, smooth transitions, instant PWA updates, and auto-scroll top restoration.
                  </p>
                </div>
              </div>
            </div>

            <button onClick={() => setShowAppInfoModal(false)} className="confirm-btn proceed primary" style={{ width: '100%', padding: '12px', borderRadius: '16px', fontSize: '0.95rem', fontWeight: '800' }}>
              Awesome!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}



// =============================================================================
// 👤 USER PROFILE ONBOARDING MODAL (One-Time Setup per Device)
// =============================================================================
function UserProfileOnboardingModal({ user, onClose, sessionDocIdRef }) {
  const [username, setUsername] = useState(() => localStorage.getItem('solyminx_device_username') || '');
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    const cleanUsername = username.trim().replace(/^@/, '');
    if (!cleanUsername) {
      setErrorMsg('Please enter a username or device name!');
      return;
    }

    setIsSaving(true);
    setErrorMsg('');
    try {
      let deviceId = localStorage.getItem('solyminx_device_id');
      if (!deviceId) {
        deviceId = 'dev_' + Math.random().toString(36).substring(2, 10) + '_' + Date.now().toString(36);
        localStorage.setItem('solyminx_device_id', deviceId);
      }

      await setDoc(doc(db, 'user_profiles', deviceId), {
        deviceId,
        userId: user.uid,
        userEmail: user.email || '',
        username: cleanUsername,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }, { merge: true });

      localStorage.setItem('solyminx_device_username', cleanUsername);

      if (sessionDocIdRef?.current) {
        updateDoc(doc(db, 'sessions', sessionDocIdRef.current), {
          username: cleanUsername,
          deviceId
        }).catch(() => { });
      }

      onClose();
    } catch (err) {
      console.error('Error saving profile:', err);
      setErrorMsg('Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="confirm-modal-overlay fade-in" style={{ zIndex: 10002 }}>
      <div className="confirm-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', width: '92%', padding: '28px 24px', borderRadius: '16px' }}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>👤</div>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '1.35rem', fontWeight: 800, color: 'var(--text)' }}>Security Check</h3>
          <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)' }}>Enter your username to continue this session </p>
        </div>

        {errorMsg && (
          <div style={{ background: 'rgba(255, 0, 85, 0.12)', border: '1px solid rgba(255, 0, 85, 0.3)', color: '#ff0055', padding: '8px 12px', borderRadius: '8px', fontSize: '0.8rem', marginBottom: '16px', textAlign: 'center' }}>
            ⚠️ {errorMsg}
          </div>
        )}

        <form onSubmit={handleSave}>
          <div>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: '8px', color: 'var(--text)' }}>
              Username <span style={{ color: '#ff0055' }}>*</span>
            </label>
            <input
              type="text"
              required
              placeholder="@Username"
              value={username}
              onChange={e => setUsername(e.target.value)}
              className="search-input-field"
              style={{ width: '100%', boxSizing: 'border-box' }}
              autoFocus
            />
          </div>

          <div style={{ marginTop: '24px' }}>
            <button
              type="submit"
              disabled={isSaving}
              className="confirm-btn proceed success"
              style={{ width: '100%', padding: '12px', borderRadius: '12px', fontWeight: 700, fontSize: '0.95rem' }}
            >
              {isSaving ? 'Saving...' : 'Save Profile ✓'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PaymentModal({ order, onClose, updateOrder, triggerToast }) {
  const [mode, setMode] = useState(null); // null | 'custom'
  const [customAmount, setCustomAmount] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const savingRef = useRef(false);
  const balance = parseFloat(order?.balance) || 0;

  useEffect(() => {
    setMode(null);
    setCustomAmount('');
    setError('');
  }, [order?.id]);

  if (!order) return null;

  const recordPayment = async amount => {
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    setError('');
    try {
      const update = applyPayment(order, amount);
      await updateOrder(order.id, update);
      triggerToast(update.balance === 0 ? '✅ Fully Paid!' : `💸 Payment of ₱${money(amount).toFixed(2)} recorded! Remaining: ₱${update.balance.toFixed(2)}`);
      onClose();
    } catch (failure) {
      setError(failure.message || 'Payment not saved. Please retry.');
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };
  const handleFullyPaid = () => recordPayment(balance);
  const handleCustomPay = () => recordPayment(customAmount);

  return (
    <div className="confirm-modal-overlay fade-in" onClick={() => !isSaving && onClose()}>
      <div className="payment-modal-content slide-up" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="payment-modal-header">
          <div className="payment-modal-icon">💳</div>
          <div>
            <h3 className="payment-modal-title">Record Payment</h3>
            <p className="payment-modal-subtitle">{order.customer}</p>
          </div>
          <button className="payment-modal-close" disabled={isSaving} onClick={onClose}>×</button>
        </div>

        {/* Balance info */}
        <div className="payment-modal-balance-row">
          <span className="payment-modal-balance-label">Remaining Balance</span>
          <span className="payment-modal-balance-amount">₱{balance.toFixed(2)}</span>
        </div>

        {error && <p role="alert">{error}</p>}
        {isSaving && <p role="status">Saving payment…</p>}
        <fieldset disabled={isSaving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        {/* Mode selection */}
        {mode === null && (
          <div className="payment-modal-choices">
            <button className="payment-choice-btn fully-paid" onClick={handleFullyPaid}>
              <span className="choice-icon">✅</span>
              <div>
                <div className="choice-label">Fully Paid</div>
                <div className="choice-sub">Mark entire balance of ₱{balance.toFixed(2)} as paid</div>
              </div>
            </button>
            <button className="payment-choice-btn custom" onClick={() => setMode('custom')}>
              <span className="choice-icon">💸</span>
              <div>
                <div className="choice-label">Custom Amount</div>
                <div className="choice-sub">Enter partial payment to deduct from balance</div>
              </div>
            </button>
          </div>
        )}

        {/* Custom amount input */}
        {mode === 'custom' && (
          <div className="payment-modal-custom">
            <button className="payment-back-btn" onClick={() => { setMode(null); setCustomAmount(''); }}>‹ Back</button>
            <label className="payment-custom-label">Amount Paid (₱)</label>
            <input
              className="payment-custom-input"
              type="number"
              min="1"
              max={balance}
              placeholder={`Max ₱${balance.toFixed(2)}`}
              value={customAmount}
              onChange={e => setCustomAmount(e.target.value)}
              autoFocus
            />
            {customAmount && parseFloat(customAmount) > 0 && parseFloat(customAmount) <= balance && (
              <div className="payment-custom-preview">
                New balance after payment: <strong>₱{(balance - parseFloat(customAmount)).toFixed(2)}</strong>
              </div>
            )}
            <button
              className="payment-custom-confirm"
              onClick={handleCustomPay}
              disabled={!customAmount || parseFloat(customAmount) <= 0}
            >
              💸 Confirm Payment
            </button>
          </div>
        )}
        </fieldset>
      </div>
    </div>
  );
}

function ConfirmModal({ config, onClose }) {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const savingRef = useRef(false);
  useEffect(() => { setError(''); }, [config]);
  if (!config) return null;
  const { title, message, onConfirm, type = 'primary', confirmText } = config;
  const confirm = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    setError('');
    try {
      await onConfirm();
      onClose();
    } catch (failure) {
      setError(failure.message || 'Could not save. Please retry.');
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };
  return (
    <div className="confirm-modal-overlay fade-in" onClick={() => !isSaving && onClose()}>
      <div className="confirm-modal-content" onClick={e => e.stopPropagation()}>
        <div className={`confirm-icon-container ${type}`}>
          {type === 'danger' ? '⚠️' : type === 'success' ? '✅' : 'ℹ️'}
        </div>
        <h3 className="confirm-title">{title}</h3>
        <p className="confirm-message">{message}</p>
        {error && <p role="alert">{error}</p>}
        <div className="confirm-actions">
          <button className="confirm-btn cancel" disabled={isSaving} onClick={onClose}>Cancel</button>
          <button className={`confirm-btn proceed ${type}`} disabled={isSaving} onClick={confirm}>
            {isSaving ? 'Saving…' : confirmText || (type === 'danger' ? 'Delete' : 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;
export { OrderForm, InventorySection, OrderDetailModal, PaymentModal, ConfirmModal };
